import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Text,
  Badge,
  SimpleGrid,
  HStack,
  VStack,
  Heading,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Flex,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
  Icon,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  AspectRatio,
  Spinner,
  Select,
  Image,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import { buildGeneratedScenarioName, buildScenarioDownloadFilename } from "../lib/scenarioNaming";
import { 
  FaExclamationTriangle, 
  FaHeartbeat, 
  FaLungs, 
  FaBrain,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaUser,
  FaWind,
  FaHelicopter,
  FaPrint,
  FaHospital,
  FaImage,
  FaVideo,
  FaSpinner,
  FaExclamationCircle
} from 'react-icons/fa';

const PROVIDER_LEVEL_SEQUENCE = ["EMT", "AEMT", "Paramedic", "CriticalCare"];
const PROVIDER_LEVEL_LABELS = {
  EMT: "EMT",
  AEMT: "AEMT",
  Paramedic: "Paramedic",
  CriticalCare: "Critical Care (CCP/FP-C)"
};

const CRYING_JORDAN_IMAGE_URL = "/images/crying-jordan.jpg";
const CRYING_JORDAN_FALLBACK_URL = "https://i.kym-cdn.com/entries/icons/original/000/017/966/cryingmj.jpg";

const normalizeProviderLevel = (value, fallback = "EMT") => {
  const source = (value || "").toString().trim().toLowerCase();
  if (!source) return fallback;
  if (source.includes("critical") || source.includes("ccp") || source.includes("fp-c") || source.includes("fpc")) return "CriticalCare";
  if (source.includes("paramedic")) return "Paramedic";
  if (source.includes("aemt") || source.includes("advanced emt") || source.includes("emt-advanced")) return "AEMT";
  if (source.includes("emt")) return "EMT";
  return fallback;
};

const providerLevelRank = (level) => {
  const normalized = normalizeProviderLevel(level, "EMT");
  const index = PROVIDER_LEVEL_SEQUENCE.indexOf(normalized);
  return index >= 0 ? index : 0;
};

const printStyles = `
  /* Page Break Utilities */
  .page-break-before {
    page-break-before: always !important;
    break-before: page !important;
  }
  
  .page-break-after {
    page-break-after: always !important;
    break-after: page !important;
  }
  
  .page-break-avoid {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .page-break-before-critical-actions {
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Simple Print Mode - Optimized for single-page fit with larger text */
  @page {
    size: letter portrait;
    margin: 0.25in;
  }

  body.simple-print {
    --chakra-colors-text: #000 !important;
    --chakra-colors-gray-900: #000 !important;
    --chakra-colors-gray-800: #000 !important;
    --chakra-colors-gray-700: #000 !important;
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    position: relative !important;
    overflow: visible !important;
  }

  /* Hide non-print elements */
  body.simple-print .no-print {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
  }

  /* Hide inline watermark bars while capturing snapshot-based print output */
  body[data-snapshot-print="true"] .inline-watermark-border-top,
  body[data-snapshot-print="true"] .inline-watermark-border-bottom {
    display: none !important;
    visibility: hidden !important;
  }

  /* Root container scales to fit page */
  body.simple-print #root,
  body.simple-print main,
  body.simple-print > div {
    background: white !important;
    margin: 0 !important;
    padding: 0.125in !important;
    display: flex !important;
    flex-direction: column !important;
    max-height: none !important;
    overflow: visible !important;
    height: auto !important;
  }

  /* Proportional flex layout for main sections */
  body.simple-print .main-content-wrapper {
    display: flex !important;
    flex-direction: column !important;
    height: auto !important;
    gap: 2px !important;
  }

  /* All cards get proportional space */
  body.simple-print .chakra-card {
    border: 1px solid #000 !important;
    padding: 3px !important;
    margin: 1px 0 !important;
    background: white !important;
    box-shadow: none !important;
    page-break-inside: avoid !important;
    flex: 1 1 auto !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: visible !important;
  }

  /* Card headers - larger and readable */
  body.simple-print .chakra-card__header {
    border-bottom: 1px solid #000 !important;
    padding: 2px 3px !important;
    font-size: 8pt !important;
    font-weight: bold !important;
    background: white !important;
    line-height: 1.2 !important;
    flex-shrink: 0 !important;
  }

  /* Keep all headers/subsections neutral in simple print */
  body.simple-print .chakra-card__header,
  body.simple-print .chakra-card__header * {
    background: white !important;
    background-color: white !important;
    color: #000 !important;
    border-color: #000 !important;
  }

  body.simple-print .cardiac-arrest-sequence,
  body.simple-print .cardiac-arrest-sequence * {
    background: white !important;
    background-color: white !important;
    color: #000 !important;
    border-color: #000 !important;
  }

  /* Card bodies - flexible height with wrapping */
  body.simple-print .chakra-card__body {
    padding: 3px !important;
    flex: 1 1 auto !important;
    overflow: visible !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 1px !important;
  }

  /* Text elements - larger with proper wrapping */
  body.simple-print .chakra-text,
  body.simple-print p,
  body.simple-print div,
  body.simple-print span,
  body.simple-print li {
    font-size: 7pt !important;
    line-height: 1.25 !important;
    color: #000 !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    hyphens: auto !important;
    margin: 0 !important;
  }

  /* Headings */
  body.simple-print .chakra-heading,
  body.simple-print h1,
  body.simple-print h2,
  body.simple-print h3,
  body.simple-print h4 {
    font-size: 9pt !important;
    line-height: 1.2 !important;
    color: #000 !important;
    margin: 0 0 1px 0 !important;
    font-weight: bold !important;
  }

  /* Badges - readable with wrapping */
  body.simple-print .chakra-badge {
    border: 1px solid #000 !important;
    padding: 1px 2px !important;
    font-size: 6pt !important;
    background: white !important;
    color: #000 !important;
    display: inline-block !important;
    white-space: normal !important;
    overflow-wrap: break-word !important;
    line-height: 1.2 !important;
    margin: 0 2px 1px 0 !important;
  }

  /* Stats/Vital signs - larger and more readable */
  body.simple-print .vital-sign-box,
  body.simple-print .chakra-stat {
    padding: 2px 3px !important;
    border: 1px solid #000 !important;
    margin: 1px !important;
    flex: 1 1 auto !important;
  }

  body.simple-print .chakra-stat__number {
    font-size: 8pt !important;
    font-weight: bold !important;
    line-height: 1.2 !important;
    color: #000 !important;
  }

  body.simple-print .chakra-stat__label {
    font-size: 6pt !important;
    line-height: 1.2 !important;
    color: #000 !important;
  }

  /* Lists - proper spacing with wrapping */
  body.simple-print .chakra-list {
    margin: 0 !important;
    padding-left: 10px !important;
    list-style-position: outside !important;
  }

  body.simple-print .chakra-list__item {
    margin: 0 0 1px 0 !important;
    padding: 0 !important;
    line-height: 1.25 !important;
  }

  /* Keep Chakra SimpleGrid behavior in simple print */
  body.simple-print .chakra-simple-grid {
    display: grid !important;
    gap: 2px !important;
  }

  body.simple-print .chakra-simple-grid > * {
    min-width: 0 !important;
  }

  /* Hide icons */
  body.simple-print svg {
    display: none !important;
  }

  /* Tables */
  body.simple-print table {
    font-size: 6pt !important;
    line-height: 1.2 !important;
    width: 100% !important;
    border-collapse: collapse !important;
  }

  body.simple-print th,
  body.simple-print td {
    padding: 2px !important;
    border: 1px solid #000 !important;
    color: #000 !important;
  }

  /* Watermark */
  body.simple-print .print-watermark {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) rotate(-45deg) !important;
    font-size: 96px !important;
    font-weight: bold !important;
    opacity: 0.06 !important;
    color: #000 !important;
    pointer-events: none !important;
    z-index: 1 !important;
  }

  /* Print/Back buttons - visible on screen only */
  body.simple-print .print-button,
  body.simple-print .back-button {
    display: inline-flex !important;
    position: fixed !important;
    z-index: 9999 !important;
    background: #FFFFFF !important;
    color: #000 !important;
    border: 3px solid #000 !important;
    padding: 8px 16px !important;
    font-size: 12pt !important;
    font-weight: bold !important;
    cursor: pointer !important;
    border-radius: 4px !important;
  }

  body.simple-print .print-button {
    top: 8px !important;
    right: 8px !important;
  }

  body.simple-print .back-button {
    top: 8px !important;
    left: 8px !important;
  }

  body.simple-print .back-button::before {
    content: "← " !important;
  }

  /* Hide buttons when actually printing */
  @media print {
    body.simple-print .print-button,
    body.simple-print .back-button {
      display: none !important;
    }
    
    /* Ensure black text in print */
    body.simple-print * {
      color: #000 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    /* Scale entire page to fit if needed */
    @page {
      size: letter portrait;
      margin: 0.25in;
    }
  }

  /* Hide mode selector and header in tablet view - NUCLEAR OPTION */
  body.tablet-view .no-print,
  body.tablet-view.portrait .no-print,
  body.tablet-view.landscape .no-print {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    height: 0 !important;
    overflow: hidden !important;
  }
  
  body.tablet-view div.no-print,
  body.tablet-view .chakra-stack.no-print,
  body.tablet-view .css-swaqeq {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    top: -99999px !important;
    width: 0 !important;
    height: 0 !important;
    max-height: 0 !important;
    overflow: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  /* EXCEPTION: Show evaluation checkboxes even in tablet/print modes */
  body.tablet-view input[type="checkbox"],
  body.tablet-view.portrait input[type="checkbox"],
  body.tablet-view.landscape input[type="checkbox"] {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
    pointer-events: auto !important;
    width: 16px !important;
    height: 16px !important;
    margin-right: 8px !important;
    cursor: pointer !important;
  }

  /* Also ensure checkboxes are visible in simple print mode for evaluation */
  body.simple-print input[type="checkbox"] {
    display: inline-block !important;
    visibility: visible !important;
    -webkit-appearance: checkbox !important;
    appearance: checkbox !important;
    width: 12px !important;
    height: 12px !important;
    margin-right: 4px !important;
    print-color-adjust: exact !important;
  }

  body.simple-print input.scenario-eval-checkbox {
    position: absolute !important;
    top: 8px !important;
    right: 8px !important;
    margin: 0 !important;
    z-index: 3 !important;
  }

  /* CRITICAL: Hide ALL form inputs and selectors in tablet view */
  body.tablet-view input,
  body.tablet-view select,
  body.tablet-view textarea,
  body.tablet-view button:not(.tablet-controls button),
  body.tablet-view form {
    display: none !important;
    visibility: hidden !important;
  }

  body.tablet-view.portrait input,
  body.tablet-view.portrait select,
  body.tablet-view.portrait textarea,
  body.tablet-view.portrait button:not(.tablet-controls button),
  body.tablet-view.portrait form {
    display: none !important;
    visibility: hidden !important;
  }

  body.tablet-view.landscape input,
  body.tablet-view.landscape select,
  body.tablet-view.landscape textarea,
  body.tablet-view.landscape button:not(.tablet-controls button),
  body.tablet-view.landscape form {
    display: none !important;
    visibility: hidden !important;
  }

  body.tablet-view .scenario-header,
  body.tablet-view.portrait .scenario-header,
  body.tablet-view.landscape .scenario-header,
  body.tablet-view div.scenario-header,
  body.tablet-view .avoid-break.scenario-header,
  body.tablet-view .css-15rkey4 {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    top: -99999px !important;
    width: 0 !important;
    height: 0 !important;
    max-height: 0 !important;
    overflow: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  /* Show tablet controls - NUCLEAR OPTION */
  body.tablet-view .tablet-controls,
  body.tablet-view.portrait .tablet-controls,
  body.tablet-view.landscape .tablet-controls,
  body.tablet-view div.tablet-controls,
  body.tablet-view .css-c8spic {
    display: block !important;
    visibility: visible !important;
    position: fixed !important;
    top: 16px !important;
    right: 16px !important;
    z-index: 99999 !important;
    opacity: 0.4 !important;
  }

  body.tablet-view .tablet-controls:hover,
  body.tablet-view.portrait .tablet-controls:hover,
  body.tablet-view.landscape .tablet-controls:hover {
    opacity: 0.95 !important;
  }

  /* Patient info banner - always visible */
  body.simple-print .patient-info-banner {
    display: block !important;
    padding: 2px !important;
    margin-bottom: 2px !important;
    border-bottom: 1px solid #000 !important;
    background: white !important;
  }

  body.simple-print .patient-info-banner .chakra-text {
    font-size: 6pt !important;
    font-weight: bold !important;
  }

  body.tablet-view .patient-info-banner {
    display: block !important;
    padding: 4px !important;
    margin-bottom: 4px !important;
  }

  body.tablet-view.portrait .patient-info-banner .chakra-text {
    font-size: 7pt !important;
  }

  body.tablet-view.landscape .patient-info-banner .chakra-text {
    font-size: 8pt !important;
  }

  /* Tablet View Mode - ON SCREEN - FULLSCREEN SCALED */
  /* Portrait: Matches simple print but with color - Fix field expansion */
  body.tablet-view.portrait {
    font-size: 7pt !important;
    line-height: 1 !important;
    overflow: auto !important;
    height: 100vh !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }
  
  body.tablet-view.portrait * {
    line-height: 1 !important;
    margin: 0 !important;
    padding: 0 !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
    text-overflow: ellipsis !important;
    max-height: none !important;
    overflow-wrap: anywhere !important;
    hyphens: auto !important;
  }
  
  body.tablet-view.portrait .chakra-text,
  body.tablet-view.portrait p,
  body.tablet-view.portrait span,
  body.tablet-view.portrait div {
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: pre-wrap !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }
  
  body.tablet-view.portrait .chakra-card {
    border: 1px solid #e2e8f0 !important;
    padding: 3px !important;
    margin: 2px 0 !important;
    page-break-inside: avoid;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-card__header {
    border-bottom: 1px solid !important;
    padding: 1px 2px !important;
    font-size: 7pt !important;
    font-weight: bold !important;
  }
  
  body.tablet-view.portrait .chakra-card__body {
    padding: 3px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-badge {
    border: 1px solid currentColor !important;
    padding: 0 2px !important;
    display: inline-block !important;
    font-size: 7pt !important;
    max-width: 100% !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
    line-height: 1.2 !important;
  }
  
  body.tablet-view.portrait .chakra-heading {
    font-size: 7pt !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  /* Badge wrapping in normal mode */
  .chakra-badge {
    max-width: 100% !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
  }
  
  body.tablet-view.portrait .chakra-text {
    font-size: 7pt !important;
    line-height: 1 !important;
    margin: 0 !important;
  }
  
  body.tablet-view.portrait .chakra-stack > * {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  body.tablet-view.portrait .chakra-list {
    margin: 0 !important;
    padding-left: 10px !important;
  }
  
  body.tablet-view.portrait .chakra-list__item {
    margin: 0 !important;
    line-height: 1.1 !important;
  }
  
  body.tablet-view.portrait .chakra-stat__number {
    font-size: 7pt !important;
  }
  
  body.tablet-view.portrait .chakra-stat__label {
    font-size: 7pt !important;
  }
  
  /* Handle long unbound text in narrative fields */
  body.tablet-view.portrait .chakra-text,
  body.tablet-view.portrait .chakra-card__body p,
  body.tablet-view.portrait .chakra-card__body div,
  body.tablet-view.portrait .chakra-list__item {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    hyphens: auto !important;
    white-space: normal !important;
    overflow: hidden !important;
  }

  /* Specifically target intervention cards and prior to arrival */
  body.tablet-view.portrait .interventions-grid .chakra-card__body,
  body.tablet-view.portrait [class*="prior"] .chakra-text,
  body.tablet-view.portrait [class*="Prior"] .chakra-text {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    hyphens: auto !important;
    white-space: pre-wrap !important;
    overflow: hidden !important;
    display: block !important;
  }
  
  /* Landscape: wider spread layout */
  body.tablet-view.landscape {
    font-size: 8pt !important;
    line-height: 1.2 !important;
    overflow: auto !important;
  }
  
  body.tablet-view.landscape * {
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
  }
  
  body.tablet-view.landscape .chakra-card {
    margin-bottom: 3px !important;
    padding: 3px !important;
  }
  
  body.tablet-view.landscape .chakra-card__header {
    padding: 3px 5px !important;
    font-size: 8pt !important;
  }
  
  body.tablet-view.landscape .chakra-card__body {
    padding: 5px !important;
  }
  
  body.tablet-view.landscape .chakra-text {
    font-size: 8pt !important;
    line-height: 1.2 !important;
  }
  
  body.tablet-view.landscape .chakra-stat__number {
    font-size: 8pt !important;
  }
  
  body.tablet-view.landscape .chakra-stat__label {
    font-size: 8pt !important;
  }
  
  /* Handle long unbound text in landscape */
  body.tablet-view.landscape .chakra-text,
  body.tablet-view.landscape .chakra-card__body p,
  body.tablet-view.landscape .chakra-card__body div,
  body.tablet-view.landscape .chakra-list__item {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    hyphens: auto !important;
    overflow: hidden !important;
  }
  
  /* Landscape: wider grids */
  body.tablet-view.landscape .first-row-grid {
    grid-template-columns: 1fr 1fr 1fr !important;
  }
  
  body.tablet-view.landscape .second-row-grid {
    grid-template-columns: 1fr 1fr 1fr !important;
  }
  
  body.tablet-view.landscape .interventions-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr 1fr 1fr !important;
  }
  
  /* CRITICAL: Force grid layouts in portrait tablet mode - MUST match simple print exactly - Fix expansion */
  body.tablet-view.portrait .first-row-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 3px !important;
    column-gap: 4px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .first-row-grid > * {
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-simple-grid.first-row-grid {
    display: grid !important;
  }
  
  body.tablet-view.portrait .second-row-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 3px !important;
    column-gap: 4px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .second-row-grid > * {
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-simple-grid.second-row-grid {
    display: grid !important;
  }
  
  body.tablet-view.portrait .trending-row-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 3px !important;
    column-gap: 4px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .trending-row-grid > * {
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-simple-grid.trending-row-grid {
    display: grid !important;
  }
  
  body.tablet-view.portrait .interventions-grid,
  body.tablet-view.portrait .critical-actions-list,
  body.tablet-view.portrait .common-pitfalls-list,
  body.tablet-view.portrait .differential-diagnosis-list {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 3px !important;
    column-gap: 4px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .interventions-grid > *,
  body.tablet-view.portrait .critical-actions-list > *,
  body.tablet-view.portrait .common-pitfalls-list > *,
  body.tablet-view.portrait .differential-diagnosis-list > * {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-stack.interventions-grid,
  body.tablet-view.portrait .chakra-list.critical-actions-list,
  body.tablet-view.portrait .chakra-list.common-pitfalls-list,
  body.tablet-view.portrait .chakra-list.differential-diagnosis-list {
    display: grid !important;
  }
  
  body.tablet-view .chakra-card {
    page-break-inside: avoid;
  }
  
  /* Fullscreen scaling for tablet view */
  body.tablet-view:fullscreen {
    width: 100vw;
    height: 100vh;
    overflow: auto;
    background: white;
  }
  
  body.tablet-view:-webkit-full-screen {
    width: 100vw;
    height: 100vh;
    overflow: auto;
    background: white;
  }
  
  body.tablet-view:-moz-full-screen {
    width: 100vw;
    height: 100vh;
    overflow: auto;
    background: white;
  }
  
  body.tablet-view:-ms-fullscreen {
    width: 100vw;
    height: 100vh;
    overflow: auto;
    background: white;
  }
  
  /* Accessible View Mode - WCAG AA Compliant */
  body.accessible {
    background: white !important;
    color: #000 !important;
    font-family: Arial, sans-serif !important;
    font-size: 14pt !important;
    line-height: 1.5 !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 20px !important;
    overflow: visible !important;
    overflow-x: visible !important;
  }

  /* CRITICAL: Override Chakra UI's dark background theme */
  html.accessible,
  html body.accessible,
  body.accessible,
  body.accessible #root,
  body.accessible .chakra-ui-light,
  body.accessible .chakra-ui-dark,
  body.accessible [data-theme],
  body.accessible main,
  body.accessible > div,
  body.accessible div#root {
    background: white !important;
    background-color: white !important;
    background-image: none !important;
  }

  /* Remove scrollbar styles - not needed for accessible view */
  /*
  body.accessible::-webkit-scrollbar {
    width: 12px !important;
  }

  body.accessible::-webkit-scrollbar-track {
    background: #f1f1f1 !important;
  }

  body.accessible::-webkit-scrollbar-thumb {
    background: #888 !important;
    border-radius: 6px !important;
  }

  body.accessible::-webkit-scrollbar-thumb:hover {
    background: #555 !important;
  }
  */

  body.accessible * {
    font-family: Arial, sans-serif !important;
    line-height: 1.5 !important;
  }

  body.accessible .no-print,
  body.accessible .patient-info-banner,
  body.accessible .scenario-header,
  body.accessible .chakra-card,
  body.accessible .chakra-badge,
  body.accessible .chakra-simple-grid,
  body.accessible svg,
  body.accessible .chakra-icon {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    height: 0 !important;
    overflow: hidden !important;
  }

  body.accessible .accessible-content {
    display: block !important;
    visibility: visible !important;
    position: relative !important;
    left: auto !important;
    height: auto !important;
  }

  /* Force hide visual content in accessible mode */
  body.accessible .visual-content-wrapper {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    height: 0 !important;
    overflow: hidden !important;
  }

  body.accessible h1 {
    font-size: 24pt !important;
    font-weight: bold !important;
    margin: 20px 0 10px 0 !important;
    color: #000 !important;
  }

  body.accessible h2 {
    font-size: 18pt !important;
    font-weight: bold !important;
    margin: 15px 0 8px 0 !important;
    color: #000 !important;
  }

  body.accessible h3 {
    font-size: 14pt !important;
    font-weight: bold !important;
    margin: 10px 0 5px 0 !important;
    color: #000 !important;
  }

  body.accessible p {
    margin: 8px 0 !important;
    color: #000 !important;
    font-size: 14pt !important;
  }

  body.accessible ul,
  body.accessible ol {
    margin: 10px 0 10px 20px !important;
    padding-left: 20px !important;
  }

  body.accessible li {
    margin: 5px 0 !important;
    color: #000 !important;
    font-size: 14pt !important;
  }

  body.accessible strong {
    font-weight: bold !important;
    color: #000 !important;
  }

  /* High Contrast Mode - White on Black */
  body.accessible.high-contrast {
    background: #000 !important;
    color: #fff !important;
  }

  body.accessible.high-contrast h1,
  body.accessible.high-contrast h2,
  body.accessible.high-contrast h3,
  body.accessible.high-contrast p,
  body.accessible.high-contrast li,
  body.accessible.high-contrast strong {
    color: #fff !important;
  }

  body.accessible.high-contrast .accessible-content {
    background: #000 !important;
    color: #fff !important;
  }

  /* High-contrast overrides for containers forced white by lines above */
  body.accessible.high-contrast #root,
  body.accessible.high-contrast .chakra-ui-light,
  body.accessible.high-contrast .chakra-ui-dark,
  body.accessible.high-contrast [data-theme],
  body.accessible.high-contrast main,
  body.accessible.high-contrast > div,
  body.accessible.high-contrast div#root {
    background: #000 !important;
    background-color: #000 !important;
    background-image: none !important;
  }

  body.accessible .sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0,0,0,0) !important;
    white-space: nowrap !important;
    border-width: 0 !important;
  }

  /* Keep controls visible in accessible mode on screen, hide when printing */
  body.accessible .accessible-controls {
    display: block !important;
    position: sticky !important;
    top: 0 !important;
    background: inherit !important;
    z-index: 1000 !important;
    padding: 10px !important;
    border-bottom: 2px solid currentColor !important;
  }

  /* Accessible View - Print as actual text, not image */
  @media print {
    body.accessible,
    body.accessible.high-contrast {
      background: white !important;
      color: #000 !important;
      font-family: Arial, sans-serif !important;
      font-size: 12pt !important;
      line-height: 1.6 !important;
      margin: 0.75in !important;
      padding: 0 !important;
      /* Force text rendering, disable any graphics */
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    
    body.accessible *,
    body.accessible.high-contrast *,
    body.accessible #root,
    body.accessible > div,
    body.accessible main,
    body.accessible [class*="chakra"],
    body.accessible [class*="css-"] {
      background: white !important;
      background-color: white !important;
      background-image: none !important;
      color: #000 !important;
      box-shadow: none !important;
    }
    
    /* Hide ALL non-text elements in print */
    body.accessible canvas,
    body.accessible img,
    body.accessible svg,
    body.accessible video,
    body.accessible.high-contrast canvas,
    body.accessible.high-contrast img,
    body.accessible.high-contrast svg,
    body.accessible.high-contrast video {
      display: none !important;
      visibility: hidden !important;
    }
    
    body.accessible .accessible-content,
    body.accessible.high-contrast .accessible-content {
      display: block !important;
      background: transparent !important;
      max-width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /* Keep each accessible section together when printing */
    body.accessible .accessible-print-stack > div,
    body.accessible.high-contrast .accessible-print-stack > div {
      page-break-inside: avoid !important;
      break-inside: avoid-page !important;
    }
    
    /* Headings */
    body.accessible h1,
    body.accessible.high-contrast h1 {
      font-size: 20pt !important;
      font-weight: bold !important;
      margin: 16pt 0 12pt 0 !important;
      page-break-after: avoid !important;
    }
    
    body.accessible h2,
    body.accessible.high-contrast h2 {
      font-size: 16pt !important;
      font-weight: bold !important;
      margin: 14pt 0 10pt 0 !important;
      page-break-after: avoid !important;
    }
    
    body.accessible h3,
    body.accessible.high-contrast h3 {
      font-size: 13pt !important;
      font-weight: bold !important;
      margin: 12pt 0 8pt 0 !important;
      page-break-after: avoid !important;
    }
    
    /* Body text */
    body.accessible p,
    body.accessible.high-contrast p {
      margin: 8pt 0 !important;
      font-size: 12pt !important;
      line-height: 1.6 !important;
    }
    
    /* Lists */
    body.accessible ul,
    body.accessible.high-contrast ul,
    body.accessible ol,
    body.accessible.high-contrast ol {
      margin: 8pt 0 !important;
      padding-left: 24pt !important;
    }
    
    body.accessible li,
    body.accessible.high-contrast li {
      margin: 4pt 0 !important;
      font-size: 12pt !important;
      line-height: 1.6 !important;
    }
    
    body.accessible strong,
    body.accessible.high-contrast strong {
      font-weight: bold !important;
    }
    
    /* Hide controls when printing */
    body.accessible .accessible-controls,
    body.accessible.high-contrast .accessible-controls,
    body.accessible .no-print,
    body.accessible.high-contrast .no-print {
      display: none !important;
    }
    
    @page {
      size: letter;
      margin: 0.75in;
    }
  }
  
  /* Conditional page break before differentials in simple print */
  body.simple-print .page-break-before-differentials {
    /* Only apply page break if .force-page-break class is present */
    display: block !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  body.simple-print .page-break-before-differentials.force-page-break {
    page-break-before: always !important;
    break-before: page !important;
  }
  
  /* Two-page variant for simple print */
  body.simple-print.two-page .page-break-after-demographics {
    page-break-after: always !important;
    break-after: page !important;
  }

  /* Two-page tablet mode: page break after interventions ONLY */
  body.two-page-mode .interventions-card {
    page-break-after: always !important;
    break-after: page !important;
  }

  /* Two-Page Print Mode - Fix layout */
  body.tablet-view.two-page-mode {
    background: white !important;
  }

  body.tablet-view.two-page-mode #root,
  body.tablet-view.two-page-mode main,
  body.tablet-view.two-page-mode > div {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* PRINT MEDIA QUERIES - only applies when actually printing */
  @media print {
    /* Preserve tablet view classes during print */
    body.tablet-view.portrait,
    body.tablet-view.landscape {
      /* Keep all tablet view styles during print */
    }
    
    /* Hide only the controls when printing from tablet view */
    body.tablet-view .tablet-controls {
      display: none !important;
      visibility: hidden !important;
    }
    
    /* Two-page mode print fixes */
    body.tablet-view.two-page-mode {
      font-size: 8pt !important;
    }
    
    body.tablet-view.two-page-mode::before {
      content: none !important;
      display: none !important;
    }
    
    body.tablet-view.two-page-mode .scenario-content-for-pdf {
      page-break-before: avoid !important;
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
    
    body.tablet-view.two-page-mode .page-break-before-differentials {
      page-break-before: always !important;
      break-before: page !important;
    }
    
    body.tablet-view.two-page-mode .no-print,
    body.tablet-view.two-page-mode .tablet-controls {
      display: none !important;
    }
    
    @page {
      size: letter;
      margin: 0.25in;
    }
    
    @media print {
      * {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
      }
      
      html:not(.color-print),
      body:not(.color-print),
      body:not(.color-print) *:not(.chakra-card__header) {
        background: white !important;
        background-color: white !important;
      }

      .no-print {
        display: none !important;
      }

      .scenario-header {
        display: none !important;
      }

      .chakra-card {
        box-shadow: none !important;
        border: 1px solid #e2e8f0 !important;
        page-break-inside: avoid;
      }

      body:not(.simple-print):not(.tablet-view):not(.color-print) .chakra-card__header {
        background-color: #f7fafc !important;
        color: #2d3748 !important;
        border-bottom: 1px solid #e2e8f0 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
    
    .chakra-badge {
      border: 1px solid currentColor;
    }
    
    .avoid-break {
      page-break-inside: avoid;
    }
    
    .page-break-before {
      page-break-before: always;
    }
    
    /* Simple Print Mode - ULTRA CONDENSED FOR ONE PAGE - Fix positioning */
    body.simple-print {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    body.simple-print * {
      box-shadow: none !important;
      color: black !important;
    }
    
    /* Hide all UI controls when printing */
    body.simple-print .print-button,
    body.simple-print .back-button,
    body.simple-print .no-print {
      display: none !important;
    }
    
    body.simple-print .chakra-card {
      border: 1px solid #000 !important;
      page-break-inside: avoid;
      display: block !important;
      flex: none !important;
      min-height: auto !important;
      height: auto !important;
    }
    
    body.simple-print .chakra-card__header {
      border-bottom: 1px solid #000 !important;
      display: block !important;
      height: auto !important;
    }
    
    body.simple-print .chakra-card__body {
      display: block !important;
      flex: none !important;
      overflow: visible !important;
      height: auto !important;
    }
    
    body.simple-print .chakra-heading {}
    
    body.simple-print .chakra-text {}
    
    body.simple-print .chakra-stat__label {}
    
    body.simple-print .chakra-stat__number {}
    
    body.simple-print .chakra-stack > * {}
    
    body.simple-print .chakra-list {}
    
    body.simple-print .chakra-list__item {}
    
    body.simple-print @page {
      size: letter;
      margin: 0.15in;
    }
    
    /* Ultra-compress preceptor notes and scenario flow in print */
    body.simple-print .preceptor-notes-section .chakra-card__body,
    body.simple-print .scenario-flow-section .chakra-card__body {
      padding: 0.25px !important;
      line-height: 0.85 !important;
    }
    
    body.simple-print .preceptor-notes-section .chakra-text,
    body.simple-print .scenario-flow-section .chakra-text {
      font-size: 3.5pt !important;
      line-height: 0.85 !important;
      margin: 0 !important;
    }
    
    body.simple-print .preceptor-notes-section .chakra-heading,
    body.simple-print .scenario-flow-section .chakra-heading {
      font-size: 4.5pt !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    /* Watermark in print */
    body.simple-print .print-watermark {
      position: fixed !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) rotate(-45deg) !important;
      font-size: 96px !important;
      font-weight: bold !important;
      opacity: 0.08 !important;
      color: #000 !important;
      pointer-events: none !important;
      user-select: none !important;
      z-index: 9998 !important;
      white-space: nowrap !important;
    }
    
    /* Page break utilities in print */
  body.simple-print .page-break-avoid {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  /* Force single-page simple print: disable explicit page breaks/separators */
  body.simple-print .page-break-before,
  body.simple-print .page-break-after,
  body.simple-print .page-break-before-differentials,
  body.simple-print .page-break {
    page-break-before: auto !important;
    page-break-after: auto !important;
    break-before: auto !important;
    break-after: auto !important;
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
  }
    
    body.simple-print .page-break-after {
      page-break-after: auto !important;
      break-after: auto !important;
    }
    
    body.simple-print .page-break-before {
      page-break-before: auto !important;
      break-before: auto !important;
    }
    
    body.simple-print .page-break {
      display: none !important;
    }
    
    /* Hide icons in simple print */
    body.simple-print svg {
      display: none !important;
    }
    
    /* Force side-by-side layout for first row in simple print */
    body.simple-print .first-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.simple-print .first-row-grid > * {
      display: block !important;
    }
    
    /* Override Chakra's SimpleGrid in simple print */
    body.simple-print .chakra-simple-grid.first-row-grid {
      display: grid !important;
    }
    
    /* Force side-by-side layout for second row (Primary + Secondary Assessment) in simple print */
    body.simple-print .second-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.simple-print .second-row-grid > * {
      display: block !important;
    }
    
    body.simple-print .chakra-simple-grid.second-row-grid {
      display: grid !important;
    }
    
    /* Force side-by-side layout for trending vitals row in simple print */
    body.simple-print .trending-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.simple-print .trending-row-grid > * {
      display: block !important;
    }
    
    body.simple-print .chakra-simple-grid.trending-row-grid {
      display: grid !important;
    }
    
    /* Force 2-column grid layout for interventions and educational content */
    body.simple-print .interventions-grid,
    body.simple-print .critical-actions-list,
    body.simple-print .common-pitfalls-list,
    body.simple-print .differential-diagnosis-list {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.simple-print .interventions-grid > *,
    body.simple-print .critical-actions-list > *,
    body.simple-print .common-pitfalls-list > *,
    body.simple-print .differential-diagnosis-list > * {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      display: block !important;
    }
    
    /* Override Chakra's flex layout */
    body.simple-print .chakra-stack.interventions-grid,
    body.simple-print .chakra-list.critical-actions-list,
    body.simple-print .chakra-list.common-pitfalls-list,
    body.simple-print .chakra-list.differential-diagnosis-list {
      display: grid !important;
    }

    body.simple-print .scenario-content-for-pdf,
    body.simple-print .main-content-wrapper {
      display: block !important;
      overflow: visible !important;
      height: auto !important;
      max-height: none !important;
    }

    body.simple-print .page-break-before-critical-actions {
      display: block !important;
      page-break-before: always !important;
      break-before: page !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
    }
    
    /* Color Print Mode - preserve card colors and readable fonts */
    body.color-print {
      background: white !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    body.color-print #root,
    body.color-print main,
    body.color-print > div {
      background: white !important;
      background-color: white !important;
    }

    body.color-print .no-print,
    body.color-print .scenario-header {
      display: none !important;
    }

    body.color-print * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    body.color-print .chakra-card {
      box-shadow: none !important;
      border: 1px solid #e2e8f0 !important;
      page-break-inside: avoid !important;
    }

    body.color-print .chakra-card__header {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    body.color-print .chakra-badge {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      border: 1px solid currentColor !important;
    }

    body.color-print .chakra-text,
    body.color-print .chakra-heading,
    body.color-print p,
    body.color-print li,
    body.color-print span {
      font-size: inherit !important;
    }

    body.color-print .chakra-card__body {
      font-size: 10pt !important;
    }

    body.color-print .chakra-card__header {
      font-size: 11pt !important;
    }

    body.color-print .chakra-stat__label {
      font-size: 8pt !important;
    }

    body.color-print .chakra-stat__number {
      font-size: 10pt !important;
    }

    /* Hide mode selector and header in tablet view - FORCE HIDE */
    body.tablet-view .no-print {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      overflow: hidden !important;
    }
    
    body.tablet-view .scenario-header {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      overflow: hidden !important;
    }
    
    /* Show tablet controls */
    body.tablet-view .tablet-controls {
      display: block !important;
      visibility: visible !important;
    }
    
    /* Tablet View Mode - PORTRAIT - Matches simple print but with color */
    body.tablet-view.portrait {
      font-size: 7pt !important;
      line-height: 1 !important;
      overflow: auto !important;
      height: 100vh !important;
    }
    
    body.tablet-view.portrait * {
      line-height: 1 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    body.tablet-view.portrait .chakra-card {
      border: 1px solid #e2e8f0 !important;
      padding: 2px !important;
      margin: 1px 0 !important;
      page-break-inside: avoid;
    }
    
    body.tablet-view.portrait .chakra-card__header {
      border-bottom: 1px solid !important;
      padding: 1px 2px !important;
      font-size: 8pt !important;
      font-weight: bold !important;
    }
    
    body.tablet-view.portrait .chakra-card__body {
      padding: 2px !important;
    }
    
    body.tablet-view.portrait .chakra-badge {
      border: 1px solid currentColor !important;
      padding: 0 2px !important;
      display: inline-block;
      font-size: 6pt !important;
    }
    
    body.tablet-view.portrait .chakra-heading {
      font-size: 8pt !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    body.tablet-view.portrait .chakra-text {
      font-size: 7pt !important;
      line-height: 1 !important;
      margin: 0 !important;
    }
    
    body.tablet-view.portrait .chakra-stack > * {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    body.tablet-view.portrait .chakra-list {
      margin: 0 !important;
      padding-left: 10px !important;
    }
    
    body.tablet-view.portrait .chakra-list__item {
      margin: 0 !important;
      line-height: 1.1 !important;
    }
    
    body.tablet-view.portrait .chakra-stat__number {
      font-size: 7pt !important;
    }
    
    body.tablet-view.portrait .chakra-stat__label {
      font-size: 6pt !important;
    }
    
    /* Tablet View Mode - LANDSCAPE - Wider spread layout */
    body.tablet-view.landscape {
      font-size: 8pt !important;
      line-height: 1.2 !important;
      overflow: auto !important;
    }
    
    body.tablet-view.landscape .chakra-card {
      margin-bottom: 3px !important;
      padding: 3px !important;
    }
    
    body.tablet-view.landscape .chakra-card__header {
      padding: 3px 5px !important;
      font-size: 10pt !important;
    }
    
    body.tablet-view.landscape .chakra-card__body {
      padding: 5px !important;
    }
    
    body.tablet-view.landscape .chakra-text {
      font-size: 8pt !important;
      line-height: 1.2 !important;
    }
    
    body.tablet-view.landscape .chakra-stat__number {
      font-size: 9pt !important;
    }
    
    body.tablet-view.landscape .chakra-stat__label {
      font-size: 7pt !important;
    }
    
    /* Landscape: wider grids */
    body.tablet-view.landscape .first-row-grid {
      grid-template-columns: 1fr 1fr 1fr !important;
    }
    
    body.tablet-view.landscape .second-row-grid {
      grid-template-columns: 1fr 1fr 1fr !important;
    }
    
    body.tablet-view.landscape .interventions-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr 1fr 1fr !important;
    }
    
    /* CRITICAL: Force grid layouts in portrait tablet mode - MUST match simple print exactly */
    body.tablet-view.portrait .first-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.tablet-view.portrait .first-row-grid > * {
      display: block !important;
    }
    
    body.tablet-view.portrait .chakra-simple-grid.first-row-grid {
      display: grid !important;
    }
    
    body.tablet-view.portrait .second-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.tablet-view.portrait .second-row-grid > * {
      display: block !important;
    }
    
    body.tablet-view.portrait .chakra-simple-grid.second-row-grid {
      display: grid !important;
    }
    
    body.tablet-view.portrait .trending-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.tablet-view.portrait .trending-row-grid > * {
      display: block !important;
    }
    
    body.tablet-view.portrait .chakra-simple-grid.trending-row-grid {
      display: grid !important;
    }
    
    body.tablet-view.portrait .interventions-grid,
    body.tablet-view.portrait .critical-actions-list,
    body.tablet-view.portrait .common-pitfalls-list,
    body.tablet-view.portrait .differential-diagnosis-list {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.tablet-view.portrait .interventions-grid > *,
    body.tablet-view.portrait .critical-actions-list > *,
    body.tablet-view.portrait .common-pitfalls-list > *,
    body.tablet-view.portrait .differential-diagnosis-list > * {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      display: block !important;
    }
    
    body.tablet-view.portrait .chakra-stack.interventions-grid,
    body.tablet-view.portrait .chakra-list.critical-actions-list,
    body.tablet-view.portrait .chakra-list.common-pitfalls-list,
    body.tablet-view.portrait .chakra-list.differential-diagnosis-list {
      display: grid !important;
    }

  /* Final safety override: prevent viewport clipping in simple print */
  @media print {
      html,
      body.simple-print {
        height: auto !important;
        max-height: none !important;
        min-height: 0 !important;
        overflow: visible !important;
      }

      body.simple-print #root,
      body.simple-print #root > div,
      body.simple-print main,
      body.simple-print .chakra-container,
      body.simple-print .scenario-content-for-pdf,
      body.simple-print .print-scenario {
        display: block !important;
        height: auto !important;
        max-height: none !important;
        min-height: 0 !important;
        overflow: visible !important;
        position: static !important;
      }

      body.simple-print .chakra-tabs,
      body.simple-print .chakra-tab-panels,
      body.simple-print .chakra-tab-panel {
        display: block !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
      }

      /* Prevent large unbreakable flex/card blocks from reserving page space */
      body.simple-print .chakra-card,
      body.simple-print .chakra-card__body,
      body.simple-print .chakra-simple-grid,
      body.simple-print .chakra-stack,
      body.simple-print .chakra-list {
        display: block !important;
        flex: none !important;
        height: auto !important;
        max-height: none !important;
        min-height: 0 !important;
        overflow: visible !important;
        page-break-inside: auto !important;
        break-inside: auto !important;
      }

      body.simple-print .first-row-grid,
      body.simple-print .second-row-grid,
      body.simple-print .trending-row-grid,
      body.simple-print .interventions-grid,
      body.simple-print .critical-actions-list,
      body.simple-print .common-pitfalls-list,
      body.simple-print .differential-diagnosis-list {
        display: grid !important;
        page-break-inside: auto !important;
        break-inside: auto !important;
      }
    }
  }

  /* Final authoritative simple-print overrides (must be last to win conflicts) */
  @media print {
    @page {
      size: auto;
      margin: 0.35in;
    }

    html,
    body.simple-print,
    body.simple-print #root,
    body.simple-print #root > div,
    body.simple-print main,
    body.simple-print .chakra-container,
    body.simple-print .chakra-stack,
    body.simple-print .scenario-content-for-pdf,
    body.simple-print .main-content-wrapper {
      display: block !important;
      position: static !important;
      overflow: visible !important;
      height: auto !important;
      max-height: none !important;
      min-height: 0 !important;
      contain: none !important;
    }

    body.simple-print .print-button,
    body.simple-print .back-button,
    body.simple-print .tablet-controls,
    body.simple-print .no-print {
      display: none !important;
      visibility: hidden !important;
    }

    body.simple-print .chakra-card,
    body.simple-print .chakra-card__body,
    body.simple-print .chakra-simple-grid,
    body.simple-print .chakra-list,
    body.simple-print .chakra-stack {
      display: block !important;
      overflow: visible !important;
      height: auto !important;
      max-height: none !important;
      min-height: 0 !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }

    body.simple-print .first-row-grid,
    body.simple-print .second-row-grid,
    body.simple-print .trending-row-grid,
    body.simple-print .interventions-grid,
    body.simple-print .critical-actions-list,
    body.simple-print .common-pitfalls-list,
    body.simple-print .differential-diagnosis-list {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 4px !important;
      page-break-inside: auto !important;
      break-inside: auto !important;
    }

    body.simple-print .page-break-before-assessment {
      display: block !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      page-break-before: always !important;
      break-before: page !important;
    }

    body.simple-print .page-break-before,
    body.simple-print .page-break-after,
    body.simple-print .page-break-before-differentials {
      display: none !important;
      page-break-before: auto !important;
      page-break-after: auto !important;
      break-before: auto !important;
      break-after: auto !important;
    }
  }

  /* Final simple print pass:
     - print only scenario content area
     - auto-scale to 1 page when possible
     - otherwise use a clean 2-page break between major sections */
  @media print {
    @page {
      size: letter portrait;
      margin: 0.25in;
    }

    html,
    body.simple-print {
      background: #fff !important;
    }

    body.simple-print * {
      visibility: hidden !important;
      box-shadow: none !important;
      text-shadow: none !important;
      background-image: none !important;
    }

    body.simple-print .scenario-content-for-pdf,
    body.simple-print .scenario-content-for-pdf * {
      visibility: visible !important;
    }

    body.simple-print .patient-info-banner,
    body.simple-print .scenario-header,
    body.simple-print .no-print,
    body.simple-print .print-button,
    body.simple-print .back-button,
    body.simple-print .tablet-controls {
      display: none !important;
    }

    body.simple-print .scenario-content-for-pdf {
      position: relative !important;
      left: auto !important;
      top: auto !important;
      width: 100% !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
      height: auto !important;
      transform-origin: top left !important;
      transform: none !important;
      background: #fff !important;
    }

    body.simple-print .scenario-content-for-pdf .chakra-card,
    body.simple-print .scenario-content-for-pdf .page-break-avoid,
    body.simple-print .scenario-content-for-pdf .avoid-break {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      -webkit-column-break-inside: avoid !important;
    }

    body.simple-print .scenario-content-for-pdf .page-break-before-critical-actions,
    body.simple-print .scenario-content-for-pdf .page-break-before,
    body.simple-print .scenario-content-for-pdf .page-break-after,
    body.simple-print .scenario-content-for-pdf .page-break-before-differentials {
      display: none !important;
      page-break-before: auto !important;
      page-break-after: auto !important;
      break-before: auto !important;
      break-after: auto !important;
    }

    body.simple-print[data-simple-print-pages="two"][data-simple-print-break="critical-actions"] .scenario-content-for-pdf .page-break-before-critical-actions {
      display: block !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      page-break-before: always !important;
      break-before: page !important;
    }
  }
`;

const dedicatedPrintStyles = `
  @page {
    size: letter portrait;
    margin: 0.25in;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    overflow: visible !important;
    height: auto !important;
  }

  .no-print,
  .scenario-header,
  .tablet-controls,
  .print-button,
  .back-button {
    display: none !important;
  }

  .scenario-content-for-pdf {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    box-sizing: border-box !important;
  }

  .scenario-content-for-pdf .chakra-card,
  .scenario-content-for-pdf .chakra-card__body,
  .scenario-content-for-pdf .chakra-card__header,
  .scenario-content-for-pdf .chakra-text,
  .scenario-content-for-pdf .chakra-heading,
  .scenario-content-for-pdf .chakra-badge,
  .scenario-content-for-pdf p,
  .scenario-content-for-pdf span,
  .scenario-content-for-pdf li,
  .scenario-content-for-pdf div {
    color: #000 !important;
    background: #fff !important;
    background-color: #fff !important;
  }

  .scenario-content-for-pdf .chakra-card {
    border: 1px solid #000 !important;
    box-shadow: none !important;
  }

  .scenario-content-for-pdf .chakra-card__header {
    border-bottom: 1px solid #000 !important;
  }

  .scenario-content-for-pdf,
  .scenario-content-for-pdf * {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }

  .page-break-before-assessment-dedicated,
  .page-break-after-assessment-row {
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
  }

  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    html,
    body,
    #root,
    #root > div,
    main,
    .chakra-container,
    .main-content-wrapper {
      display: block !important;
      position: static !important;
      width: auto !important;
      height: auto !important;
      max-height: none !important;
      min-height: 0 !important;
      overflow: visible !important;
      transform: none !important;
    }

    .scenario-content-for-pdf {
      width: 100% !important;
      max-width: none !important;
      display: block !important;
      height: auto !important;
      overflow: visible !important;
      margin: 0 !important;
      padding: 0 !important;
      transform: none !important;
    }

    .patient-info-banner {
      display: none !important;
    }

    .scenario-watermark,
    .subtle-watermark {
      display: none !important;
    }

    /* Dedicated print hardening: preserve two-column look using print-stable flex wrapping. */
    .first-row-grid,
    .second-row-grid,
    .trending-row-grid,
    .interventions-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 4px !important;
    }

    .first-row-grid > *,
    .second-row-grid > *,
    .trending-row-grid > *,
    .interventions-grid > * {
      min-width: 0 !important;
      margin: 0 !important;
      break-inside: auto !important;
      page-break-inside: auto !important;
    }

    .critical-actions-list,
    .common-pitfalls-list,
    .differential-diagnosis-list {
      display: block !important;
    }

    .chakra-stack,
    .chakra-list {
      display: block !important;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      page-break-inside: auto !important;
      break-inside: auto !important;
    }

    /* Keep major cards intact when printing */
    .chakra-card,
    .chakra-card__body,
    .page-break-avoid,
    .avoid-break {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      -webkit-column-break-inside: avoid !important;
    }

    .page-break-before-assessment {
      display: block !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      page-break-before: always !important;
      break-before: page !important;
    }

    .page-break-before-assessment-dedicated,
    .page-break-after-assessment-row {
      display: none !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
    }

    body[data-dedicated-split="before"] .page-break-before-assessment-dedicated {
      display: block !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      page-break-before: always !important;
      break-before: page !important;
    }

    body[data-dedicated-split="after"] .page-break-after-assessment-row {
      display: block !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      page-break-before: always !important;
      break-before: page !important;
    }

    .page-break-between-history-assessment {
      display: block !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      page-break-before: always !important;
      break-before: page !important;
    }

    .page-break-before,
    .page-break-after,
    .page-break-before-differentials {
      display: none !important;
    }
  }
`;

// Helper to check if value exists and is not N/A or Unknown
const hasValue = (value) => {
  if (!value) return false;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    return normalized !== 'n/a' && normalized !== 'unknown' && normalized !== '';
  }
  return true;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const PRINT_COPYRIGHT_NOTICE =
  "Copyright © ElevationEMS / PapaMedic AI. Confidential educational material. Unauthorized copying or distribution prohibited.";

const BODY_MODE_CLASSES = [
  "normal",
  "view",
  "simple-print",
  "tablet-view",
  "landscape",
  "portrait",
  "accessible",
  "high-contrast",
  "two-page-mode",
  "color-print",
  "accessibility-mode",
];

// Checkable item wrapper component for evaluation mode
const CheckableItem = ({ children, isChecked, onToggle, label, evaluationMode }) => {
  if (!evaluationMode) {
    return children;
  }

  return (
    <Box
      w="100%"
      display="block"
      breakInside="avoid"
      mb={1}
      cursor="pointer"
      onClick={() => onToggle(!isChecked)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(!isChecked);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={label || "Toggle evaluation item"}
      aria-pressed={isChecked}
    >
      <Box w="100%" minW={0} position="relative">
        <Box
          as="input"
          type="checkbox"
          className="scenario-eval-checkbox"
          checked={isChecked}
          onChange={(e) => onToggle(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          cursor="pointer"
          position="absolute"
          top="8px"
          right="8px"
          w="14px"
          h="14px"
          zIndex={3}
          bg="whiteAlpha.900"
          borderRadius="sm"
        />
        <Box
          w="100%"
          minW={0}
          sx={{
            "& > *": {
              paddingRight: "34px !important"
            }
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

// Helper function to format vital signs with units
const formatVital = (vital) => {
  if (!vital) return null;
  if (typeof vital === 'object') {
    return vital.value ? `${vital.value} ${vital.unit || ''}`.trim() : null;
  }
  return vital;
};

// Helper to determine if vital is abnormal
const getVitalStatus = (type, value) => {
  if (!value) return 'normal';
  
  const numValue = typeof value === 'object' ? parseFloat(value.value) : parseFloat(value);
  if (isNaN(numValue)) return 'normal';

  const ranges = {
    hr: { critical: [numValue < 40 || numValue > 150], warning: [numValue < 60 || numValue > 120] },
    bp_systolic: { critical: [numValue < 90 || numValue > 180], warning: [numValue < 100 || numValue > 140] },
    rr: { critical: [numValue < 8 || numValue > 30], warning: [numValue < 12 || numValue > 24] },
    spo2: { critical: [numValue < 90], warning: [numValue < 94] },
    temp: { critical: [numValue < 95 || numValue > 104], warning: [numValue < 97 || numValue > 100.4] },
    bloodGlucose: { critical: [numValue < 60 || numValue > 300], warning: [numValue < 70 || numValue > 180] }
  };

  const range = ranges[type];
  if (!range) return 'normal';
  
  if (range.critical.some(condition => condition)) return 'critical';
  if (range.warning.some(condition => condition)) return 'warning';
  return 'normal';
};

// Get color for vital status
const getVitalColor = (status) => {
  switch(status) {
    case 'critical': return 'red.600';
    case 'warning': return 'orange.500';
    default: return 'green.600';
  }
};

// Get LOC color
const getLOCColor = (loc) => {
  if (!loc) return 'gray';
  const normalized = loc.toLowerCase();
  if (normalized.includes('alert') || normalized.includes('awake')) return 'green';
  if (normalized.includes('verbal') || normalized.includes('confused')) return 'yellow';
  if (normalized.includes('pain') || normalized.includes('unresponsive')) return 'red';
  return 'gray';
};

// Component to display generated media (images and videos)
const MediaDisplaySection = ({ scenario }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Check if any media exists or is being generated
  const hasMedia = scenario?.patientImageUrl || 
                   scenario?.sceneArrivalVideoUrl || 
                   scenario?.deteriorationVideoUrl || 
                   scenario?.improvementVideoUrl;
  
  const isGenerating = scenario?.mediaGenerationStatus === 'pending' || 
                      scenario?.mediaGenerationStatus === 'processing';
  
  const hasFailed = scenario?.mediaGenerationStatus === 'failed';
  
  if (!hasMedia && !isGenerating && !hasFailed && !scenario?.mediaGenerationEnabled) {
    return null; // Don't show section if media was never enabled
  }
  
  return (
    <Card 
      bg={cardBg} 
      borderColor={borderColor} 
      shadow="md" 
      borderRadius="lg"
      className="page-break-avoid"
      mb={4}
    >
      <CardHeader>
        <HStack spacing={3}>
          <Icon as={FaImage} boxSize={5} color="purple.400" />
          <Heading size="md">Generated Media</Heading>
          {isGenerating && (
            <Badge colorScheme="blue" display="flex" alignItems="center" gap={1}>
              <Spinner size="xs" />
              Generating...
            </Badge>
          )}
          {hasFailed && (
            <Badge colorScheme="red" display="flex" alignItems="center" gap={1}>
              <Icon as={FaExclamationCircle} />
              Generation Failed
            </Badge>
          )}
          {scenario?.mediaGenerationStatus === 'completed' && (
            <Badge colorScheme="green">Complete</Badge>
          )}
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Patient Image */}
          {scenario?.patientImageUrl && (
            <Box>
              <Heading size="sm" mb={3}>Patient Image</Heading>
              <AspectRatio ratio={16/9} maxW="600px">
                <Image 
                  src={scenario.patientImageUrl} 
                  alt="Patient appearance"
                  borderRadius="md"
                  objectFit="cover"
                  fallback={
                    <Flex align="center" justify="center" bg="gray.700" borderRadius="md">
                      <Spinner size="xl" color="purple.400" />
                    </Flex>
                  }
                />
              </AspectRatio>
              <Link href={scenario.patientImageUrl} isExternal color="blue.400" fontSize="sm" mt={2}>
                View Full Size
              </Link>
            </Box>
          )}
          
          {/* Scene Arrival Video */}
          {scenario?.sceneArrivalVideoUrl && (
            <Box>
              <Heading size="sm" mb={3}>Scene Arrival Video</Heading>
              <AspectRatio ratio={16/9} maxW="800px">
                <video 
                  controls 
                  style={{ borderRadius: '8px' }}
                  preload="metadata"
                >
                  <source src={scenario.sceneArrivalVideoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </AspectRatio>
              <Text fontSize="sm" color="gray.400" mt={2}>
                First-person POV arrival at scene
              </Text>
            </Box>
          )}
          
          {/* Clinical Progression Videos */}
          {(scenario?.deteriorationVideoUrl || scenario?.improvementVideoUrl) && (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {scenario?.deteriorationVideoUrl && (
                <Box>
                  <Heading size="sm" mb={3}>Patient Deterioration</Heading>
                  <AspectRatio ratio={16/9}>
                    <video 
                      controls 
                      style={{ borderRadius: '8px' }}
                      preload="metadata"
                    >
                      <source src={scenario.deteriorationVideoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </AspectRatio>
                  <Text fontSize="sm" color="gray.400" mt={2}>
                    Without intervention
                  </Text>
                </Box>
              )}
              
              {scenario?.improvementVideoUrl && (
                <Box>
                  <Heading size="sm" mb={3}>Patient Improvement</Heading>
                  <AspectRatio ratio={16/9}>
                    <video 
                      controls 
                      style={{ borderRadius: '8px' }}
                      preload="metadata"
                    >
                      <source src={scenario.improvementVideoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </AspectRatio>
                  <Text fontSize="sm" color="gray.400" mt={2}>
                    With proper treatment
                  </Text>
                </Box>
              )}
            </SimpleGrid>
          )}
          
          {/* Show generation status if still pending */}
          {isGenerating && (
            <Alert status="info" borderRadius="md">
              <AlertIcon as={FaSpinner} animation="spin 1s linear infinite" />
              <Box>
                <AlertTitle>Media Generation In Progress</AlertTitle>
                <AlertDescription>
                  Videos are being generated in the background. This typically takes 1-2 minutes per video.
                  Refresh this page to see updates.
                </AlertDescription>
              </Box>
            </Alert>
          )}
          
          {/* Show error if generation failed */}
          {hasFailed && scenario?.mediaGenerationError && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Media Generation Failed</AlertTitle>
                <AlertDescription>
                  {scenario.mediaGenerationError}
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

const ScenarioDisplay = ({ 
  scenario, 
  metadata, 
  evaluationForm,
  evaluationMode = false,
  evaluationData = {},
  onEvaluationChange = () => {},
  watermarkText = 'EVALUATION COPY',
  allowPrintControls = true,
  initialPrintMode = "normal",
  dedicatedPrintMode = false,
}) => {
  // Debug logging
  useEffect(() => {
    console.log('=== METADATA DEBUG ===');
    console.log('metadata prop:', metadata);
    console.log('metadata.levelOfCare:', metadata?.levelOfCare);
    console.log('metadata.mechanismNatureOfInjury:', metadata?.mechanismNatureOfInjury);
    console.log('=== END METADATA DEBUG ===');
  }, [metadata]);

  const [printMode, setPrintMode] = useState(initialPrintMode || "normal"); // 'normal', 'simple-print', 'tablet-view', 'accessible'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [orientationMode, setOrientationMode] = useState('auto'); // 'auto', 'landscape', 'portrait'
  const [isHighContrast, setIsHighContrast] = useState(true); // Default to white on black
  const [shouldForcePageBreak, setShouldForcePageBreak] = useState(false);
  const applyBodyModeClasses = useCallback((mode, options = {}) => {
    if (typeof document === "undefined" || !document.body) return;
    const body = document.body;
    BODY_MODE_CLASSES.forEach((cls) => body.classList.remove(cls));

    if (mode === "tablet-view") {
      body.classList.add("tablet-view", options.isLandscape ? "landscape" : "portrait");
      return;
    }

    if (mode === "accessible") {
      body.classList.add("accessible");
      if (options.isHighContrast !== false) {
        body.classList.add("high-contrast");
      }
      return;
    }

    if (mode === "simple-print") {
      body.classList.add("simple-print");
      return;
    }

    if (mode === "view") {
      body.classList.add("view");
      return;
    }

    body.classList.add("normal");
  }, []);

  // Apply body class immediately on mount for dedicated print pages
  useEffect(() => {
    if (initialPrintMode && initialPrintMode !== 'normal') {
      applyBodyModeClasses(initialPrintMode, { isLandscape, isHighContrast });
    }
    return () => {
      if (typeof document !== "undefined" && document.body) {
        BODY_MODE_CLASSES.forEach((cls) => document.body.classList.remove(cls));
      }
    };
  }, [applyBodyModeClasses, initialPrintMode, isHighContrast, isLandscape]);

  const printDebugEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const params = new URLSearchParams(window.location.search || "");
      return params.get("printDebug") === "1" || window.localStorage?.getItem("printDebug") === "1";
    } catch (error) {
      return false;
    }
  }, []);
  const printDebugLog = useCallback((event, details = {}) => {
    if (!printDebugEnabled || typeof window === "undefined") return;
    const payload = {
      ts: new Date().toISOString(),
      event,
      mode: printMode,
      path: window.location.pathname,
      search: window.location.search,
      ...details,
    };
    if (!Array.isArray(window.__printDebugEvents)) window.__printDebugEvents = [];
    window.__printDebugEvents.push(payload);
    if (window.__printDebugEvents.length > 500) {
      window.__printDebugEvents.splice(0, window.__printDebugEvents.length - 500);
    }
    console.log("[PRINTDBG]", payload);
  }, [printDebugEnabled, printMode]);

  useEffect(() => {
    if (!printDebugEnabled || typeof window === "undefined") return;

    const persistEvents = () => {
      try {
        const events = Array.isArray(window.__printDebugEvents) ? window.__printDebugEvents : [];
        window.localStorage?.setItem("printDebugEvents", JSON.stringify(events.slice(-1000)));
      } catch (error) {
        // ignore persistence errors
      }
    };

    printDebugLog("debug_enabled", {
      dedicatedPrintMode,
      initialPrintMode,
      allowPrintControls,
    });
    persistEvents();

    window.__dumpPrintDebugEvents = () => {
      const events = Array.isArray(window.__printDebugEvents) ? window.__printDebugEvents : [];
      console.table(events);
      return events;
    };
    window.__dumpStoredPrintDebugEvents = () => {
      try {
        const stored = JSON.parse(window.localStorage?.getItem("printDebugEvents") || "[]");
        console.table(stored);
        return stored;
      } catch (error) {
        return [];
      }
    };

    const beforeHandler = () => {
      printDebugLog("beforeprint_event", {
        bodyClass: document.body?.className || "",
      });
      persistEvents();
    };
    const afterHandler = () => {
      printDebugLog("afterprint_event", {
        bodyClass: document.body?.className || "",
      });
      persistEvents();
    };
    const unloadHandler = () => {
      persistEvents();
    };

    window.addEventListener("beforeprint", beforeHandler);
    window.addEventListener("afterprint", afterHandler);
    window.addEventListener("beforeunload", unloadHandler);
    window.addEventListener("unload", unloadHandler);

    if (!window.__printDebugWrapped) {
      const originalPrint = window.print?.bind(window);
      const originalOpen = window.open?.bind(window);
      window.__printDebugOriginalPrint = originalPrint;
      window.__printDebugOriginalOpen = originalOpen;

      window.print = (...args) => {
        const stack = new Error("window.print callsite").stack?.split("\n").slice(1, 7).join("\n");
        if (!Array.isArray(window.__printDebugEvents)) window.__printDebugEvents = [];
        window.__printDebugEvents.push({
          ts: new Date().toISOString(),
          event: "window.print_called",
          path: window.location.pathname,
          search: window.location.search,
          bodyClass: document.body?.className || "",
          args,
          stack,
        });
        console.log("[PRINTDBG]", "window.print_called", { args, stack });
        if (typeof originalPrint === "function") return originalPrint(...args);
        return undefined;
      };

      window.open = (...args) => {
        const stack = new Error("window.open callsite").stack?.split("\n").slice(1, 7).join("\n");
        if (!Array.isArray(window.__printDebugEvents)) window.__printDebugEvents = [];
        window.__printDebugEvents.push({
          ts: new Date().toISOString(),
          event: "window.open_called",
          path: window.location.pathname,
          search: window.location.search,
          args,
          stack,
        });
        console.log("[PRINTDBG]", "window.open_called", { args, stack });
        if (typeof originalOpen === "function") return originalOpen(...args);
        return null;
      };

      if (window.HTMLCanvasElement && !window.HTMLCanvasElement.prototype.__printDebugWrapped) {
        const proto = window.HTMLCanvasElement.prototype;
        const originalToDataURL = proto.toDataURL;
        const originalToBlob = proto.toBlob;

        proto.toDataURL = function patchedToDataURL(...args) {
          const stack = new Error("canvas.toDataURL callsite").stack?.split("\n").slice(1, 6).join("\n");
          if (!Array.isArray(window.__printDebugEvents)) window.__printDebugEvents = [];
          window.__printDebugEvents.push({
            ts: new Date().toISOString(),
            event: "canvas.toDataURL_called",
            path: window.location.pathname,
            size: { width: this.width, height: this.height },
            args,
            stack,
          });
          console.log("[PRINTDBG]", "canvas.toDataURL_called", { width: this.width, height: this.height, args, stack });
          return originalToDataURL.apply(this, args);
        };

        proto.toBlob = function patchedToBlob(...args) {
          const stack = new Error("canvas.toBlob callsite").stack?.split("\n").slice(1, 6).join("\n");
          if (!Array.isArray(window.__printDebugEvents)) window.__printDebugEvents = [];
          window.__printDebugEvents.push({
            ts: new Date().toISOString(),
            event: "canvas.toBlob_called",
            path: window.location.pathname,
            size: { width: this.width, height: this.height },
            stack,
          });
          console.log("[PRINTDBG]", "canvas.toBlob_called", { width: this.width, height: this.height, stack });
          return originalToBlob.apply(this, args);
        };

        proto.__printDebugWrapped = true;
      }

      window.__printDebugWrapped = true;
    }

    return () => {
      persistEvents();
      window.removeEventListener("beforeprint", beforeHandler);
      window.removeEventListener("afterprint", afterHandler);
      window.removeEventListener("beforeunload", unloadHandler);
      window.removeEventListener("unload", unloadHandler);
    };
  }, [allowPrintControls, dedicatedPrintMode, initialPrintMode, printDebugEnabled, printDebugLog]);
  const [watermarkConfig, setWatermarkConfig] = useState(null);
  const [showRawUserInput, setShowRawUserInput] = useState(false);
  const [showRawInput, setShowRawInput] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState(null); // { type: 'image' | 'video', url: string, title: string }
  const [captureBlockVisible, setCaptureBlockVisible] = useState(false);
  const [captureBlockReason, setCaptureBlockReason] = useState("");
  const [captureShowMeme, setCaptureShowMeme] = useState(false);
  const [captureImageSrc, setCaptureImageSrc] = useState(CRYING_JORDAN_IMAGE_URL);
  const captureBlockTimeoutRef = useRef(null);
  const lastMemeTriggerAtRef = useRef(0);
  const suppressFocusSignalsUntilRef = useRef(0);
  const inferredDisplayLevel = normalizeProviderLevel(
    scenario?.trainingLevel ||
    scenario?.ScenarioMetadata?.TrainingLevel ||
    metadata?.levelOfCare ||
    metadata?.trainingLevel,
    "EMT"
  );
  const [selectedDisplayLevel, setSelectedDisplayLevel] = useState(inferredDisplayLevel);

  // Get current user for watermarking
  const user = useUser(api);
  const borderWatermarkLabel = useMemo(() => {
    const institutionLabel = watermarkConfig?.institution || user?.institution || metadata?.institution || "Educational Use";
    const scenarioLabel = scenario?.id ? `Scenario ${scenario.id}` : "Scenario";
    return `${watermarkText || "Confidential"} • ${institutionLabel} • ${scenarioLabel}`;
  }, [watermarkConfig?.institution, user?.institution, metadata?.institution, scenario?.id, watermarkText]);
  const printBorderLabel = useMemo(() => {
    return `${borderWatermarkLabel} • ${new Date().toISOString()}`;
  }, [borderWatermarkLabel]);

  // Helper to check if user has admin role
  const isAdmin = () => {
    if (!user?.roles) return false;
    
    // Check if user has admin or system-admin role
    return user.roles.some(role => {
      // Handle both string roles and role objects with key property
      if (typeof role === 'string') {
        return role === 'admin' || role === 'system-admin';
      } else if (role && typeof role === 'object') {
        return role.key === 'admin' || role.key === 'system-admin' || role === 'admin' || role === 'system-admin';
      }
      return false;
    });
  };
  
  // Fetch watermark configuration
  const [{ data: configData, fetching: fetchingConfig }, getWatermarkConfig] = useGlobalAction(api.getWatermarkConfig);

  useEffect(() => {
    // Fetch watermark config on mount
    if (user?.institution || user?.id) {
      getWatermarkConfig({
        institution: user?.institution,
        userId: user?.id
      });
    }
  }, [user?.institution, user?.id]);

  useEffect(() => {
    // Update watermark config when data arrives
    if (configData) {
      setWatermarkConfig(configData);
    }
  }, [configData]);

  useEffect(() => {
    setSelectedDisplayLevel(inferredDisplayLevel);
  }, [inferredDisplayLevel]);

  // Evaluation checkbox handlers
  const handleCheckboxToggle = (category, field, value) => {
    if (!evaluationMode) return;
    
    const newData = { ...evaluationData };
    if (!newData[category]) {
      newData[category] = {};
    }
    newData[category][field] = value;
    onEvaluationChange(newData);
  };

  const isChecked = (category, field) => {
    return evaluationData?.[category]?.[field] || false;
  };

  const clearSimplePrintLayout = useCallback(() => {
    if (typeof document === "undefined" || !document.body) return;
    document.body.removeAttribute("data-simple-print-pages");
    document.body.removeAttribute("data-simple-print-break");
  }, []);

  const prepareSimplePrintLayout = useCallback(() => {
    if (typeof document === "undefined" || !document.body) {
      return { pages: "one", scale: 1 };
    }

    const container = document.querySelector(".scenario-content-for-pdf");
    if (!container) {
      clearSimplePrintLayout();
      return { pages: "one", scale: 1 };
    }

    const LETTER_HEIGHT_IN = 11;
    const PRINT_MARGIN_IN = 0.25;
    const DPI = 96;
    const printableHeightPx = (LETTER_HEIGHT_IN - (PRINT_MARGIN_IN * 2)) * DPI;

    const rect = container.getBoundingClientRect();
    const contentHeight = Math.max(container.scrollHeight, rect.height, 1);

    let pages = "one";
    if (contentHeight > printableHeightPx) {
      pages = "two";
    }

    document.body.setAttribute("data-simple-print-pages", pages);
    if (pages === "two") {
      document.body.setAttribute("data-simple-print-break", "critical-actions");
    } else {
      document.body.removeAttribute("data-simple-print-break");
    }

    return { pages, scale: 1 };
  }, [clearSimplePrintLayout]);

  const printScenarioContainer = useCallback((mode = "color") => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const sourceNode = document.querySelector(".scenario-content-for-pdf");
    if (!sourceNode) {
      window.print();
      return;
    }

    const clonedNode = sourceNode.cloneNode(true);
    if (clonedNode?.querySelectorAll) {
      clonedNode.querySelectorAll(".no-print, .print-button, .back-button, .tablet-controls, .scenario-header, .patient-info-banner, .inline-watermark-border-top, .inline-watermark-border-bottom")
        .forEach((el) => el.remove());
    }

    const isSimpleMode = mode === "simple";
    const borderLabel = escapeHtml(printBorderLabel);
    const copyrightLabel = escapeHtml(PRINT_COPYRIGHT_NOTICE);
    const headStyles = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
      .filter((node) => {
        if (!isSimpleMode) return true;
        if (node.tagName !== "STYLE") return true;
        const css = node.textContent || "";
        // Exclude the in-app simple print stylesheet to avoid viewport clipping conflicts in popup print.
        if (css.includes("body.simple-print")) return false;
        if (css.includes("data-simple-print-pages")) return false;
        return true;
      })
      .map((node) => node.outerHTML)
      .join("\n");
    const bodyClass = isSimpleMode ? "simple-print-popup" : "color-print";

    const printWindow = window.open("", "_blank", "width=1280,height=900,menubar=yes,toolbar=yes");
    if (!printWindow) {
      window.print();
      return;
    }

    const extraStyles = `
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        width: auto !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
      }
      .print-root {
        width: 100% !important;
        max-width: none !important;
        margin: 0.16in 0 !important;
        padding: 0 !important;
        overflow: visible !important;
      }
      .print-fixed-border {
        position: fixed;
        left: ${isSimpleMode ? "0.25in" : "0.35in"};
        right: ${isSimpleMode ? "0.25in" : "0.35in"};
        z-index: 2147483647;
        font-size: 8px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #6b7280;
        background: #ffffff;
        line-height: 1.2;
        pointer-events: none;
      }
      .print-fixed-border.top {
        top: 0.02in;
        border-bottom: 1px solid #d1d5db;
        padding: 0 0 2px 0;
      }
      .print-fixed-border.bottom {
        bottom: 0.02in;
        border-top: 1px solid #d1d5db;
        padding: 2px 0 0 0;
        text-transform: none;
        letter-spacing: 0;
      }
      .print-root .scenario-content-for-pdf {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
        transform: none !important;
      }
      .print-root .scenario-content-for-pdf,
      .print-root .scenario-content-for-pdf * {
        max-height: none !important;
        overflow: visible !important;
      }
      .print-root .no-print,
      .print-root .print-button,
      .print-root .back-button,
      .print-root .tablet-controls,
      .print-root .scenario-header,
      .print-root .patient-info-banner {
        display: none !important;
      }
      @page {
        size: letter portrait;
        margin: ${isSimpleMode ? "0.25in" : "0.35in"};
      }
      @media print {
        html, body, .print-root, .print-root * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
      body.simple-print-popup .chakra-card__header,
      body.simple-print-popup .chakra-badge {
        background: #fff !important;
        color: #111 !important;
        border-color: #d1d5db !important;
      }
      body.simple-print-popup .print-watermark,
      body.simple-print-popup .scenario-watermark,
      body.simple-print-popup .subtle-watermark {
        display: none !important;
      }
    `;

    printWindow.document.open();
    printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Scenario Print</title>
    ${headStyles}
    <style>${extraStyles}</style>
  </head>
  <body class="${bodyClass}">
    <div class="print-fixed-border top">${borderLabel}</div>
    <div class="print-root">${clonedNode.outerHTML}</div>
    <div class="print-fixed-border bottom">${copyrightLabel}</div>
    <script>
      window.addEventListener("load", function() {
        setTimeout(function() { window.print(); }, 120);
      });
      window.addEventListener("afterprint", function() { window.close(); });
    <\/script>
  </body>
</html>`);
    printWindow.document.close();
  }, [printBorderLabel]);

  const printSimpleView = useCallback(async (pageMode = "one", options = {}) => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const useSimpleLayout = options.useSimpleLayout !== false;
    const borderLabel = escapeHtml(printBorderLabel);
    const copyrightLabel = escapeHtml(PRINT_COPYRIGHT_NOTICE);

    const sourceNode = document.querySelector(".scenario-content-for-pdf");
    if (!sourceNode) {
      window.print();
      return;
    }

    const layout = useSimpleLayout ? prepareSimplePrintLayout() : { pages: "auto", scale: 1 };
    printDebugLog("simple_print_layout_prepared", { ...layout, pageMode, useSimpleLayout });

    const printWindow = window.open("", "_blank", "width=1280,height=900,menubar=yes,toolbar=yes");
    if (!printWindow) {
      // Fallback to legacy popup print if the browser blocks new windows.
      printScenarioContainer(useSimpleLayout ? "simple" : "color");
      if (useSimpleLayout) clearSimplePrintLayout();
      return;
    }

    try {
      document.body?.setAttribute("data-snapshot-print", "true");
      const { default: html2canvas } = await import("html2canvas");

      const DPI = 96;
      const PAGE_WIDTH_PX = 8.5 * DPI;
      const PAGE_HEIGHT_PX = 11 * DPI;
      const MARGIN_PX = 0.25 * DPI;
      const printableWidth = PAGE_WIDTH_PX - (MARGIN_PX * 2);
      const printableHeight = PAGE_HEIGHT_PX - (MARGIN_PX * 2);
      const isOnePageMode = pageMode === "one";
      const framePaddingPx = 6 * 2; // .print-page-frame has 6px top + 6px bottom padding.
      const frameBorderPx = 2; // top+bottom border in .print-page-frame
      const borderBandsPx = isOnePageMode ? 48 : 40; // Reserve space for top+bottom border rows inside each page frame.
      const safetyPaddingPx = isOnePageMode ? 4 : 8; // Small guard to prevent footer spillover to next page.
      const innerPrintableWidth = Math.max(1, printableWidth - 12);
      const innerPrintableHeight = Math.max(1, printableHeight - framePaddingPx - frameBorderPx - borderBandsPx - safetyPaddingPx);
      const blockGapPx = 8;
      const buildPageShell = (innerHtml) =>
        `<section class="print-page"><div class="print-page-frame"><div class="print-page-border top">${borderLabel}</div><div class="print-page-inner">${innerHtml}</div><div class="print-page-border bottom">${copyrightLabel}</div></div></section>`;

      let pagesMarkup = "";
      if (pageMode === "two" || pageMode === "auto") {
        const visibleBlocks = Array.from(sourceNode.children).filter((node) => {
          if (!(node instanceof HTMLElement)) return false;
          if (node.classList.contains("no-print")) return false;
          if (node.classList.contains("print-button")) return false;
          if (node.classList.contains("back-button")) return false;
          if (node.classList.contains("tablet-controls")) return false;
          const style = window.getComputedStyle(node);
          if (style.display === "none" || style.visibility === "hidden") return false;
          return node.offsetHeight > 0 || node.scrollHeight > 0;
        });
        const captureTargets = visibleBlocks.length > 0 ? visibleBlocks : [sourceNode];
        const captures = [];

        for (const block of captureTargets) {
          const captureWidth = Math.ceil(Math.max(block.scrollWidth, block.clientWidth, sourceNode.clientWidth, 900));
          const captureHeight = Math.ceil(Math.max(block.scrollHeight, block.clientHeight));
          if (captureHeight <= 0) continue;
          const canvas = await html2canvas(block, {
            scale: 1.45,
            useCORS: true,
            backgroundColor: "#ffffff",
            logging: false,
            width: captureWidth,
            height: captureHeight,
            windowWidth: Math.max(captureWidth, document.documentElement.clientWidth),
            windowHeight: Math.max(captureHeight, document.documentElement.clientHeight),
            scrollX: 0,
            scrollY: 0,
          });
          captures.push({
            dataUrl: canvas.toDataURL("image/png"),
            width: canvas.width,
            height: canvas.height,
          });
        }

        const normalized = captures.map((capture) => ({
          ...capture,
          renderWidth: innerPrintableWidth,
          renderHeight: (capture.height / Math.max(capture.width, 1)) * innerPrintableWidth,
        }));

        const buildPages = (scale) => {
          const pages = [{ items: [], used: 0 }];
          for (const item of normalized) {
            let pageWidth = item.renderWidth * scale;
            let pageHeight = item.renderHeight * scale;
            if (pageHeight > innerPrintableHeight) {
              const shrink = innerPrintableHeight / pageHeight;
              pageHeight = innerPrintableHeight;
              pageWidth = pageWidth * shrink;
            }

            const lastPage = pages[pages.length - 1];
            const gap = lastPage.items.length > 0 ? blockGapPx : 0;
            if (lastPage.used + gap + pageHeight <= innerPrintableHeight + 0.5) {
              lastPage.items.push({ ...item, pageWidth, pageHeight });
              lastPage.used += gap + pageHeight;
              continue;
            }

            pages.push({
              items: [{ ...item, pageWidth, pageHeight }],
              used: pageHeight,
            });
          }
          return pages;
        };

        let scale = 1;
        let pages = buildPages(scale);
        if (pageMode === "two") {
          while (pages.length > 2 && scale > 0.18) {
            scale *= 0.92;
            pages = buildPages(scale);
          }
        }

        pagesMarkup = pages
          .map((page) => {
            const images = page.items
              .map((item) => {
                const xOffsetIn = ((innerPrintableWidth - item.pageWidth) / 2) / DPI;
                return `<img src="${item.dataUrl}" alt="Scenario section" style="display:block;width:${(item.pageWidth / DPI).toFixed(4)}in;height:${(item.pageHeight / DPI).toFixed(4)}in;max-width:none;border:0;margin:0 0 0 ${xOffsetIn.toFixed(4)}in;" />`;
              })
              .join("");
            return buildPageShell(images);
          })
          .join("");
      } else {
        const captureWidth = Math.ceil(Math.max(sourceNode.scrollWidth, sourceNode.clientWidth, 900));
        const captureHeight = Math.ceil(Math.max(sourceNode.scrollHeight, sourceNode.clientHeight, 1200));
        const canvas = await html2canvas(sourceNode, {
          scale: 1.45,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          width: captureWidth,
          height: captureHeight,
          windowWidth: Math.max(captureWidth, document.documentElement.clientWidth),
          windowHeight: Math.max(captureHeight, document.documentElement.clientHeight),
          scrollX: 0,
          scrollY: 0,
        });

        const naturalRenderWidth = innerPrintableWidth;
        const naturalRenderHeight = (canvas.height / Math.max(canvas.width, 1)) * naturalRenderWidth;
        const onePageBufferPx = isOnePageMode ? 0 : 0;
        const fitScale = Math.min(1, (innerPrintableHeight - onePageBufferPx) / Math.max(naturalRenderHeight, 1));
        const finalRenderWidth = naturalRenderWidth * fitScale;
        const finalRenderHeight = naturalRenderHeight * fitScale;
        const xOffsetIn = ((innerPrintableWidth - finalRenderWidth) / 2) / DPI;
        pagesMarkup = buildPageShell(`<img src="${canvas.toDataURL("image/png")}" alt="Scenario" style="display:block;width:${(finalRenderWidth / DPI).toFixed(4)}in;height:${(finalRenderHeight / DPI).toFixed(4)}in;max-width:none;border:0;margin:0 0 0 ${xOffsetIn.toFixed(4)}in;" />`);
      }

      printWindow.document.open();
      printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Scenario Print</title>
    <style>
      @page { size: letter portrait; margin: 0.25in; }
      html, body {
        margin: 0;
        padding: 0;
        background: #fff;
      }
      .print-page {
        width: 100%;
        page-break-after: always;
        break-after: page;
      }
      .print-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }
      .print-page-frame {
        box-sizing: border-box;
        width: 100%;
        border: 1px solid #d1d5db;
        padding: 6px;
      }
      .print-page-border {
        font-size: 8px;
        line-height: 1.2;
        color: #6b7280;
        background: #ffffff;
      }
      .print-page-border.top {
        text-transform: uppercase;
        letter-spacing: 0.08em;
        border-bottom: 1px solid #d1d5db;
        padding-bottom: 3px;
        margin-bottom: 6px;
      }
      .print-page-border.bottom {
        border-top: 1px solid #d1d5db;
        padding-top: 3px;
        margin-top: 6px;
      }
      body.one-page-print .print-page-frame {
        padding: 5px;
      }
      body.one-page-print .print-page-border {
        font-size: 7px;
        line-height: 1.15;
      }
      body.one-page-print .print-page-border.top {
        padding-bottom: 2px;
        margin-bottom: 4px;
      }
      body.one-page-print .print-page-border.bottom {
        padding-top: 2px;
        margin-top: 4px;
      }
      .print-page-inner {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${blockGapPx}px;
        align-items: flex-start;
      }
      @media print {
        html, body, .print-page, .print-page-inner, img {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    </style>
  </head>
  <body class="${isOnePageMode ? "one-page-print" : ""}">${pagesMarkup}
    <script>
      window.addEventListener("load", function() {
        setTimeout(function() { window.print(); }, 120);
      });
      window.addEventListener("afterprint", function() { window.close(); });
    <\/script>
  </body>
</html>`);
      printWindow.document.close();
    } catch (error) {
      console.error("Simple print image pipeline failed, falling back:", error);
      try {
        printWindow.close();
      } catch (closeError) {
        // no-op
      }
      printScenarioContainer(useSimpleLayout ? "simple" : "color");
    } finally {
      document.body?.removeAttribute("data-snapshot-print");
      if (useSimpleLayout) clearSimplePrintLayout();
    }
  }, [clearSimplePrintLayout, prepareSimplePrintLayout, printDebugLog, printScenarioContainer, printBorderLabel]);



  const handlePrintMode = (mode) => {
    if (!allowPrintControls && mode === "simple-print") return;
    if (mode !== "simple-print") {
      clearSimplePrintLayout();
    }
    printDebugLog("handlePrintMode_called", {
      fromMode: printMode,
      toMode: mode,
      bodyClassBefore: document.body?.className || "",
    });
    suppressFocusSignalsUntilRef.current = Date.now() + 2500;
    setPrintMode(mode);

    if (mode === 'tablet-view') {
      enterFullscreen();
    } else if (isFullscreen) {
      exitFullscreen();
    }

    printDebugLog("handlePrintMode_completed", {
      toMode: mode,
      bodyClassAfter: document.body?.className || "",
    });
  };

  const toggleHighContrast = () => {
    setIsHighContrast((prev) => !prev);
  };
  
  const enterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().then(() => setIsFullscreen(true)).catch(err => console.log(err));
    } else if (elem.webkitRequestFullscreen) { /* Safari */
      elem.webkitRequestFullscreen();
      setIsFullscreen(true);
    } else if (elem.msRequestFullscreen) { /* IE11 */
      elem.msRequestFullscreen();
      setIsFullscreen(true);
    }
  };
  
  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(err => console.log(err));
    } else if (document.webkitExitFullscreen) { /* Safari */
      document.webkitExitFullscreen();
      setIsFullscreen(false);
    } else if (document.msExitFullscreen) { /* IE11 */
      document.msExitFullscreen();
      setIsFullscreen(false);
    }
  };

  const setOrientation = (mode) => {
    setOrientationMode(mode);

    if (mode === 'landscape') {
      setIsLandscape(true);
    } else if (mode === 'portrait') {
      setIsLandscape(false);
    } else {
      const newIsLandscape = window.innerWidth > window.innerHeight;
      setIsLandscape(newIsLandscape);
    }
  };

  const checkContentHeight = () => {
    if (printMode !== 'simple-print') return;
    
    // Get the element before the page break
    const pageBreakEl = document.querySelector('.page-break-before-differentials');
    if (!pageBreakEl) return;
    
    // Calculate height of content before the break
    const contentBeforeBreak = pageBreakEl.getBoundingClientRect().top;
    
    // Standard US Letter page height at 96 DPI minus margins (roughly 950px for content area)
    const pageHeight = 950;
    
    // If content fits on one page (with some buffer), force the page break
    // If content spills onto page 2 anyway, don't force it
    if (contentBeforeBreak < pageHeight) {
      setShouldForcePageBreak(true);
    } else {
      setShouldForcePageBreak(false);
    }
  };

  const exitTabletMode = () => {
    suppressFocusSignalsUntilRef.current = Date.now() + 2500;
    setPrintMode('normal');
    exitFullscreen();
  };

  const generateFilename = () => {
    return buildScenarioDownloadFilename(
      { ...scenario, id: metadata?.id || scenario?.id },
      "pdf",
      metadata?.title || "clinical-scenario"
    );
  };

  const openMediaModal = (type, url, title) => {
    setCurrentMedia({ type, url, title });
    setMediaModalOpen(true);
  };

  const handleSaveTabletPDF = async (pageMode = 'single') => {
    printDebugLog("handleSaveTabletPDF_enter", { pageMode });
    // For two-page mode, just use native print with the CSS we already have
    if (pageMode === 'two') {
      // Switch to tablet view temporarily
      const originalPrintMode = printMode;
      
      setPrintMode('tablet-view');
      applyBodyModeClasses("tablet-view", { isLandscape: false, isHighContrast });
      document.body.classList.add("two-page-mode");
      
      // Wait for styles to apply
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Use native print dialog
      window.print();
      
      // Restore after a delay to allow print dialog to capture styles
      setTimeout(() => {
        setPrintMode(originalPrintMode);
        applyBodyModeClasses(originalPrintMode, { isLandscape, isHighContrast });
        document.body.classList.remove("two-page-mode");
      }, 500);
      
      return;
    }
    
    // Import libraries
    const { jsPDF } = await import('jspdf');
    const html2canvas = await import('html2canvas');
    
    // Switch to tablet view temporarily
    const originalPrintMode = printMode;
    
    // Apply tablet-view styles
    setPrintMode('tablet-view');
    applyBodyModeClasses("tablet-view", { isLandscape: false, isHighContrast });
    
    // Wait for styles to apply
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Get the main content element
    const element = document.querySelector('.scenario-content-for-pdf') || document.body;
    
    // Create canvas with optimized settings for file size
    const canvas = await html2canvas.default(element, {
      scale: 1.5, // Balanced quality and file size
      useCORS: true,
      logging: false,
      imageTimeout: 0,
      removeContainer: true,
      allowTaint: true,
      windowWidth: 800 // Smaller window
    });
    
    // Use JPEG with higher compression for smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.7); // 70% quality JPEG
    const pdf = new jsPDF('p', 'pt', 'letter');
    
    // Get PDF page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Define margins
    const margin = 20;
    const availableWidth = pageWidth - (2 * margin);
    const availableHeight = pageHeight - (2 * margin);
    
    // Calculate aspect ratios
    const canvasAspect = canvas.width / canvas.height;
    const pageAspect = availableWidth / availableHeight;
    
    // Calculate scaled dimensions to fit content on one page with margins
    let scaledWidth, scaledHeight, xOffset, yOffset;
    
    if (canvasAspect > pageAspect) {
      // Image is wider relative to page - fit to width
      scaledWidth = availableWidth;
      scaledHeight = availableWidth / canvasAspect;
      xOffset = margin;
      yOffset = margin + (availableHeight - scaledHeight) / 2;
    } else {
      // Image is taller relative to page - fit to height
      scaledHeight = availableHeight;
      scaledWidth = availableHeight * canvasAspect;
      xOffset = margin + (availableWidth - scaledWidth) / 2;
      yOffset = margin;
    }
    
    // Add image to PDF with FAST compression
    pdf.addImage(imgData, 'JPEG', xOffset, yOffset, scaledWidth, scaledHeight, undefined, 'FAST');
    
    // Restore original mode
    setPrintMode(originalPrintMode);
    applyBodyModeClasses(originalPrintMode, { isLandscape, isHighContrast });
    
    // Save PDF
    pdf.save(generateFilename());
  };

  const handleSaveSimplePDF = async () => {
    printDebugLog("handleSaveSimplePDF_enter");
    const scenarioNode = document.querySelector(".scenario-content-for-pdf");
    if (!scenarioNode) {
      window.print();
      return;
    }

    let scratch = null;
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      // Build an unconstrained off-screen clone so capture isn't limited by viewport/layout overflow.
      scratch = document.createElement("div");
      scratch.setAttribute("aria-simple-print-clone", "true");
      scratch.style.position = "fixed";
      scratch.style.left = "-100000px";
      scratch.style.top = "0";
      scratch.style.width = `${Math.max(scenarioNode.scrollWidth, scenarioNode.clientWidth, 1000)}px`;
      scratch.style.height = "auto";
      scratch.style.maxHeight = "none";
      scratch.style.overflow = "visible";
      scratch.style.background = "#ffffff";
      scratch.style.zIndex = "-1";

      const cloneRoot = scenarioNode.cloneNode(true);
      cloneRoot.style.width = "100%";
      cloneRoot.style.height = "auto";
      cloneRoot.style.maxHeight = "none";
      cloneRoot.style.overflow = "visible";
      cloneRoot.style.position = "static";

      const loosenNode = (el) => {
        if (!(el instanceof HTMLElement)) return;
        el.style.maxHeight = "none";
        el.style.height = "auto";
        el.style.overflow = "visible";
        if (el.style.position === "fixed" || el.style.position === "sticky") {
          el.style.position = "static";
        }
      };
      loosenNode(cloneRoot);
      cloneRoot.querySelectorAll("*").forEach(loosenNode);

      scratch.appendChild(cloneRoot);
      document.body.appendChild(scratch);
      await new Promise((resolve) => setTimeout(resolve, 150));

      const sourceBlocks = Array.from(cloneRoot.children).filter((node) => {
        if (!(node instanceof HTMLElement)) return false;
        const style = window.getComputedStyle(node);
        return style.display !== "none" && node.offsetHeight > 0;
      });
      const captureBlocks = sourceBlocks.length > 0 ? sourceBlocks : [cloneRoot];
      const captures = [];

      for (const block of captureBlocks) {
        const captureWidth = Math.ceil(Math.max(block.scrollWidth, block.clientWidth, 900));
        const captureHeight = Math.ceil(Math.max(block.scrollHeight, block.clientHeight));
        if (captureHeight <= 0) continue;
        const canvas = await html2canvas(block, {
          scale: 1.15,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          width: captureWidth,
          height: captureHeight,
          windowWidth: captureWidth,
          windowHeight: captureHeight,
          scrollX: 0,
          scrollY: 0,
        });
        captures.push(canvas);
      }

      if (captures.length === 0) {
        window.print();
        return;
      }

      const pdf = new jsPDF("p", "pt", "letter");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 18;
      const innerWidth = pageWidth - margin * 2;
      const innerHeight = pageHeight - margin * 2;
      const targetPages = 2;

      const totalHeightAtFullWidth = captures.reduce((sum, canvas) => {
        return sum + (canvas.height / canvas.width) * innerWidth;
      }, 0);
      const globalScale = Math.min(1, (targetPages * innerHeight) / Math.max(totalHeightAtFullWidth, 1));

      let y = margin;
      const ensureSpace = (needed) => {
        if ((y - margin) + needed <= innerHeight + 0.1) return;
        pdf.addPage();
        y = margin;
      };

      for (const canvas of captures) {
        let srcOffset = 0;
        while (srcOffset < canvas.height - 0.5) {
          const remainingPt = innerHeight - (y - margin);
          if (remainingPt <= 2) {
            pdf.addPage();
            y = margin;
            continue;
          }

          const maxSrcSliceHeight = Math.max(
            1,
            Math.floor((remainingPt / (innerWidth * globalScale)) * canvas.width)
          );
          const srcSliceHeight = Math.min(maxSrcSliceHeight, canvas.height - srcOffset);
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = srcSliceHeight;
          const sliceCtx = sliceCanvas.getContext("2d");
          if (!sliceCtx) break;
          sliceCtx.fillStyle = "#ffffff";
          sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          sliceCtx.drawImage(
            canvas,
            0,
            srcOffset,
            canvas.width,
            srcSliceHeight,
            0,
            0,
            sliceCanvas.width,
            sliceCanvas.height
          );

          const renderWidth = innerWidth * globalScale;
          const renderHeight = (sliceCanvas.height / sliceCanvas.width) * renderWidth;
          ensureSpace(renderHeight);
          pdf.addImage(
            sliceCanvas.toDataURL("image/jpeg", 0.62),
            "JPEG",
            margin,
            y,
            renderWidth,
            renderHeight,
            undefined,
            "FAST"
          );
          y += renderHeight + 2;
          srcOffset += srcSliceHeight;
        }
      }

      const blobUrl = pdf.output("bloburl");
      const pdfWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (pdfWindow) {
        setTimeout(() => {
          try {
            pdfWindow.focus();
            pdfWindow.print();
          } catch (e) {
            // no-op
          }
        }, 700);
      } else {
        pdf.save(generateFilename());
      }
    } catch (error) {
      console.error("Simple segmented print failed, falling back:", error);
      window.print();
    } finally {
      if (scratch && scratch.parentNode) {
        scratch.parentNode.removeChild(scratch);
      }
    }
  };

  const handlePrint = () => {
    printDebugLog("handlePrint_click", { currentMode: printMode });
    if (printMode === "accessible") {
      handleAccessiblePrint();
      return;
    }
    if (printMode === "simple-print") {
      printDebugLog("handlePrint_simple_print");
      printSimpleView();
      return;
    }
    printDebugLog("handlePrint_color_snapshot_print");
    printSimpleView("auto", { useSimpleLayout: false });
  };

  const handleDownloadColorPdf = async () => {
    if (isDownloadingPdf) return;
    const scenarioNode = document.querySelector(".scenario-content-for-pdf");
    if (!scenarioNode) return;

    setIsDownloadingPdf(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const captureWidth = Math.ceil(Math.max(scenarioNode.scrollWidth, scenarioNode.clientWidth, 1100));
      const captureHeight = Math.ceil(Math.max(scenarioNode.scrollHeight, scenarioNode.clientHeight, 1800));

      const canvas = await html2canvas(scenarioNode, {
        scale: 1.15,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: captureWidth,
        height: captureHeight,
        windowWidth: captureWidth,
        windowHeight: captureHeight,
        scrollX: 0,
        scrollY: 0,
      });

      const buildPdf = (jpegQuality) => {
        const pdf = new jsPDF("p", "pt", "letter");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 18;
        const innerWidth = pageWidth - margin * 2;
        const innerHeight = pageHeight - margin * 2;

        let srcOffset = 0;
        let firstPage = true;
        while (srcOffset < canvas.height - 0.5) {
          const remainingHeight = canvas.height - srcOffset;
          const maxSliceHeight = Math.max(
            1,
            Math.floor((innerHeight / innerWidth) * canvas.width)
          );
          const sliceHeight = Math.min(maxSliceHeight, remainingHeight);

          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceHeight;
          const sliceCtx = sliceCanvas.getContext("2d");
          if (!sliceCtx) break;

          sliceCtx.fillStyle = "#ffffff";
          sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          sliceCtx.drawImage(
            canvas,
            0,
            srcOffset,
            canvas.width,
            sliceHeight,
            0,
            0,
            sliceCanvas.width,
            sliceCanvas.height
          );

          if (!firstPage) pdf.addPage();
          firstPage = false;

          const renderHeight = (sliceCanvas.height / sliceCanvas.width) * innerWidth;
          pdf.addImage(
            sliceCanvas.toDataURL("image/jpeg", jpegQuality),
            "JPEG",
            margin,
            margin,
            innerWidth,
            renderHeight,
            undefined,
            "FAST"
          );
          srcOffset += sliceHeight;
        }

        return pdf;
      };

      let pdf = buildPdf(0.68);
      let blob = pdf.output("blob");

      if (blob.size > 5 * 1024 * 1024) {
        pdf = buildPdf(0.54);
        blob = pdf.output("blob");
      }
      if (blob.size > 5 * 1024 * 1024) {
        pdf = buildPdf(0.42);
      }

      pdf.save(generateFilename());
    } catch (error) {
      console.error("Color PDF download failed:", error);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Accessible print: opens a clean popup window with ONLY the text content,
  // zero Chakra wrappers, zero inline styles, zero images. Pure text PDF.
  const handleAccessiblePrint = () => {
    const sourceNode = document.querySelector('.accessible-content');
    if (!sourceNode) {
      // Fallback: at least set accessible class and print
      const origMode = printMode;
      applyBodyModeClasses("accessible", { isHighContrast: false, isLandscape });
      setTimeout(() => {
        window.print();
        setTimeout(() => { applyBodyModeClasses(origMode, { isLandscape, isHighContrast }); }, 500);
      }, 100);
      return;
    }

    // Deep-clone the node so we can mutate it without affecting the live DOM
    const clone = sourceNode.cloneNode(true);

    // Strip ALL inline style attributes recursively — Chakra injects inline
    // styles with dark backgrounds that defeat CSS !important overrides in print
    clone.removeAttribute('style');
    clone.querySelectorAll('[style]').forEach(el => el.removeAttribute('style'));

    // Remove non-text elements: SVGs, images, videos, canvas, buttons
    clone.querySelectorAll('svg, img, video, canvas, button, [role="button"]').forEach(el => el.remove());

    // Remove the controls bar (Back button, Print button) — has class accessible-controls
    clone.querySelectorAll('.accessible-controls, .no-print').forEach(el => el.remove());

    // Remove aria-hidden decorative elements
    clone.querySelectorAll('[aria-hidden="true"]').forEach(el => el.remove());

    // Get clean inner HTML
    const contentHtml = clone.innerHTML;

    // Build a minimal, standalone HTML document — no React, no Chakra, no dark theme
    const scenarioTitle = scenario?.chiefComplaint || metadata?.title || 'Clinical Scenario';
    const borderLabel = escapeHtml(printBorderLabel);
    const copyrightLabel = escapeHtml(PRINT_COPYRIGHT_NOTICE);
    const printHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${scenarioTitle.replace(/</g, '&lt;')}</title>
  <style>
    /* === WCAG AA Accessible Print Stylesheet === */
    *, *::before, *::after {
      box-sizing: border-box;
      background: white !important;
      background-color: white !important;
      background-image: none !important;
      color: #000 !important;
      border-color: #000 !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }
    html, body {
      margin: 0;
      padding: 0;
      background: white !important;
      color: #000 !important;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 12pt;
      line-height: 1.6;
    }
    .print-wrapper {
      max-width: 7in;
      margin: 0 auto;
      padding: 0.5in;
      border: 1px solid #d1d5db;
    }
    .print-border {
      font-size: 8pt;
      line-height: 1.3;
      color: #555 !important;
      background: #fff !important;
    }
    .print-border.top {
      text-transform: uppercase;
      letter-spacing: 0.08em;
      border-bottom: 1px solid #d1d5db;
      margin-bottom: 10pt;
      padding-bottom: 4pt;
    }
    .print-border.bottom {
      border-top: 1px solid #d1d5db;
      margin-top: 10pt;
      padding-top: 4pt;
    }
    h1 { font-size: 20pt; font-weight: bold; margin: 0 0 12pt 0; page-break-after: avoid; }
    h2 { font-size: 15pt; font-weight: bold; margin: 14pt 0 8pt 0; page-break-after: avoid; }
    h3 { font-size: 13pt; font-weight: bold; margin: 10pt 0 6pt 0; page-break-after: avoid; }
    p  { font-size: 12pt; margin: 6pt 0; }
    ul, ol { margin: 6pt 0 6pt 20pt; padding: 0; }
    li { font-size: 12pt; margin: 3pt 0; line-height: 1.5; }
    .accessible-print-stack > div {
      page-break-inside: avoid;
      break-inside: avoid-page;
    }
    strong { font-weight: bold; }
    em { font-style: italic; }
    /* Hide anything that is not plain text */
    svg, img, video, canvas, input, select, textarea, button,
    [role="button"], [role="img"], .accessible-controls, .no-print,
    [aria-hidden="true"] {
      display: none !important;
    }
    @page {
      size: letter;
      margin: 0.75in;
    }
    @media print {
      html, body, .print-wrapper, * {
        background: white !important;
        background-color: white !important;
        color: #000 !important;
      }
      svg, img, video, canvas, input, select, textarea, button {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  <div class="print-wrapper">
    <div class="print-border top">${borderLabel}</div>
    <div>${contentHtml}</div>
    <div class="print-border bottom">${copyrightLabel}</div>
  </div>
  <script>window.addEventListener('load', function() { window.print(); window.addEventListener('afterprint', function() { window.close(); }); });<\/script>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=800,height=600,menubar=yes,toolbar=yes');
    if (!printWindow) {
      // Popup blocked - fall back to window.print() with accessible class
      const origMode = printMode;
      applyBodyModeClasses("accessible", { isHighContrast: false, isLandscape });
      setTimeout(() => {
        window.print();
        setTimeout(() => { applyBodyModeClasses(origMode, { isLandscape, isHighContrast }); }, 500);
      }, 100);
      return;
    }

    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  useEffect(() => {
    console.log('=== SCENARIO DISPLAY COMPONENT DEBUG ===');
    console.log('Received scenario prop:', scenario);
    console.log('Has patientInfo?', !!scenario?.patientInfo);
    console.log('Has sceneSetup?', !!scenario?.sceneSetup);
    console.log('Has interventions?', !!scenario?.interventions);
    console.log('Interventions (camelCase):', scenario?.interventions);
    console.log('Interventions (PascalCase):', scenario?.Interventions);
    console.log('Has learningObjectives?', !!scenario?.learningObjectives);
    console.log('Learning objectives (camelCase):', scenario?.learningObjectives);
    console.log('Learning objectives (PascalCase):', scenario?.EducationalComponents?.LearningObjectives);
    console.log('Has criticalActions?', !!scenario?.criticalActions);
    console.log('Critical actions (camelCase):', scenario?.criticalActions);
    console.log('Critical actions (PascalCase):', scenario?.EducationalComponents?.CriticalActions);
    console.log('Has commonPitfalls?', !!scenario?.commonPitfalls);
    console.log('Has differentialDiagnosis?', !!scenario?.differentialDiagnosis);
    console.log('Has secondaryAssessment (camelCase)?', !!scenario?.secondaryAssessment);
    console.log('Has SecondaryAssessment (PascalCase)?', !!scenario?.AssessmentFindings?.SecondaryAssessment);
    console.log('secondaryAssessment data:', scenario?.secondaryAssessment);
    console.log('SecondaryAssessment data:', scenario?.AssessmentFindings?.SecondaryAssessment);
    console.log('=== END COMPONENT DEBUG ===');
  }, [scenario]);

  useEffect(() => {
    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      setIsFullscreen(isCurrentlyFullscreen);
      
      // Don't auto-exit tablet mode when fullscreen exits
      // User can manually exit with the Exit button
    };
    
    // Listen for orientation changes
    const handleOrientationChange = () => {
      if (orientationMode === 'auto') {
        setIsLandscape(window.innerWidth > window.innerHeight);
      }
      // If locked to landscape or portrait, don't update
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Cleanup on unmount
    return () => {
      if (typeof document !== "undefined" && document.body) {
        BODY_MODE_CLASSES.forEach((cls) => document.body.classList.remove(cls));
      }
      clearSimplePrintLayout();
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (isFullscreen) {
        exitFullscreen();
      }
    };
  }, [clearSimplePrintLayout, isFullscreen, printMode, orientationMode]);

  useEffect(() => {
    applyBodyModeClasses(printMode, { isLandscape, isHighContrast });
  }, [applyBodyModeClasses, printMode, isLandscape, isHighContrast]);

  useEffect(() => {
    if (printMode !== "simple-print") {
      clearSimplePrintLayout();
    }
  }, [clearSimplePrintLayout, printMode]);

  useEffect(() => {
    if (printMode === 'simple-print') {
      // Wait for layout to settle
      setTimeout(checkContentHeight, 100);
    }
  }, [printMode]);

  useEffect(() => {
    if (!dedicatedPrintMode || printMode !== "simple-print") {
      if (typeof document !== "undefined") {
        document.body.removeAttribute("data-dedicated-split");
      }
      return;
    }

    // Force split before Primary/Secondary Assessment row for dedicated print route.
    const timer = setTimeout(() => {
      document.body.setAttribute("data-dedicated-split", "before");
      printDebugLog("dedicated_split_forced", { split: "before" });
    }, 120);
    return () => clearTimeout(timer);
  }, [dedicatedPrintMode, metadata, printDebugLog, printMode, scenario]);

  useEffect(() => {
    if (!printDebugEnabled) return;
    const onGlobalPrintShortcut = (event) => {
      const isPrintShortcut =
        (event.ctrlKey || event.metaKey) && (event.key === "p" || event.key === "P");
      if (!isPrintShortcut) return;
      printDebugLog("global_keydown_print_shortcut", {
        currentMode: printMode,
        key: event.key,
        code: event.code,
        ctrlKey: !!event.ctrlKey,
        metaKey: !!event.metaKey,
        defaultPrevented: !!event.defaultPrevented,
      });
    };
    window.addEventListener("keydown", onGlobalPrintShortcut, true);
    document.addEventListener("keydown", onGlobalPrintShortcut, true);
    return () => {
      window.removeEventListener("keydown", onGlobalPrintShortcut, true);
      document.removeEventListener("keydown", onGlobalPrintShortcut, true);
    };
  }, [printDebugEnabled, printDebugLog, printMode]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const isPrintShortcut =
        (event.ctrlKey || event.metaKey) && (event.key === "p" || event.key === "P");
      if (!isPrintShortcut) return;

      if (printMode === "accessible") {
        // In accessible mode: always intercept Ctrl+P and open the clean text popup
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        handleAccessiblePrint();
        return;
      }

      if (printMode === "simple-print") {
        printDebugLog("keydown_print_shortcut_intercepted", {
          key: event.key,
          code: event.code,
          ctrlKey: !!event.ctrlKey,
          metaKey: !!event.metaKey,
        });
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        printSimpleView();
        return;
      }

      if (!allowPrintControls) return;

      printDebugLog("keydown_print_shortcut_intercepted", {
        key: event.key,
        code: event.code,
        ctrlKey: !!event.ctrlKey,
        metaKey: !!event.metaKey,
      });
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      printSimpleView("auto", { useSimpleLayout: false });
    };

    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [allowPrintControls, handleAccessiblePrint, printSimpleView, printDebugLog, printMode]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.Image !== "function") {
      return;
    }
    const primary = new window.Image();
    primary.onload = () => setCaptureImageSrc(CRYING_JORDAN_IMAGE_URL);
    primary.onerror = () => setCaptureImageSrc(CRYING_JORDAN_FALLBACK_URL);
    primary.src = CRYING_JORDAN_IMAGE_URL;

    const fallback = new window.Image();
    fallback.src = CRYING_JORDAN_FALLBACK_URL;
  }, []);

  useEffect(() => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path.includes("/scenario-share/")) return;

    let lastCaptureHotkeyAt = 0;
    let lastModifierComboAt = 0;
    const keyState = {
      meta: false,
      shift: false,
      ctrl: false,
      alt: false,
    };

    const triggerCaptureBlock = (reason, showMeme = true) => {
      const now = Date.now();
      if (showMeme) {
        lastMemeTriggerAtRef.current = now;
      }
      const keepMeme = showMeme || (now - lastMemeTriggerAtRef.current < 3000);
      setCaptureBlockReason(reason || "Possible screenshot/capture activity detected.");
      setCaptureShowMeme(keepMeme);
      setCaptureBlockVisible(true);
      if (captureBlockTimeoutRef.current) {
        clearTimeout(captureBlockTimeoutRef.current);
      }
      captureBlockTimeoutRef.current = setTimeout(() => {
        setCaptureBlockVisible(false);
        setCaptureBlockReason("");
        setCaptureShowMeme(false);
      }, 12000);
    };

    const handleKeyDown = (e) => {
      const key = String(e.key || "");
      const code = String(e.code || "");
      keyState.meta = !!e.metaKey;
      keyState.shift = !!e.shiftKey;
      keyState.ctrl = !!e.ctrlKey;
      keyState.alt = !!e.altKey;

      const isMacCaptureHotkey =
        e.metaKey &&
        e.shiftKey &&
        (key === "3" || key === "4" || key === "5" || code === "Digit3" || code === "Digit4" || code === "Digit5");
      const isMacAltCaptureHotkey =
        e.metaKey &&
        e.ctrlKey &&
        e.shiftKey &&
        (key === "3" || key === "4" || key === "5" || code === "Digit3" || code === "Digit4" || code === "Digit5");
      const isPrintScreenKey = key === "PrintScreen" || code === "PrintScreen" || key === "F13" || code === "F13";
      const isSystemSnipHotkey = e.shiftKey && (key.toLowerCase() === "s") && (e.metaKey || e.ctrlKey || e.altKey);

      if ((e.metaKey && e.shiftKey) || (e.ctrlKey && e.shiftKey) || (e.altKey && e.shiftKey)) {
        lastModifierComboAt = Date.now();
      }

      if (isMacCaptureHotkey || isMacAltCaptureHotkey || isPrintScreenKey || isSystemSnipHotkey) {
        lastCaptureHotkeyAt = Date.now();
        triggerCaptureBlock("Possible screenshot hotkey detected.", true);
      }
    };

    const handleKeyUp = (e) => {
      keyState.meta = !!e.metaKey;
      keyState.shift = !!e.shiftKey;
      keyState.ctrl = !!e.ctrlKey;
      keyState.alt = !!e.altKey;
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      triggerCaptureBlock("Context menu capture path blocked.", true);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) return;
      if (Date.now() < suppressFocusSignalsUntilRef.current) return;
      if (Date.now() - lastCaptureHotkeyAt < 2500) {
        triggerCaptureBlock("Visibility changed during capture hotkey.", true);
      } else if (
        Date.now() - lastModifierComboAt < 1800 ||
        (keyState.shift && (keyState.meta || keyState.ctrl || keyState.alt))
      ) {
        triggerCaptureBlock("Possible capture signal detected before visibility change.", true);
      }
    };

    const handleWindowBlur = () => {
      if (Date.now() < suppressFocusSignalsUntilRef.current) return;
      if (Date.now() - lastCaptureHotkeyAt < 2500) {
        triggerCaptureBlock("Window blur during capture hotkey.", true);
      } else if (
        Date.now() - lastModifierComboAt < 1800 ||
        (keyState.shift && (keyState.meta || keyState.ctrl || keyState.alt))
      ) {
        triggerCaptureBlock("Possible capture signal detected before window blur.", true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      if (captureBlockTimeoutRef.current) {
        clearTimeout(captureBlockTimeoutRef.current);
      }
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [printMode]);

  const renderOvertWatermark = () => {
    if (!watermarkConfig || !watermarkConfig.watermarkingEnabled) return null;

    const { overtWatermarkText, overtWatermarkPosition, overtWatermarkColor, overtWatermarkOpacity } = watermarkConfig;
    const institutionName = watermarkConfig.institution || user?.institution || 'Educational Use Only';
    const timestamp = new Date().toISOString();
    const displayText = overtWatermarkText || `${institutionName} • ${new Date().getFullYear()} • Educational Use Only`;
    const color = overtWatermarkColor || '#000000';
    const opacity = overtWatermarkOpacity || 0.15;

    const baseStyle = {
      pointerEvents: 'none',
      userSelect: 'none',
      color: color,
      opacity: opacity,
      fontWeight: 'bold',
      zIndex: 9998,
    };

    switch (overtWatermarkPosition) {
      case 'diagonal':
        return (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            overflow="hidden"
            {...baseStyle}
          >
            {[...Array(20)].map((_, i) => (
              <Text
                key={i}
                position="absolute"
                fontSize="48px"
                transform={`rotate(-45deg) translate(${i * 200}px, ${i * 150}px)`}
                whiteSpace="nowrap"
              >
                {watermarkText}
              </Text>
            ))}
          </Box>
        );
      
      case 'top':
        return (
          <Box
            position="absolute"
            top="20px"
            left="50%"
            transform="translateX(-50%)"
            {...baseStyle}
          >
            <Text fontSize="32px" textAlign="center">
              {watermarkText}
            </Text>
          </Box>
        );
      
      case 'bottom':
        return (
          <Box
            position="absolute"
            bottom="20px"
            left="50%"
            transform="translateX(-50%)"
            {...baseStyle}
          >
            <Text fontSize="32px" textAlign="center">
              {watermarkText}
            </Text>
          </Box>
        );
      
      case 'corners':
        return (
          <>
            <Box position="absolute" top="10px" left="10px" {...baseStyle}>
              <Text fontSize="24px">{watermarkText}</Text>
            </Box>
            <Box position="absolute" top="10px" right="10px" {...baseStyle}>
              <Text fontSize="24px">{watermarkText}</Text>
            </Box>
            <Box position="absolute" bottom="10px" left="10px" {...baseStyle}>
              <Text fontSize="24px">{watermarkText}</Text>
            </Box>
            <Box position="absolute" bottom="10px" right="10px" {...baseStyle}>
              <Text fontSize="24px">{watermarkText}</Text>
            </Box>
          </>
        );
      
      default:
        return null;
    }
  };

  const renderSubtleWatermark = () => {
    if (!watermarkConfig || !watermarkConfig.watermarkingEnabled || !watermarkConfig.subtleWatermarkEnabled) return null;

    const watermarkData = {
      scenario: scenario?.id,
      viewer: user?.id,
      institution: watermarkConfig.institution || user?.institution,
      timestamp: new Date().toISOString(),
      pattern: watermarkConfig.subtleWatermarkPattern || 'default-pattern',
    };

    return (
      <Box
        position="absolute"
        opacity={0.001}
        fontSize="1px"
        top={0}
        left={0}
        pointerEvents="none"
        userSelect="none"
        data-watermark-scenario={scenario?.id}
        data-watermark-viewer={user?.id}
        data-watermark-institution={user?.institution}
        data-watermark-timestamp={watermarkData.timestamp}
        data-watermark-pattern={watermarkConfig.subtleWatermarkPattern}
        className="subtle-watermark"
      >
        {JSON.stringify(watermarkData)}
      </Box>
    );
  };

  const generateTextOnlyPDF = () => {
    let text = '';
    
    // Helper to add section
    const addSection = (title) => {
      text += `\n\n${title.toUpperCase()}\n`;
      text += '-'.repeat(title.length) + '\n';
    };
    
    // Helper to check if value exists and is not N/A or Unknown
    const isValid = (value) => {
      if (!value) return false;
      if (typeof value === 'string') {
        const normalized = value.toLowerCase().trim();
        return normalized !== 'n/a' && normalized !== 'unknown' && normalized !== '';
      }
      return true;
    };
    
    // Helper to format vital
    const formatVitalText = (vital) => {
      if (!vital) return '';
      if (typeof vital === 'object') {
        return vital.value ? `${vital.value} ${vital.unit || ''}`.trim() : '';
      }
      return vital.toString();
    };
    
    // Header Block
    text += 'CLINICAL SCENARIO\n';
    text += '='.repeat(80) + '\n';
    if (metadata?.levelOfCare && isValid(metadata.levelOfCare)) {
      text += `Training Level: ${metadata.levelOfCare.replace(/_/g, ' ')}\n`;
    }
    if (patientInfo.age && isValid(patientInfo.age)) {
      text += `Patient Age: ${patientInfo.age}\n`;
    }
    if (scenario.chiefComplaint && isValid(scenario.chiefComplaint)) {
      text += `Chief Complaint: ${scenario.chiefComplaint}\n`;
    }
    if (metadata?.mechanismNatureOfInjury && isValid(metadata.mechanismNatureOfInjury)) {
      text += `Mechanism/Nature of Injury: ${metadata.mechanismNatureOfInjury}\n`;
    }
    text += '='.repeat(80) + '\n';
    text += '\n\n';
    
    // Metadata
    if (metadata?.levelOfCare || scenario.difficulty || metadata?.mechanismNatureOfInjury) {
      addSection('Scenario Metadata');
      if (metadata?.levelOfCare) {
        text += `  Training Level: ${metadata.levelOfCare.replace(/_/g, ' ')}\n`;
      }
      if (scenario.difficulty) {
        text += `  Difficulty: ${scenario.difficulty} out of 5\n`;
      }
      if (metadata?.mechanismNatureOfInjury) {
        text += `  Mechanism/Nature of Injury: ${metadata.mechanismNatureOfInjury}\n`;
      }
    }
    
    // Background and Dispatch
    addSection('Background and Dispatch Information');
    if (sceneSetup.timeOfDay && isValid(sceneSetup.timeOfDay)) {
      text += `  Time of Day: ${sceneSetup.timeOfDay}\n`;
    }
    if (sceneSetup.location && isValid(sceneSetup.location)) {
      text += `  Location: ${sceneSetup.location}\n`;
    }
    if (sceneSetup.weather && isValid(sceneSetup.weather)) {
      text += `  Weather: ${sceneSetup.weather}\n`;
    }
    if (sceneSetup.mechanism && isValid(sceneSetup.mechanism)) {
      text += `  Mechanism/Nature: ${sceneSetup.mechanism}\n`;
    }
    if (sceneSetup.dispatchInfo && isValid(sceneSetup.dispatchInfo)) {
      text += `  Dispatch Information: ${sceneSetup.dispatchInfo}\n`;
    }
    if ((sceneSetup.safetyConsiderations || sceneSetup.safetyIssues) && isValid(sceneSetup.safetyConsiderations || sceneSetup.safetyIssues)) {
      text += `  SAFETY ALERT: ${sceneSetup.safetyConsiderations || sceneSetup.safetyIssues}\n`;
    }
    if (sceneSetup.bystanders && isValid(sceneSetup.bystanders)) {
      text += `  Bystanders/Scene: ${sceneSetup.bystanders}\n`;
    }
    
    // Patient Information
    addSection('Patient Information');
    if (patientInfo.name && isValid(patientInfo.name)) {
      text += `  Name: ${patientInfo.name}\n`;
    }
    if (patientInfo.age && isValid(patientInfo.age)) {
      text += `  Age: ${patientInfo.age}\n`;
    }
    if (patientInfo.sex && isValid(patientInfo.sex)) {
      text += `  Sex: ${patientInfo.sex}\n`;
    }
    if (patientInfo.weight && isValid(patientInfo.weight)) {
      text += `  Weight: ${patientInfo.weight}\n`;
    }
    if (patientInfo.physicalAppearance && isValid(patientInfo.physicalAppearance)) {
      text += `  Physical Appearance: ${patientInfo.physicalAppearance}\n`;
    }
    
    // Chief Complaint and History
    addSection('Chief Complaint and History');
    if (scenario.chiefComplaint && isValid(scenario.chiefComplaint)) {
      text += `  Chief Complaint: ${scenario.chiefComplaint}\n`;
    }
    if ((history.hpi || scenario.enhancedHpi) && (isValid(history.hpi) || isValid(scenario.enhancedHpi))) {
      text += `  History of Present Illness: ${scenario.enhancedHpi || history.hpi}\n`;
    }
    if (history.allergies && isValid(history.allergies)) {
      text += `  ALLERGIES: ${history.allergies}\n`;
    }
    if (history.medications && isValid(history.medications)) {
      text += `  Medications: ${history.medications}\n`;
    }
    if (history.pmhx && isValid(history.pmhx)) {
      text += `  Past Medical History: ${history.pmhx}\n`;
    }
    if (history.lastMeal && isValid(history.lastMeal)) {
      text += `  Last Oral Intake: ${history.lastMeal}\n`;
    }
    if (history.events && isValid(history.events)) {
      text += `  Events Leading to Incident: ${history.events}\n`;
    }
    
    // Primary Assessment
    addSection('Primary Assessment');
    if (primaryAssessment.mentalStatus && isValid(primaryAssessment.mentalStatus)) {
      text += `  Level of Consciousness: ${primaryAssessment.mentalStatus}\n`;
    }
    if (primaryAssessment.airway && isValid(primaryAssessment.airway)) {
      text += `  Airway: ${primaryAssessment.airway}\n`;
    }
    if (primaryAssessment.breathing && isValid(primaryAssessment.breathing)) {
      text += `  Breathing: ${primaryAssessment.breathing}\n`;
    }
    if (primaryAssessment.circulation && isValid(primaryAssessment.circulation)) {
      text += `  Circulation: ${primaryAssessment.circulation}\n`;
    }
    if (primaryAssessment.skinColor || primaryAssessment.skinTemp || primaryAssessment.skinMoisture) {
      const skinParts = [
        primaryAssessment.skinColor && isValid(primaryAssessment.skinColor) ? primaryAssessment.skinColor : null,
        primaryAssessment.skinTemp && isValid(primaryAssessment.skinTemp) ? primaryAssessment.skinTemp : null,
        primaryAssessment.skinMoisture && isValid(primaryAssessment.skinMoisture) ? primaryAssessment.skinMoisture : null
      ].filter(Boolean);
      if (skinParts.length > 0) {
        text += `  Skin: ${skinParts.join(', ')}\n`;
      }
    }
    
    // Initial Vital Signs
    if (vitals.initial) {
      text += `\n  Initial Vital Signs:\n`;
      if (vitals.initial.hr && formatVitalText(vitals.initial.hr)) {
        text += `    - Heart Rate: ${formatVitalText(vitals.initial.hr)}\n`;
      }
      if (vitals.initial.bp && formatVitalText(vitals.initial.bp)) {
        text += `    - Blood Pressure: ${formatVitalText(vitals.initial.bp)}\n`;
      }
      if (vitals.initial.rr && formatVitalText(vitals.initial.rr)) {
        text += `    - Respiratory Rate: ${formatVitalText(vitals.initial.rr)}\n`;
      }
      if (vitals.initial.spo2 && formatVitalText(vitals.initial.spo2)) {
        text += `    - SpO2: ${formatVitalText(vitals.initial.spo2)}\n`;
      }
      if (vitals.initial.temp && formatVitalText(vitals.initial.temp)) {
        text += `    - Temperature: ${formatVitalText(vitals.initial.temp)}\n`;
      }
      if (vitals.initial.bloodGlucose && formatVitalText(vitals.initial.bloodGlucose)) {
        text += `    - Blood Glucose: ${formatVitalText(vitals.initial.bloodGlucose)}\n`;
      }
      if (vitals.initial.rhythm && formatVitalText(vitals.initial.rhythm)) {
        text += `    - Cardiac Rhythm: ${formatVitalText(vitals.initial.rhythm)}\n`;
      }
      if (vitals.initial.etco2 && formatVitalText(vitals.initial.etco2)) {
        text += `    - End-Tidal CO2: ${formatVitalText(vitals.initial.etco2)}\n`;
      }
      if (vitals.initial.pupils && isValid(vitals.initial.pupils)) {
        text += `    - Pupils: ${vitals.initial.pupils}\n`;
      }
      if (vitals.initial.gcs && isValid(vitals.initial.gcs)) {
        text += `    - Glasgow Coma Scale: ${vitals.initial.gcs}\n`;
      }
    }
    
    // Secondary Assessment
    if (secondaryAssessment && Object.keys(secondaryAssessment).some(key => isValid(secondaryAssessment[key]))) {
      addSection('Secondary Assessment (Physical Exam)');
      if (secondaryAssessment.heent && isValid(secondaryAssessment.heent)) {
        text += `  HEENT: ${secondaryAssessment.heent}\n`;
      }
      if (secondaryAssessment.neck && isValid(secondaryAssessment.neck)) {
        text += `  Neck: ${secondaryAssessment.neck}\n`;
      }
      if (secondaryAssessment.chest && isValid(secondaryAssessment.chest)) {
        text += `  Chest: ${secondaryAssessment.chest}\n`;
      }
      if (secondaryAssessment.respiratory && isValid(secondaryAssessment.respiratory)) {
        text += `  Respiratory: ${secondaryAssessment.respiratory}\n`;
      }
      if (secondaryAssessment.cardiovascular && isValid(secondaryAssessment.cardiovascular)) {
        text += `  Cardiovascular: ${secondaryAssessment.cardiovascular}\n`;
      }
      if (secondaryAssessment.abdomen && isValid(secondaryAssessment.abdomen)) {
        text += `  Abdomen: ${secondaryAssessment.abdomen}\n`;
      }
      if (secondaryAssessment.pelvis && isValid(secondaryAssessment.pelvis)) {
        text += `  Pelvis: ${secondaryAssessment.pelvis}\n`;
      }
      if (secondaryAssessment.extremities && isValid(secondaryAssessment.extremities)) {
        text += `  Extremities: ${secondaryAssessment.extremities}\n`;
      }
      if (secondaryAssessment.back && isValid(secondaryAssessment.back)) {
        text += `  Back: ${secondaryAssessment.back}\n`;
      }
      if (secondaryAssessment.neuro && isValid(secondaryAssessment.neuro)) {
        text += `  Neurological: ${secondaryAssessment.neuro}\n`;
      }
      if (secondaryAssessment.skin && isValid(secondaryAssessment.skin)) {
        text += `  Skin: ${secondaryAssessment.skin}\n`;
      }
    }
    
    // Trending Vital Signs
    if (vitals.trending && vitals.trending.length > 0) {
      addSection('Trending Vital Signs');
      vitals.trending.forEach((vitalSet, index) => {
        const time = vitalSet.time && isValid(vitalSet.time) ? vitalSet.time : `Time ${index + 1}`;
        text += `  ${time}:\n`;
        if (vitalSet.hr && formatVitalText(vitalSet.hr)) {
          text += `    - HR: ${formatVitalText(vitalSet.hr)}\n`;
        }
        if (vitalSet.bp && formatVitalText(vitalSet.bp)) {
          text += `    - BP: ${formatVitalText(vitalSet.bp)}\n`;
        }
        if (vitalSet.rr && formatVitalText(vitalSet.rr)) {
          text += `    - RR: ${formatVitalText(vitalSet.rr)}\n`;
        }
        if (vitalSet.spo2 && formatVitalText(vitalSet.spo2)) {
          text += `    - SpO2: ${formatVitalText(vitalSet.spo2)}\n`;
        }
        if (vitalSet.temp && formatVitalText(vitalSet.temp)) {
          text += `    - Temp: ${formatVitalText(vitalSet.temp)}\n`;
        }
        if (vitalSet.bloodGlucose && formatVitalText(vitalSet.bloodGlucose)) {
          text += `    - BGL: ${formatVitalText(vitalSet.bloodGlucose)}\n`;
        }
        if (vitalSet.etco2 && formatVitalText(vitalSet.etco2)) {
          text += `    - EtCO2: ${formatVitalText(vitalSet.etco2)}\n`;
        }
        if (vitalSet.gcs && isValid(vitalSet.gcs)) {
          text += `    - GCS: ${vitalSet.gcs}\n`;
        }
      });
    }
    
    // Interventions
    if (interventions && (interventions.LifeSaving?.length > 0 || interventions.Secondary?.length > 0 || interventions.Optional?.length > 0)) {
      addSection('Interventions');
      
      if (interventions.LifeSaving && interventions.LifeSaving.length > 0) {
        text += `\n  Critical/Life-Saving Interventions:\n`;
        interventions.LifeSaving.forEach(intervention => {
          text += `    - ${intervention}\n`;
        });
      }
      
      if (interventions.Secondary && interventions.Secondary.length > 0) {
        text += `\n  Secondary Interventions:\n`;
        interventions.Secondary.forEach(intervention => {
          text += `    - ${intervention}\n`;
        });
      }
      
      if (interventions.Optional && interventions.Optional.length > 0) {
        text += `\n  Optional/Conditional Interventions:\n`;
        interventions.Optional.forEach(intervention => {
          text += `    - ${intervention}\n`;
        });
      }
    }
    
    // Critical Actions
    if (learning.criticalActions && learning.criticalActions.length > 0) {
      addSection('Critical Actions');
      learning.criticalActions.forEach(action => {
        text += `  - ${action}\n`;
      });
    }
    
    // Common Pitfalls
    if (learning.commonPitfalls && learning.commonPitfalls.length > 0) {
      addSection('Common Pitfalls');
      learning.commonPitfalls.forEach(pitfall => {
        text += `  - ${pitfall}\n`;
      });
    }
    
    // Differential Diagnosis
    if (learning.differentialDiagnosis && learning.differentialDiagnosis.length > 0) {
      addSection('Differential Diagnosis');
      learning.differentialDiagnosis.forEach(diagnosis => {
        text += `  - ${diagnosis}\n`;
      });
    }
    
    // Expected Outcome
    if (outcome.finalCondition || outcome.disposition || outcome.continuingCareNeeds) {
      addSection('Expected Outcome');
      if (outcome.finalCondition && isValid(outcome.finalCondition)) {
        text += `  Final Condition: ${outcome.finalCondition}\n`;
      }
      if (outcome.disposition && isValid(outcome.disposition)) {
        text += `  Disposition: ${outcome.disposition}\n`;
      }
      if (outcome.continuingCareNeeds && isValid(outcome.continuingCareNeeds)) {
        text += `  Continuing Care: ${outcome.continuingCareNeeds}\n`;
      }
    }
    
    return text;
  };


  if (!scenario) {
    return (
      <Box p={4}>
        <Text>No scenario data available</Text>
      </Box>
    );
  }

  const isSimplePrint = printMode === "simple-print";

  // Color mode values
  const cardBg = isSimplePrint ? "white" : useColorModeValue('white', 'gray.800');
  const cardTextColor = isSimplePrint ? "black" : useColorModeValue('gray.900', 'white');
  const boxBg = "transparent";
  const boxBorderColor = isSimplePrint ? "black" : useColorModeValue('gray.200', 'gray.500');
  const labelColor = isSimplePrint ? "black" : useColorModeValue('gray.600', 'gray.400');
  const mutedTextColor = isSimplePrint ? "black" : useColorModeValue('gray.500', 'gray.400');
  const textColor = isSimplePrint ? "black" : useColorModeValue('gray.800', 'gray.200');
  const boldTextColor = isSimplePrint ? "black" : useColorModeValue('gray.700', 'gray.300');
  const defaultElementHeaderColor = "white";
  const defaultElementHeaderBg = useColorModeValue('gray.600', 'gray.700');
  const elementHeaderColor = isSimplePrint ? "black" : defaultElementHeaderColor;
  const elementHeaderBg = isSimplePrint ? "transparent" : defaultElementHeaderBg;
  const alertBgInfo = isSimplePrint ? "white" : useColorModeValue('blue.50', 'blue.900');
  const alertBgWarning = isSimplePrint ? "white" : useColorModeValue('orange.50', 'orange.900');
  const alertBgError = isSimplePrint ? "white" : useColorModeValue('red.50', 'red.900');
  const defaultStatsBoxBg = useColorModeValue('gray.900', 'gray.900');
  const statsBoxBg = isSimplePrint ? "transparent" : defaultStatsBoxBg;
  const vitalLabelColor = isSimplePrint ? "black" : useColorModeValue('gray.700', 'gray.200');
  const vitalNeutralColor = isSimplePrint ? "black" : useColorModeValue('gray.800', 'gray.100');
  const vitalCriticalColor = isSimplePrint ? "black" : useColorModeValue('red.700', 'red.200');
  const vitalWarningColor = isSimplePrint ? "black" : useColorModeValue('orange.700', 'orange.200');
  const monitorVitalColors = {
    hr: isSimplePrint ? "#000000" : "#48BB78",
    bp: isSimplePrint ? "#000000" : "#FFFFFF",
    rr: isSimplePrint ? "#000000" : "#F687B3",
    spo2: isSimplePrint ? "#000000" : "#63B3ED",
    etco2: isSimplePrint ? "#000000" : "#ED8936",
    temp: isSimplePrint ? "#000000" : "#B794F4"
  };
  const initialVitalTextColor = (status) => {
    if (status === 'critical') return vitalCriticalColor;
    if (status === 'warning') return vitalWarningColor;
    return vitalNeutralColor;
  };
  const interventionCardBg = isSimplePrint ? "white" : useColorModeValue('blue.50', 'blue.900');
  const trendingCardBg = isSimplePrint ? "white" : useColorModeValue('gray.50', 'gray.700');

  const patientInfo = {
    name: scenario.PatientDemographics?.FullName || scenario.patientInfo?.name,
    age: scenario.PatientDemographics?.Age || scenario.patientInfo?.age,
    weight: scenario.PatientDemographics?.Weight || scenario.patientInfo?.weight,
    sex: scenario.PatientDemographics?.Sex || scenario.patientInfo?.sex,
    ethnicity: scenario.PatientDemographics?.Ethnicity || scenario.patientInfo?.ethnicity,
    physicalAppearance: scenario.PatientDemographics?.PhysicalAppearance || scenario.patientInfo?.appearance || scenario.patientInfo?.physicalAppearance,
    moulage: scenario.PatientDemographics?.Moulage || scenario.patientInfo?.moulage || scenario.sceneSetup?.moulage || scenario.SceneSetup?.Moulage
  };

  const sceneSetup = {
    location: scenario.SceneSetup?.Location || scenario.sceneSetup?.location,
    timeOfDay: scenario.SceneSetup?.TimeOfDay || scenario.sceneSetup?.timeOfDay,
    weather: scenario.SceneSetup?.Weather || scenario.sceneSetup?.weather,
    safetyIssues: scenario.SceneSetup?.SafetyIssues || scenario.sceneSetup?.safetyIssues,
    safetyConsiderations: scenario.SceneSetup?.SafetyIssues || scenario.sceneSetup?.safetyConsiderations,
    dispatchInfo: scenario.SceneSetup?.DispatchInformation || scenario.sceneSetup?.dispatchInfo,
    bystanders: scenario.SceneSetup?.Bystanders || scenario.sceneSetup?.bystanders,
    mechanism: scenario.sceneSetup?.mechanism,
    priorToArrivalCare: scenario.SceneSetup?.PriorToArrivalCare || scenario.sceneSetup?.priorToArrivalCare || 'None',
    transportContext: scenario.SceneSetup?.TransportContext || scenario.sceneSetup?.transportContext,
    flightOperations: scenario.SceneSetup?.FlightOperations || scenario.sceneSetup?.flightOperations
  };

  const primaryAssessment = {
    mentalStatus: scenario.AssessmentFindings?.PrimaryAssessment?.MentalStatus || scenario.VitalSigns?.Initial?.GCS || scenario.primaryAssessment?.mentalStatus,
    airway: scenario.AssessmentFindings?.PrimaryAssessment?.Airway || scenario.primaryAssessment?.airway,
    breathing: scenario.AssessmentFindings?.PrimaryAssessment?.Breathing || scenario.primaryAssessment?.breathing,
    circulation: scenario.AssessmentFindings?.PrimaryAssessment?.Circulation || scenario.primaryAssessment?.circulation,
    skinColor: scenario.AssessmentFindings?.PrimaryAssessment?.Skin?.split(',')[0] || scenario.primaryAssessment?.skinColor,
    skinTemp: scenario.AssessmentFindings?.PrimaryAssessment?.Skin?.includes('cool') ? 'Cool' : scenario.primaryAssessment?.skinTemp,
    skinMoisture: scenario.AssessmentFindings?.PrimaryAssessment?.Skin?.includes('clammy') ? 'Clammy' : scenario.primaryAssessment?.skinMoisture
  };

  const secondaryAssessment = {
    heent: scenario.AssessmentFindings?.SecondaryAssessment?.HEENT || scenario.secondaryAssessment?.heent || scenario.secondaryAssessment?.HEENT,
    neck: scenario.AssessmentFindings?.SecondaryAssessment?.NeckBack || scenario.secondaryAssessment?.neck || scenario.secondaryAssessment?.NeckBack,
    chest: scenario.AssessmentFindings?.SecondaryAssessment?.Chest || scenario.secondaryAssessment?.chest,
    respiratory: scenario.AssessmentFindings?.SecondaryAssessment?.Respiratory || scenario.secondaryAssessment?.respiratory,
    cardiovascular: scenario.AssessmentFindings?.SecondaryAssessment?.Cardiovascular || scenario.secondaryAssessment?.cardiovascular,
    abdomen: scenario.AssessmentFindings?.SecondaryAssessment?.GI || scenario.secondaryAssessment?.abdomen || scenario.secondaryAssessment?.GI,
    pelvis: scenario.AssessmentFindings?.SecondaryAssessment?.Pelvis || scenario.secondaryAssessment?.pelvis,
    extremities: scenario.AssessmentFindings?.SecondaryAssessment?.Musculoskeletal || scenario.secondaryAssessment?.extremities || scenario.secondaryAssessment?.Musculoskeletal,
    back: scenario.AssessmentFindings?.SecondaryAssessment?.Back || scenario.secondaryAssessment?.back,
    neuro: scenario.AssessmentFindings?.SecondaryAssessment?.NervousSystem || scenario.secondaryAssessment?.neuro || scenario.secondaryAssessment?.NervousSystem,
    skin: scenario.AssessmentFindings?.SecondaryAssessment?.Integumentary || scenario.secondaryAssessment?.skin || scenario.secondaryAssessment?.Integumentary
  };

  const vitals = {
    initial: scenario.VitalSigns?.Initial || scenario.vitalSigns?.initial,
    trending: scenario.VitalSigns?.Trending || scenario.vitalSigns?.trending
  };

  // Convert vital sign format if needed
  if (vitals.initial) {
    vitals.initial = {
      hr: vitals.initial.HR || vitals.initial.hr,
      bp: vitals.initial.BP || vitals.initial.bp,
      rr: vitals.initial.RR || vitals.initial.rr,
      spo2: vitals.initial.SpO2 || vitals.initial.spo2,
      etco2: vitals.initial.ETCO2 || vitals.initial.etco2,
      temp: vitals.initial.Temp || vitals.initial.temp,
      bloodGlucose: vitals.initial.BGL || vitals.initial.bloodGlucose,
      rhythm: vitals.initial.Rhythm || vitals.initial.rhythm,
      '12Lead': vitals.initial['12Lead'] || vitals.initial['12-Lead'] || vitals.initial.TwelveLeadECG || vitals.initial.twelveLeadECG,
      pupils: vitals.initial.Pupils || vitals.initial.pupils,
      gcs: vitals.initial.GCS || vitals.initial.gcs
    };
  }

  if (vitals.trending && vitals.trending.length > 0) {
    vitals.trending = vitals.trending.map(v => ({
      time: v.Time || v.time,
      hr: v.HR || v.hr,
      bp: v.BP || v.bp,
      rr: v.RR || v.rr,
      spo2: v.SpO2 || v.spo2,
      etco2: v.ETCO2 || v.etco2,
      temp: v.Temp || v.temp,
      bloodGlucose: v.BGL || v.bloodGlucose,
      gcs: v.GCS || v.gcs
    }));
  }

  const initialVitalChecklistKeys = [
    vitals.initial?.hr && formatVital(vitals.initial.hr) ? "initial_hr" : null,
    vitals.initial?.bp && formatVital(vitals.initial.bp) ? "initial_bp" : null,
    vitals.initial?.rr && formatVital(vitals.initial.rr) ? "initial_rr" : null,
    vitals.initial?.etco2 && formatVital(vitals.initial.etco2) ? "initial_etco2" : null,
    vitals.initial?.spo2 && formatVital(vitals.initial.spo2) ? "initial_spo2" : null,
    vitals.initial?.temp && formatVital(vitals.initial.temp) ? "initial_temp" : null,
    vitals.initial?.bloodGlucose && formatVital(vitals.initial.bloodGlucose) ? "initial_bgl" : null,
    vitals.initial?.rhythm && formatVital(vitals.initial.rhythm) ? "initial_rhythm" : null,
    vitals.initial?.pupils && hasValue(vitals.initial.pupils) ? "initial_pupils" : null,
    vitals.initial?.['12Lead'] && formatVital(vitals.initial['12Lead']) ? "initial_12lead" : null
  ].filter(Boolean);

  const initialVitalTileCount = [
    vitals.initial?.hr && formatVital(vitals.initial.hr),
    vitals.initial?.bp && formatVital(vitals.initial.bp),
    vitals.initial?.rr && formatVital(vitals.initial.rr),
    vitals.initial?.etco2 && formatVital(vitals.initial.etco2),
    vitals.initial?.spo2 && formatVital(vitals.initial.spo2),
    vitals.initial?.temp && formatVital(vitals.initial.temp),
    vitals.initial?.bloodGlucose && formatVital(vitals.initial.bloodGlucose),
    vitals.initial?.rhythm && formatVital(vitals.initial.rhythm),
    vitals.initial?.pupils && hasValue(vitals.initial.pupils)
  ].filter(Boolean).length;

  const areAllInitialVitalsChecked =
    initialVitalChecklistKeys.length > 0 &&
    initialVitalChecklistKeys.every((key) => isChecked("vitals", key));

  const toggleAllInitialVitals = (checked) => {
    const nextData = { ...(evaluationData || {}) };
    nextData.vitals = { ...(nextData.vitals || {}) };
    initialVitalChecklistKeys.forEach((key) => {
      nextData.vitals[key] = checked;
    });
    onEvaluationChange(nextData);
  };

  const normalizeChecklistText = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "object") {
      return String(
        value.label ||
        value.name ||
        value.action ||
        value.title ||
        value.text ||
        value.value ||
        value.description ||
        ""
      ).trim();
    }
    return String(value).trim();
  };

  const makeChecklistKey = (prefix, value, index) => {
    const normalized = normalizeChecklistText(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return `${prefix}_${normalized || index}`;
  };

  // Handle both new categorized format and legacy array format
  let interventions = scenario.Interventions || scenario.interventions;
  if (Array.isArray(interventions)) {
    // Legacy format - convert to categorized with defensive copy
    interventions = {
      LifeSaving: [],
      Secondary: Array.from(interventions.map((i) => normalizeChecklistText(i)).filter(Boolean)),
      Optional: []
    };
  } else if (!interventions || typeof interventions !== 'object') {
    interventions = { LifeSaving: [], Secondary: [], Optional: [] };
  } else {
    const groupedInterventions = {
      LifeSaving: interventions.LifeSaving || interventions.lifeSaving || [],
      Secondary: interventions.Secondary || interventions.secondary || [],
      Optional: interventions.Optional || interventions.optional || []
    };

    // Support flattened intervention lists found in some scenario outputs.
    if (groupedInterventions.LifeSaving.length === 0 && scenario.criticalInterventions) {
      groupedInterventions.LifeSaving = scenario.criticalInterventions;
    }
    if (groupedInterventions.Secondary.length === 0 && scenario.secondaryInterventions) {
      groupedInterventions.Secondary = scenario.secondaryInterventions;
    }
    if (groupedInterventions.Optional.length === 0 && scenario.optionalInterventions) {
      groupedInterventions.Optional = scenario.optionalInterventions;
    }

    // Create mutable copies of all intervention arrays to prevent read-only errors
    interventions = {
      LifeSaving: Array.from(groupedInterventions.LifeSaving || []).map((i) => normalizeChecklistText(i)).filter(Boolean),
      Secondary: Array.from(groupedInterventions.Secondary || []).map((i) => normalizeChecklistText(i)).filter(Boolean),
      Optional: Array.from(groupedInterventions.Optional || []).map((i) => normalizeChecklistText(i)).filter(Boolean)
    };
  }
  const fallbackScopedLevel = "EMT";
  const normalizeScopedItems = (items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((item) => {
        if (typeof item === "string") {
          const text = normalizeChecklistText(item);
          return text ? { text, minLevel: fallbackScopedLevel } : null;
        }
        if (!item || typeof item !== "object") return null;
        const text = normalizeChecklistText(item.text || item.label || item.name || item.action || item.title || item.value || item.description);
        if (!text) return null;
        return {
          text,
          minLevel: normalizeProviderLevel(
            item.minLevel || item.level || item.trainingLevel || item.scopeLevel || item.scope,
            fallbackScopedLevel
          )
        };
      })
      .filter(Boolean);
  };

  const scopedInterventionsSource = scenario.levelScopedInterventions || scenario.LevelScopedInterventions || scenario.level_scoped_interventions || {};
  const levelScopedInterventions = {
    LifeSaving: normalizeScopedItems(scopedInterventionsSource.LifeSaving || scopedInterventionsSource.lifeSaving),
    Secondary: normalizeScopedItems(scopedInterventionsSource.Secondary || scopedInterventionsSource.secondary),
    Optional: normalizeScopedItems(scopedInterventionsSource.Optional || scopedInterventionsSource.optional)
  };

  if (levelScopedInterventions.LifeSaving.length === 0) {
    levelScopedInterventions.LifeSaving = interventions.LifeSaving.map((text) => ({ text, minLevel: fallbackScopedLevel }));
  }
  if (levelScopedInterventions.Secondary.length === 0) {
    levelScopedInterventions.Secondary = interventions.Secondary.map((text) => ({ text, minLevel: fallbackScopedLevel }));
  }
  if (levelScopedInterventions.Optional.length === 0) {
    levelScopedInterventions.Optional = interventions.Optional.map((text) => ({ text, minLevel: fallbackScopedLevel }));
  }

  const deteriorationVitals = scenario.DeteriorationVitals || scenario.deteriorationVitals;
  const failedManagement = scenario.FailedManagementDescription || scenario.failedManagementDescription;

  const history = {
    hpi: scenario.History?.HPI ? `${scenario.History.HPI.Onset || ''} ${scenario.History.HPI.Provocation || ''} ${scenario.History.HPI.Quality || ''} ${scenario.History.HPI.Severity || ''} ${scenario.History.HPI.Time || ''}`.trim() : scenario.history?.hpi,
    events: scenario.History?.Events || scenario.history?.events,
    pmhx: scenario.History?.PMHx || scenario.history?.pmhx,
    medications: scenario.History?.Medications || scenario.history?.medications,
    allergies: scenario.History?.Allergies || scenario.history?.allergies,
    lastMeal: scenario.History?.LastMeal || scenario.history?.lastMeal
  };

  let learning = {
    learningObjectives: Array.from(scenario.EducationalComponents?.LearningObjectives || scenario.learningObjectives || []),
    criticalActions: Array.from(
      scenario.EducationalComponents?.CriticalActions ||
      scenario.criticalActions ||
      scenario.CriticalActions ||
      scenario.mandatoryActions ||
      []
    ).map((item) => normalizeChecklistText(item)).filter(Boolean),
    commonPitfalls: Array.from(
      scenario.EducationalComponents?.CommonPitfalls ||
      scenario.commonPitfalls ||
      scenario.CommonPitfalls ||
      scenario.pitfalls ||
      []
    ).map((item) => normalizeChecklistText(item)).filter(Boolean),
    differentialDiagnosis: Array.from(scenario.EducationalComponents?.DifferentialDiagnosis || scenario.differentialDiagnosis || [])
  };

  const levelScopedCriticalActionsSource =
    scenario.levelScopedCriticalActions ||
    scenario.LevelScopedCriticalActions ||
    scenario.level_scoped_critical_actions ||
    scenario.EducationalComponents?.levelScopedCriticalActions ||
    scenario.EducationalComponents?.LevelScopedCriticalActions ||
    scenario.EducationalComponents?.level_scoped_critical_actions;

  const levelScopedPitfallsSource =
    scenario.levelScopedPitfalls ||
    scenario.LevelScopedPitfalls ||
    scenario.level_scoped_pitfalls ||
    scenario.EducationalComponents?.levelScopedPitfalls ||
    scenario.EducationalComponents?.LevelScopedPitfalls ||
    scenario.EducationalComponents?.level_scoped_pitfalls;

  const levelScopedCriticalActions = normalizeScopedItems(levelScopedCriticalActionsSource);
  const levelScopedPitfalls = normalizeScopedItems(levelScopedPitfallsSource);
  const scopedCriticalActions = levelScopedCriticalActions.length > 0
    ? levelScopedCriticalActions
    : learning.criticalActions.map((text) => ({ text, minLevel: fallbackScopedLevel }));
  const scopedPitfalls = levelScopedPitfalls.length > 0
    ? levelScopedPitfalls
    : learning.commonPitfalls.map((text) => ({ text, minLevel: fallbackScopedLevel }));

  const selectedLevelRank = providerLevelRank(selectedDisplayLevel);
  const toVisibleScopedText = (items) =>
    items
      .filter((item) => providerLevelRank(item.minLevel) <= selectedLevelRank)
      .map((item) => item.text);

  interventions = {
    LifeSaving: toVisibleScopedText(levelScopedInterventions.LifeSaving),
    Secondary: toVisibleScopedText(levelScopedInterventions.Secondary),
    Optional: toVisibleScopedText(levelScopedInterventions.Optional)
  };

  learning = {
    ...learning,
    criticalActions: toVisibleScopedText(scopedCriticalActions),
    commonPitfalls: toVisibleScopedText(scopedPitfalls)
  };

  // Proctor Notes
  const proctorNotes = {
    timingGuidance: scenario.proctorNotes?.timingGuidance || scenario.ProctorNotes?.TimingGuidance,
    progressionCues: scenario.proctorNotes?.progressionCues || scenario.ProctorNotes?.ProgressionCues,
    criticalTimings: scenario.proctorNotes?.criticalTimings || scenario.ProctorNotes?.CriticalTimings
  };

  const hasProctorNotes = proctorNotes.timingGuidance || proctorNotes.progressionCues || proctorNotes.criticalTimings;

  // Cardiac Arrest Sequence
  const cardiacArrest = {
    applicable: scenario.cardiacArrestSequence?.applicable || scenario.CardiacArrestSequence?.Applicable,
    initialRhythm: scenario.cardiacArrestSequence?.initialRhythm || scenario.CardiacArrestSequence?.InitialRhythm,
    sequence: Array.from(scenario.cardiacArrestSequence?.sequence || scenario.CardiacArrestSequence?.Sequence || []),
    targetOutcome: scenario.cardiacArrestSequence?.targetOutcome || scenario.CardiacArrestSequence?.TargetOutcome,
    postResuscitationCare: scenario.cardiacArrestSequence?.postResuscitationCare || scenario.CardiacArrestSequence?.PostResuscitationCare
  };

  const hasCardiacArrest = cardiacArrest.applicable === true || cardiacArrest.applicable === 'true';

  // Transport Context (FP-C/CCP-C)
  const transportContext = scenario.sceneSetup?.transportContext || scenario.SceneSetup?.TransportContext;

  // Moulage
  const moulage = scenario.sceneSetup?.moulage || scenario.SceneSetup?.Moulage;

  // Definitive Care
  const definitiveCare = {
    labTests: scenario.definitiveCare?.labTests || scenario.DefinitiveCare?.LabTests || [],
    imagingStudies: scenario.definitiveCare?.imagingStudies || scenario.DefinitiveCare?.ImagingStudies || [],
    diagnosticProcedures: scenario.definitiveCare?.diagnosticProcedures || scenario.DefinitiveCare?.DiagnosticProcedures || [],
    specialistConsultations: scenario.definitiveCare?.specialistConsultations || scenario.DefinitiveCare?.SpecialistConsultations || [],
    definitiveInterventions: scenario.definitiveCare?.definitiveInterventions || scenario.DefinitiveCare?.DefinitiveInterventions || []
  };

  const hasDefinitiveCare = definitiveCare.labTests.length > 0 || 
                           definitiveCare.imagingStudies.length > 0 || 
                           definitiveCare.diagnosticProcedures.length > 0 || 
                           definitiveCare.specialistConsultations.length > 0 ||
                           definitiveCare.definitiveInterventions.length > 0;

  const outcome = {
    finalCondition: scenario.ExpectedOutcome?.FinalCondition || scenario.expectedOutcome?.finalCondition,
    disposition: scenario.ExpectedOutcome?.Disposition || scenario.expectedOutcome?.disposition,
    continuingCareNeeds: scenario.ExpectedOutcome?.ContinuingCareNeeds || scenario.expectedOutcome?.continuingCareNeeds
  };

  // Parse BP for status checking
  const getBPStatus = (bp) => {
    if (!bp) return 'normal';
    const formatted = formatVital(bp);
    if (!formatted) return 'normal';
    const systolic = parseInt(formatted.split('/')[0]);
    return getVitalStatus('bp_systolic', systolic);
  };

  const renderAccessibleContent = () => {
    const bgColor = isHighContrast ? '#000000' : '#FFFFFF';
    const textColor = isHighContrast ? '#FFFFFF' : '#000000';

    // Helper to sanitize text and remove markdown/formatting
    const sanitizeText = (text) => {
      if (!text) return '';
      return String(text)
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/^[-•–—]\s+/gm, '')
        .replace(/^\s*[-•–—]\s+/gm, '')
        .replace(/#{1,6}\s+/g, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/_{1,2}([^_]+)_{1,2}/g, '$1')
        .trim();
    };

    const secondaryLabels = {
      heent: 'HEENT',
      neck: 'Neck',
      chest: 'Chest',
      respiratory: 'Respiratory',
      cardiovascular: 'Cardiovascular',
      abdomen: 'Abdomen',
      pelvis: 'Pelvis',
      extremities: 'Extremities',
      back: 'Back',
      neuro: 'Neurological',
      skin: 'Skin/Integumentary',
    };
    
    return (
      <Box bg={bgColor} color={textColor} p={6} minH="100vh" className="accessible-content">
        {/* Skip to main content link for screen readers */}
        <Box as="a" href="#main-content" className="sr-only" _focus={{
          position: 'static',
          width: 'auto',
          height: 'auto',
          padding: 2,
          margin: 2,
          overflow: 'visible',
          clip: 'auto'
        }}>
          Skip to main content
        </Box>
        {/* Back, Toggle, and Print buttons - fixed at top */}
        <HStack position="fixed" top={4} left={4} right={4} justifyContent="space-between" zIndex={1000} className="accessible-controls">
          <Button
            onClick={() => {
              setPrintMode('normal');
            }}
            size="lg"
            bg={textColor}
            color={bgColor}
            _hover={{ opacity: 0.8 }}
            className="back-button"
          >
            ← Back
          </Button>
          <Button
            onClick={toggleHighContrast}
            size="lg"
            bg={textColor}
            color={bgColor}
            _hover={{ opacity: 0.8 }}
          >
            {isHighContrast ? '◐ Black on White' : '◑ White on Black'}
          </Button>
          {allowPrintControls && (
            <Button
              onClick={handleAccessiblePrint}
              size="lg"
              bg={textColor}
              color={bgColor}
              _hover={{ opacity: 0.8 }}
              className="print-button"
            >
              🖨 Print
            </Button>
          )}
        </HStack>

        {/* Main content */}
        <Box as="main" id="main-content" mt={20}>
          {/* Scenario Title */}
          <Heading as="h1" size="xl" mb={6} color={textColor}>
            {sanitizeText(scenario.chiefComplaint) || 'Clinical Scenario'}
          </Heading>

          {/* Scenario content as nested bulleted list */}
          <VStack align="stretch" spacing={4} fontSize="lg" className="accessible-print-stack">
            {/* Patient Demographics */}
            {patientInfo && (patientInfo.age || patientInfo.sex || patientInfo.ethnicity || patientInfo.weight) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Patient Demographics</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {patientInfo.age && hasValue(patientInfo.age) && <ListItem>Age: {sanitizeText(patientInfo.age)}</ListItem>}
                  {patientInfo.sex && hasValue(patientInfo.sex) && <ListItem>Sex: {sanitizeText(patientInfo.sex)}</ListItem>}
                  {patientInfo.ethnicity && hasValue(patientInfo.ethnicity) && <ListItem>Ethnicity: {sanitizeText(patientInfo.ethnicity)}</ListItem>}
                  {patientInfo.weight && hasValue(patientInfo.weight) && <ListItem>Weight: {sanitizeText(patientInfo.weight)}</ListItem>}
                  {patientInfo.physicalAppearance && hasValue(patientInfo.physicalAppearance) && <ListItem>Physical Appearance: {sanitizeText(patientInfo.physicalAppearance)}</ListItem>}
                </List>
              </Box>
            )}

            {/* Scene Setup */}
            {sceneSetup && (sceneSetup.location || sceneSetup.dispatchInfo || sceneSetup.bystanders || sceneSetup.safetyIssues) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Scene Setup</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {sceneSetup.location && hasValue(sceneSetup.location) && <ListItem>Location: {sanitizeText(sceneSetup.location)}</ListItem>}
                  {sceneSetup.timeOfDay && hasValue(sceneSetup.timeOfDay) && <ListItem>Time of Day: {sanitizeText(sceneSetup.timeOfDay)}</ListItem>}
                  {sceneSetup.weather && hasValue(sceneSetup.weather) && <ListItem>Weather: {sanitizeText(sceneSetup.weather)}</ListItem>}
                  {sceneSetup.dispatchInfo && hasValue(sceneSetup.dispatchInfo) && <ListItem>Dispatch Info: {sanitizeText(sceneSetup.dispatchInfo)}</ListItem>}
                  {sceneSetup.bystanders && hasValue(sceneSetup.bystanders) && <ListItem>Bystanders: {sanitizeText(sceneSetup.bystanders)}</ListItem>}
                  {(sceneSetup.safetyIssues || sceneSetup.safetyConsiderations) && hasValue(sceneSetup.safetyIssues || sceneSetup.safetyConsiderations) && (
                    <ListItem>Safety Alert: {sanitizeText(sceneSetup.safetyIssues || sceneSetup.safetyConsiderations)}</ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Chief Complaint */}
            {scenario.chiefComplaint && hasValue(scenario.chiefComplaint) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Chief Complaint</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  <ListItem>{sanitizeText(scenario.chiefComplaint)}</ListItem>
                </List>
              </Box>
            )}

            {/* Preceptor Notes & Scenario Flow */}
            {(scenario.narrativeArc || hasProctorNotes) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Preceptor Notes & Scenario Flow</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {scenario.narrativeArc?.arrival && hasValue(scenario.narrativeArc.arrival) && <ListItem>Arrival: {sanitizeText(scenario.narrativeArc.arrival)}</ListItem>}
                  {scenario.narrativeArc?.keyTurningPoint && hasValue(scenario.narrativeArc.keyTurningPoint) && <ListItem>Key Turning Point: {sanitizeText(scenario.narrativeArc.keyTurningPoint)}</ListItem>}
                  {scenario.narrativeArc?.intervention && hasValue(scenario.narrativeArc.intervention) && <ListItem>Intervention: {sanitizeText(scenario.narrativeArc.intervention)}</ListItem>}
                  {scenario.narrativeArc?.resolution && hasValue(scenario.narrativeArc.resolution) && <ListItem>Resolution: {sanitizeText(scenario.narrativeArc.resolution)}</ListItem>}
                  {proctorNotes.timingGuidance && hasValue(proctorNotes.timingGuidance) && <ListItem>Timing Guidance: {sanitizeText(proctorNotes.timingGuidance)}</ListItem>}
                  {proctorNotes.criticalTimings && hasValue(proctorNotes.criticalTimings) && <ListItem>Critical Timings: {sanitizeText(proctorNotes.criticalTimings)}</ListItem>}
                  {proctorNotes.progressionCues && hasValue(proctorNotes.progressionCues) && <ListItem>Progression Cues: {sanitizeText(proctorNotes.progressionCues)}</ListItem>}
                </List>
              </Box>
            )}

            {/* Primary Assessment */}
            {primaryAssessment && (primaryAssessment.mentalStatus || primaryAssessment.airway || primaryAssessment.breathing || primaryAssessment.circulation) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Primary Assessment</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {primaryAssessment.mentalStatus && hasValue(primaryAssessment.mentalStatus) && <ListItem>Mental Status: {sanitizeText(primaryAssessment.mentalStatus)}</ListItem>}
                  {primaryAssessment.airway && hasValue(primaryAssessment.airway) && <ListItem>Airway: {sanitizeText(primaryAssessment.airway)}</ListItem>}
                  {primaryAssessment.breathing && hasValue(primaryAssessment.breathing) && <ListItem>Breathing: {sanitizeText(primaryAssessment.breathing)}</ListItem>}
                  {primaryAssessment.circulation && hasValue(primaryAssessment.circulation) && <ListItem>Circulation: {sanitizeText(primaryAssessment.circulation)}</ListItem>}
                  {primaryAssessment.skinColor && hasValue(primaryAssessment.skinColor) && <ListItem>Skin Color: {sanitizeText(primaryAssessment.skinColor)}</ListItem>}
                  {primaryAssessment.skinTemp && hasValue(primaryAssessment.skinTemp) && <ListItem>Skin Temperature: {sanitizeText(primaryAssessment.skinTemp)}</ListItem>}
                  {primaryAssessment.skinMoisture && hasValue(primaryAssessment.skinMoisture) && <ListItem>Skin Moisture: {sanitizeText(primaryAssessment.skinMoisture)}</ListItem>}
                </List>
              </Box>
            )}

            {/* Vital Signs */}
            {vitals.initial && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Initial Vital Signs</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {vitals.initial.hr && formatVital(vitals.initial.hr) && <ListItem>Heart Rate: {sanitizeText(formatVital(vitals.initial.hr))}</ListItem>}
                  {vitals.initial.bp && formatVital(vitals.initial.bp) && <ListItem>Blood Pressure: {sanitizeText(formatVital(vitals.initial.bp))}</ListItem>}
                  {vitals.initial.rr && formatVital(vitals.initial.rr) && <ListItem>Respiratory Rate: {sanitizeText(formatVital(vitals.initial.rr))}</ListItem>}
                  {vitals.initial.spo2 && formatVital(vitals.initial.spo2) && <ListItem>SpO2: {sanitizeText(formatVital(vitals.initial.spo2))}</ListItem>}
                  {vitals.initial.temp && formatVital(vitals.initial.temp) && <ListItem>Temperature: {sanitizeText(formatVital(vitals.initial.temp))}</ListItem>}
                  {vitals.initial.bloodGlucose && formatVital(vitals.initial.bloodGlucose) && <ListItem>Blood Glucose: {sanitizeText(formatVital(vitals.initial.bloodGlucose))}</ListItem>}
                  {vitals.initial.etco2 && formatVital(vitals.initial.etco2) && <ListItem>EtCO2: {sanitizeText(formatVital(vitals.initial.etco2))}</ListItem>}
                  {vitals.initial.gcs && hasValue(vitals.initial.gcs) && <ListItem>GCS: {sanitizeText(vitals.initial.gcs)}</ListItem>}
                  {vitals.initial.rhythm && formatVital(vitals.initial.rhythm) && <ListItem>Cardiac Rhythm: {sanitizeText(formatVital(vitals.initial.rhythm))}</ListItem>}
                  {vitals.initial.pupils && hasValue(vitals.initial.pupils) && <ListItem>Pupils: {sanitizeText(vitals.initial.pupils)}</ListItem>}
                  {vitals.initial['12Lead'] && hasValue(vitals.initial['12Lead']) && <ListItem>12-Lead ECG: {sanitizeText(vitals.initial['12Lead'])}</ListItem>}
                </List>
              </Box>
            )}

            {/* SAMPLE History */}
            {history && (history.allergies || history.medications || history.pmhx || history.lastMeal || history.events) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>SAMPLE History</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {scenario.chiefComplaint && hasValue(scenario.chiefComplaint) && <ListItem>Symptoms: {sanitizeText(scenario.chiefComplaint)}</ListItem>}
                  {history.allergies && hasValue(history.allergies) && <ListItem>Allergies: {sanitizeText(history.allergies)}</ListItem>}
                  {history.medications && hasValue(history.medications) && <ListItem>Medications: {sanitizeText(history.medications)}</ListItem>}
                  {history.pmhx && hasValue(history.pmhx) && <ListItem>Past Medical History: {sanitizeText(history.pmhx)}</ListItem>}
                  {history.lastMeal && hasValue(history.lastMeal) && <ListItem>Last Oral Intake: {sanitizeText(history.lastMeal)}</ListItem>}
                  {history.events && hasValue(history.events) && <ListItem>Events: {sanitizeText(history.events)}</ListItem>}
                </List>
              </Box>
            )}

            {/* History of Present Illness */}
            {(history.hpi || scenario.enhancedHpi) && (hasValue(history.hpi) || hasValue(scenario.enhancedHpi)) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>History of Present Illness</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  <ListItem>{sanitizeText(scenario.enhancedHpi || history.hpi)}</ListItem>
                </List>
              </Box>
            )}

            {/* Secondary Assessment */}
            {secondaryAssessment && Object.keys(secondaryAssessment).some(key => hasValue(secondaryAssessment[key])) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Secondary Assessment</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {Object.entries(secondaryAssessment).map(([key, value]) => {
                    if (value && hasValue(value)) {
                      const label = key.toUpperCase() === 'HEENT' ? 'HEENT' : 
                                   key.toUpperCase() === 'GI' ? 'GI/Abdomen' :
                                   key.charAt(0).toUpperCase() + key.slice(1);
                      return <ListItem key={key}>{label}: {sanitizeText(value)}</ListItem>;
                    }
                    return null;
                  })}
                </List>
              </Box>
            )}

            {/* Interventions */}
            {interventions && (interventions.LifeSaving?.length > 0 || interventions.Secondary?.length > 0 || interventions.Optional?.length > 0) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Interventions</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {interventions.LifeSaving && interventions.LifeSaving.length > 0 && (
                    <ListItem>
                      <Heading as="h3" size="md" fontWeight="semibold" color={textColor}>Critical/Life-Saving Interventions:</Heading>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {interventions.LifeSaving.map((item, i) => <ListItem key={i}>{sanitizeText(item)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {interventions.Secondary && interventions.Secondary.length > 0 && (
                    <ListItem>
                      <Heading as="h3" size="md" fontWeight="semibold" color={textColor}>Secondary Interventions:</Heading>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {interventions.Secondary.map((item, i) => <ListItem key={i}>{sanitizeText(item)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {interventions.Optional && interventions.Optional.length > 0 && (
                    <ListItem>
                      <Heading as="h3" size="md" fontWeight="semibold" color={textColor}>Optional/Conditional Interventions:</Heading>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {interventions.Optional.map((item, i) => <ListItem key={i}>{sanitizeText(item)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Expected Outcome */}
            {outcome && (outcome.finalCondition || outcome.disposition || outcome.continuingCareNeeds) && (
              <Box>
                <Text fontWeight="bold" fontSize="xl">Expected Outcome</Text>
                <List styleType="disc" ml={6} mt={2}>
                  {outcome.finalCondition && hasValue(outcome.finalCondition) && <ListItem>Final Condition: {outcome.finalCondition}</ListItem>}
                  {outcome.disposition && hasValue(outcome.disposition) && <ListItem>Disposition: {outcome.disposition}</ListItem>}
                  {outcome.continuingCareNeeds && hasValue(outcome.continuingCareNeeds) && <ListItem>Continuing Care: {outcome.continuingCareNeeds}</ListItem>}
                </List>
              </Box>
            )}

            {/* Trending Vital Signs */}
            {vitals.trending && vitals.trending.length > 0 && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Trending Vital Signs</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {vitals.trending.map((vitalSet, index) => (
                    <ListItem key={index}>
                      <Text fontWeight="semibold">{vitalSet.time && hasValue(vitalSet.time) ? sanitizeText(vitalSet.time) : `Time ${index + 1}`}:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {vitalSet.hr && formatVital(vitalSet.hr) && <ListItem>Heart Rate: {sanitizeText(formatVital(vitalSet.hr))}</ListItem>}
                        {vitalSet.bp && formatVital(vitalSet.bp) && <ListItem>Blood Pressure: {sanitizeText(formatVital(vitalSet.bp))}</ListItem>}
                        {vitalSet.rr && formatVital(vitalSet.rr) && <ListItem>Respiratory Rate: {sanitizeText(formatVital(vitalSet.rr))}</ListItem>}
                        {vitalSet.spo2 && formatVital(vitalSet.spo2) && <ListItem>SpO2: {sanitizeText(formatVital(vitalSet.spo2))}</ListItem>}
                        {vitalSet.temp && formatVital(vitalSet.temp) && <ListItem>Temperature: {sanitizeText(formatVital(vitalSet.temp))}</ListItem>}
                        {vitalSet.bloodGlucose && formatVital(vitalSet.bloodGlucose) && <ListItem>Blood Glucose: {sanitizeText(formatVital(vitalSet.bloodGlucose))}</ListItem>}
                        {vitalSet.etco2 && formatVital(vitalSet.etco2) && <ListItem>EtCO2: {sanitizeText(formatVital(vitalSet.etco2))}</ListItem>}
                        {vitalSet.gcs && hasValue(vitalSet.gcs) && <ListItem>GCS: {sanitizeText(vitalSet.gcs)}</ListItem>}
                      </List>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Progression With Treatment */}
            {scenario.vitalSigns?.progressionWithProperTreatment && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Progression WITH Proper Treatment</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {scenario.vitalSigns.progressionWithProperTreatment.description && hasValue(scenario.vitalSigns.progressionWithProperTreatment.description) && (
                    <ListItem>Description: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.description)}</ListItem>
                  )}
                  {scenario.vitalSigns.progressionWithProperTreatment.timeframe && hasValue(scenario.vitalSigns.progressionWithProperTreatment.timeframe) && (
                    <ListItem>Timeframe: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.timeframe)}</ListItem>
                  )}
                  {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals && (
                    <ListItem>
                      <Text>Expected Vitals:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.hr && <ListItem>Heart Rate: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.hr)}</ListItem>}
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.bp && <ListItem>Blood Pressure: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.bp)}</ListItem>}
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.rr && <ListItem>Respiratory Rate: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.rr)}</ListItem>}
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.spo2 && <ListItem>SpO2: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.spo2)}</ListItem>}
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.gcs && <ListItem>GCS: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.gcs)}</ListItem>}
                      </List>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Deterioration Without Treatment */}
            {scenario.vitalSigns?.progressionWithoutTreatment && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Deterioration WITHOUT Treatment</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {scenario.vitalSigns.progressionWithoutTreatment.description && hasValue(scenario.vitalSigns.progressionWithoutTreatment.description) && (
                    <ListItem>Description: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.description)}</ListItem>
                  )}
                  {scenario.vitalSigns.progressionWithoutTreatment.timeframe && hasValue(scenario.vitalSigns.progressionWithoutTreatment.timeframe) && (
                    <ListItem>Timeframe: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.timeframe)}</ListItem>
                  )}
                  {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals && (
                    <ListItem>
                      <Text>Expected Vitals:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.hr && <ListItem>Heart Rate: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.hr)}</ListItem>}
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.bp && <ListItem>Blood Pressure: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.bp)}</ListItem>}
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.rr && <ListItem>Respiratory Rate: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.rr)}</ListItem>}
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.spo2 && <ListItem>SpO2: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.spo2)}</ListItem>}
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.gcs && <ListItem>GCS: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.gcs)}</ListItem>}
                      </List>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Cardiac Arrest Sequence */}
            {hasCardiacArrest && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Cardiac Arrest Sequence</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {cardiacArrest.initialRhythm && hasValue(cardiacArrest.initialRhythm) && <ListItem>Initial Rhythm: {sanitizeText(cardiacArrest.initialRhythm)}</ListItem>}
                  {Array.isArray(cardiacArrest.sequence) && cardiacArrest.sequence.map((step, i) => (
                    <ListItem key={i}>Step {i + 1}: {sanitizeText(step)}</ListItem>
                  ))}
                  {cardiacArrest.targetOutcome && hasValue(cardiacArrest.targetOutcome) && <ListItem>Target Outcome: {sanitizeText(cardiacArrest.targetOutcome)}</ListItem>}
                  {cardiacArrest.postResuscitationCare && hasValue(cardiacArrest.postResuscitationCare) && <ListItem>Post-Resuscitation Care: {sanitizeText(cardiacArrest.postResuscitationCare)}</ListItem>}
                </List>
              </Box>
            )}

            {/* Learning Objectives */}
            {learning.learningObjectives && learning.learningObjectives.length > 0 && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Learning Objectives</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {learning.learningObjectives.map((objective, i) => <ListItem key={i}>{sanitizeText(objective)}</ListItem>)}
                </List>
              </Box>
            )}

            {/* Transport Context (FP-C/CCP-C) */}
            {transportContext && hasValue(transportContext) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Transport Context</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  <ListItem>{sanitizeText(transportContext)}</ListItem>
                </List>
              </Box>
            )}

            {/* Definitive Care */}
            {hasDefinitiveCare && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Hospital Definitive Care</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {definitiveCare.labTests && definitiveCare.labTests.length > 0 && (
                    <ListItem>
                      <Text fontWeight="semibold">Laboratory Results:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {definitiveCare.labTests.map((test, i) => <ListItem key={i}>{sanitizeText(test)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {definitiveCare.imagingStudies && definitiveCare.imagingStudies.length > 0 && (
                    <ListItem>
                      <Text fontWeight="semibold">Imaging Studies:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {definitiveCare.imagingStudies.map((study, i) => <ListItem key={i}>{sanitizeText(study)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {definitiveCare.diagnosticProcedures && definitiveCare.diagnosticProcedures.length > 0 && (
                    <ListItem>
                      <Text fontWeight="semibold">Diagnostic Procedures:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {definitiveCare.diagnosticProcedures.map((proc, i) => <ListItem key={i}>{sanitizeText(proc)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {definitiveCare.definitiveInterventions && definitiveCare.definitiveInterventions.length > 0 && (
                    <ListItem>
                      <Text fontWeight="semibold">Definitive Treatment:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {definitiveCare.definitiveInterventions.map((intervention, i) => <ListItem key={i}>{sanitizeText(intervention)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Critical Actions */}
            {learning.criticalActions && learning.criticalActions.length > 0 && (
              <Box>
                <Text fontWeight="bold" fontSize="xl">Critical Actions</Text>
                <List styleType="disc" ml={6} mt={2}>
                  {learning.criticalActions.map((action, i) => <ListItem key={i}>{action}</ListItem>)}
                </List>
              </Box>
            )}

            {/* Common Pitfalls */}
            {learning.commonPitfalls && learning.commonPitfalls.length > 0 && (
              <Box>
                <Text fontWeight="bold" fontSize="xl">Common Pitfalls</Text>
                <List styleType="disc" ml={6} mt={2}>
                  {learning.commonPitfalls.map((pitfall, i) => <ListItem key={i}>{pitfall}</ListItem>)}
                </List>
              </Box>
            )}

            {/* Differential Diagnosis */}
            {learning.differentialDiagnosis && learning.differentialDiagnosis.length > 0 && (
              <Box>
                <Text fontWeight="bold" fontSize="xl">Differential Diagnosis</Text>
                <List styleType="disc" ml={6} mt={2}>
                  {learning.differentialDiagnosis.map((dx, i) => {
                    if (typeof dx === 'string') {
                      return <ListItem key={i}>{dx}</ListItem>;
                    } else {
                      return (
                        <ListItem key={i}>
                          {sanitizeText(dx.condition || dx.Condition)}
                          {(dx.likelihood || dx.Likelihood) && ` (${sanitizeText(dx.likelihood || dx.Likelihood)})`}
                          {(dx.supportingFindings || dx.SupportingFindings) && ` | Supporting: ${sanitizeText(dx.supportingFindings || dx.SupportingFindings)}`}
                          {(dx.contradictingFindings || dx.ContradictingFindings) && ` | Against: ${sanitizeText(dx.contradictingFindings || dx.ContradictingFindings)}`}
                        </ListItem>
                      );
                    }
                  })}
                </List>
              </Box>
            )}
          </VStack>
        </Box>
      </Box>
    );
  };

  return (
    <Box position="relative">
      {captureBlockVisible && (
        <Box
          position="fixed"
          inset={0}
          bg="black"
          zIndex={2000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={6}
          className="no-print"
        >
          <VStack spacing={4} maxW="720px" width="full">
            <Heading size="md" color="white" textAlign="center">
              Capture Block Active
            </Heading>
            <Text color="gray.200" textAlign="center">
              {captureBlockReason || "Capture activity detected. Content has been temporarily blocked."}
            </Text>
            {captureShowMeme && (
              <Alert status="error" borderRadius="md" bg="whiteAlpha.200" color="white" maxW="680px">
                <AlertIcon color="red.300" />
                <Box>
                  <AlertTitle>Possible Screenshot Detected</AlertTitle>
                  <AlertDescription>
                    This event may indicate a screenshot/capture attempt.
                  </AlertDescription>
                </Box>
              </Alert>
            )}
            {captureShowMeme && (
              <Box
                as="img"
                src={captureImageSrc}
                alt="Capture blocked"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = CRYING_JORDAN_FALLBACK_URL;
                }}
                maxH="60vh"
                borderRadius="lg"
                border="2px solid"
                borderColor="whiteAlpha.500"
                objectFit="contain"
                bg="white"
              />
            )}
          </VStack>
        </Box>
      )}
      <style>{printStyles}</style>{dedicatedPrintMode && <style>{dedicatedPrintStyles}</style>}
      <style>{`
        /* Ensure watermarks print */
        .scenario-watermark,
        .subtle-watermark {
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        
        @media print {
          .scenario-watermark {
            opacity: 0.25 !important;
          }
        }
      `}</style>



      {/* Accessible Content - Hidden in other modes */}
      {printMode === 'accessible' && renderAccessibleContent()}



      {/* Visual Content - Hidden in accessible mode */}
      {printMode !== 'accessible' && (
        <>
          {/* Tablet Mode Controls - Translucent overlay */}
          {printMode === 'tablet-view' && (
        <Box 
          className="tablet-controls" 
          position="fixed" 
          top={4} 
          right={4} 
          zIndex={9999}
          bg="blackAlpha.300"
          borderRadius="md"
          p={1}
          opacity={0.4}
          _hover={{ opacity: 0.95, bg: 'blackAlpha.500' }}
          transition="all 0.2s"
        >
          <VStack spacing={1}>
            <Button 
              size="xs" 
              colorScheme="red" 
              onClick={exitTabletMode}
              variant="solid"
            >
              Exit
            </Button>
            <Button 
              size="xs" 
              colorScheme={orientationMode === 'auto' ? 'blue' : 'gray'}
              onClick={() => setOrientation('auto')}
              variant="solid"
            >
              Auto
            </Button>
            <Button 
              size="xs" 
              colorScheme={orientationMode === 'landscape' ? 'blue' : 'gray'}
              onClick={() => setOrientation('landscape')}
              variant="solid"
            >
              ⟷
            </Button>
            <Button 
              size="xs" 
              colorScheme={orientationMode === 'portrait' ? 'blue' : 'gray'}
              onClick={() => setOrientation('portrait')}
              variant="solid"
            >
              ⬍
            </Button>
          </VStack>
        </Box>
      )}

      {/* Print Mode Selector - Top Only */}
      {printMode !== 'accessible' && printMode !== 'tablet-view' && (
        <Box className="no-print scenario-header avoid-break" mb={4}>
          <HStack spacing={4} justify="space-between" wrap="wrap">
            <HStack spacing={2}>
              <Text fontWeight="bold" fontSize="sm">View Mode:</Text>
              <Button
                size="sm"
                colorScheme={printMode === 'normal' ? 'blue' : 'gray'}
                onClick={() => handlePrintMode('normal')}
                variant={printMode === 'normal' ? 'solid' : 'outline'}
              >
                Normal View
              </Button>
              {allowPrintControls && (
                <Button
                  size="sm"
                  colorScheme={printMode === 'simple-print' ? 'blue' : 'gray'}
                  onClick={() => handlePrintMode('simple-print')}
                  variant={printMode === 'simple-print' ? 'solid' : 'outline'}
                >
                  Simple (1 Page)
                </Button>
              )}
              <Button
                size="sm"
                colorScheme="teal"
                onClick={() => handlePrintMode('tablet-view')}
              >
                Tablet View
              </Button>
              <Button
                size="sm"
                colorScheme="purple"
                onClick={() => handlePrintMode('accessible')}
              >
                Accessible Mode
              </Button>
            </HStack>
            {allowPrintControls && printMode === "normal" && (
              <HStack spacing={2}>
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={handlePrint}
                >
                  Print Scenario
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={handleDownloadColorPdf}
                  isLoading={isDownloadingPdf}
                  loadingText="Building PDF"
                >
                  Download PDF
                </Button>
              </HStack>
            )}
          </HStack>
        </Box>
      )}

      {/* Print Instructions for Simple Mode */}
	      {allowPrintControls && printMode === 'simple-print' && (
	        <Box 
          position="fixed" 
          top="8px" 
          right="8px" 
          bg="white" 
          border="3px solid black" 
          p={3} 
          borderRadius="md"
          zIndex={10000}
          className="print-button"
          shadow="xl"
	        >
	          <VStack spacing={2} align="stretch">
	            <Text fontWeight="bold" fontSize="sm" color="black">Simple Print Mode</Text>
	            <Text fontSize="xs" color="black">Ctrl/Cmd+P prints 1 page by default</Text>
	            <Button onClick={() => { void printSimpleView("one"); }} colorScheme="blue" size="sm">
	              🖨️ Print 1 Page
	            </Button>
	            <Button onClick={() => { void printSimpleView("two"); }} colorScheme="teal" size="sm">
	              🖨️ Print 2 Pages
	            </Button>
	            <Button onClick={() => handlePrintMode('normal')} size="sm" variant="outline">
	              ← Back to Normal
	            </Button>
	          </VStack>
        </Box>
      )}

      {/* Accessibility mode contrast toggle - only when IN accessible mode */}
      {printMode === 'accessible' && (
        <Box className="no-print" bg="gray.100" p={3} mb={3} borderRadius="md" border="2px solid" borderColor="purple.500">
          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              colorScheme={isHighContrast ? 'purple' : 'gray'}
              onClick={toggleHighContrast}
              leftIcon={<Box as="span">{isHighContrast ? '⚫' : '⚪'}</Box>}
            >
              {isHighContrast ? 'White on Black' : 'Black on White'}
            </Button>
          </HStack>
        </Box>
      )}

      {/* Raw Generator Input Section - Show user's original form data - ADMIN ONLY */}
      {printMode !== 'accessible' && isAdmin() && (
        <>
          <Box className="no-print" mt={2} mb={2}>
            <label>
              <input
                type="checkbox"
                checked={showRawUserInput}
                onChange={(e) => setShowRawUserInput(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Show raw generator input (user's form data)
            </label>
          </Box>
          
          {showRawUserInput && (
            <Card bg={cardBg} shadow="md" color={cardTextColor} mt={2} mb={3} className="no-print">
              <CardHeader bg="gray.600" color="white" borderTopRadius="md">
                <Heading size="md" color="white">Raw Generator Input (User's Form Data)</Heading>
              </CardHeader>
              <CardBody>
                <Text fontSize="sm" mb={2} color={labelColor}>
                  This shows the original parameters that were entered into the scenario generator form before the AI created the scenario.
                </Text>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', overflow: 'auto' }}>
                  {JSON.stringify(metadata, null, 2)}
                </pre>
              </CardBody>
            </Card>
          )}
        </>
      )}

      {/* Patient Info Banner - Always Visible */}
      <Box 
        bg={useColorModeValue('gray.100', 'gray.900')} 
        borderBottom="2px solid" 
        borderColor="blue.500"
        p={2}
        mb={2}
        className="patient-info-banner"
      >
        <HStack spacing={4} justify="center" flexWrap="wrap">
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="bold" color="blue.700">Display Level:</Text>
            {isSimplePrint ? (
              <Text fontSize="sm" fontWeight="bold">{PROVIDER_LEVEL_LABELS[selectedDisplayLevel] || selectedDisplayLevel}</Text>
            ) : (
              <Select
                size="sm"
                maxW="240px"
                value={selectedDisplayLevel}
                onChange={(e) => setSelectedDisplayLevel(normalizeProviderLevel(e.target.value))}
                bg={cardBg}
              >
                {PROVIDER_LEVEL_SEQUENCE.map((level) => (
                  <option key={level} value={level}>
                    {PROVIDER_LEVEL_LABELS[level]}
                  </option>
                ))}
              </Select>
            )}
          </HStack>
          {/* Patient Age */}
          {patientInfo.age && hasValue(patientInfo.age) && (
            <Text fontSize="sm" fontWeight="bold">
              Age: {patientInfo.age}
            </Text>
          )}
          {/* Chief Complaint */}
          {scenario.chiefComplaint && hasValue(scenario.chiefComplaint) && (
            <Text fontSize="sm" fontWeight="bold">
              CC: {scenario.chiefComplaint}
            </Text>
          )}
          {/* MOI/NOI - from metadata */}
          {showRawInput && metadata?.mechanismNatureOfInjury && hasValue(metadata.mechanismNatureOfInjury) && (
            <Text fontSize="sm" fontWeight="bold" color="orange.600">
              MOI/NOI: {metadata.mechanismNatureOfInjury}
            </Text>
          )}
        </HStack>
      </Box>

      <Box
        maxW={isSimplePrint ? "none" : "1200px"}
        mx="auto"
        p={{ base: 2, md: 3 }}
        bg={cardBg}
        color={cardTextColor}
        className="scenario-content-for-pdf"
      >
      <Box
        className="inline-watermark-border-top"
        border="1px solid"
        borderColor={boxBorderColor}
        bg={boxBg}
        borderRadius="sm"
        px={2}
        py={1}
        mb={2}
      >
        <Text
          fontSize="8px"
          letterSpacing="0.08em"
          textTransform="uppercase"
          color={mutedTextColor}
          lineHeight="1.2"
          noOfLines={1}
        >
          {borderWatermarkLabel}
        </Text>
      </Box>

      {/* Generated Media Section */}
      <MediaDisplaySection scenario={scenario} />

      {/* Preceptor Notes & Scenario Flow - FIRST, FULL WIDTH */}
      {(hasProctorNotes || scenario.narrativeArc) && (
        <Card bg={cardBg} shadow="md" color={cardTextColor} mb={2} className="page-break-avoid">
          <CardHeader bg="teal.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">Preceptor Notes & Scenario Flow</Heading>
          </CardHeader>
          <CardBody p={1}>
            <SimpleGrid columns={2} spacing={2}>
              {/* Left Column: Scenario Flow */}
              {scenario.narrativeArc && (
                <Box>
                  <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Scenario Flow:</Text>
                  {scenario.narrativeArc.fullNarrative ? (
                    <Text fontSize="sm" lineHeight="tall" color={textColor} fontStyle="italic">
                      {scenario.narrativeArc.fullNarrative}
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={1}>
                      {scenario.narrativeArc.arrival && (
                        <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                          <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontSize="xs" fontWeight="bold" color="blue.600">Arrival:</Text>
                          <Text fontSize="sm" color={textColor}>{scenario.narrativeArc.arrival}</Text>
                        </Box>
                      )}
                      {scenario.narrativeArc.keyTurningPoint && (
                        <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                          <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontSize="xs" fontWeight="bold" color="orange.600">Key Turning Point:</Text>
                          <Text fontSize="sm" color={textColor}>{scenario.narrativeArc.keyTurningPoint}</Text>
                        </Box>
                      )}
                      {scenario.narrativeArc.intervention && (
                        <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                          <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontSize="xs" fontWeight="bold" color="green.600">Intervention:</Text>
                          <Text fontSize="sm" color={textColor}>{scenario.narrativeArc.intervention}</Text>
                        </Box>
                      )}
                      {scenario.narrativeArc.resolution && (
                        <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                          <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontSize="xs" fontWeight="bold" color="purple.600">Resolution:</Text>
                          <Text fontSize="sm" color={textColor}>{scenario.narrativeArc.resolution}</Text>
                        </Box>
                      )}
                    </VStack>
                  )}
                </Box>
              )}
              
              {/* Right Column: Preceptor Notes */}
              {hasProctorNotes && (
                <Box>
                  <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Preceptor Guidance:</Text>
                  <VStack align="stretch" spacing={1}>
                    {proctorNotes.timingGuidance && hasValue(proctorNotes.timingGuidance) && (
                      <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden" borderLeft="4px solid" borderLeftColor="blue.500">
                        <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color="blue.600" fontSize="sm">⏱️ Timing Guidance</Text>
                        <Text fontSize="sm" color={textColor}>{proctorNotes.timingGuidance}</Text>
                      </Box>
                    )}
                    {proctorNotes.criticalTimings && hasValue(proctorNotes.criticalTimings) && (
                      <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden" borderLeft="4px solid" borderLeftColor="red.500">
                        <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color="red.600" fontSize="sm">⚡ Critical Timings</Text>
                        <Text fontSize="sm" color={textColor}>{proctorNotes.criticalTimings}</Text>
                      </Box>
                    )}
                    {proctorNotes.progressionCues && hasValue(proctorNotes.progressionCues) && (
                      <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden" borderLeft="4px solid" borderLeftColor="green.500">
                        <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color="green.600" fontSize="sm">➡️ Progression Cues</Text>
                        <Text fontSize="sm" color={textColor}>{proctorNotes.progressionCues}</Text>
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}
              
              {/* Full-width if only one column has content */}
              {!scenario.narrativeArc && hasProctorNotes && <Box />}
              {scenario.narrativeArc && !hasProctorNotes && <Box />}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}


      {/* Row 1: Background/Dispatch and Patient History - 2 Columns */}
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr"
        gap={1}
        mb={1}
        className="first-row-grid page-break-avoid"
      >
        {/* Background / Dispatch */}
        <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
          <CardHeader bg="blue.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">
              <HStack><FaMapMarkerAlt /><Text>Background / Dispatch</Text></HStack>
            </Heading>
          </CardHeader>
          <CardBody p={1}>
            {/* Setup/dispatch in two stacked columns with extended boundaries */}
            <SimpleGrid columns={2} spacing={1} alignItems="stretch">
              <VStack align="stretch" spacing={1} h="100%">
                {sceneSetup.dispatchInfo && hasValue(sceneSetup.dispatchInfo) && (
                  <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                    <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" fontSize="sm" color={elementHeaderColor}>Dispatch Info:</Text>
                    {scenario.sceneArrivalVideoUrl ? (
                      <Text
                        fontSize="md"
                        color="blue.500"
                        textDecoration="underline"
                        cursor="pointer"
                        onClick={() => openMediaModal('video', scenario.sceneArrivalVideoUrl, 'Scene Arrival Video')}
                        _hover={{ color: 'blue.600' }}
                      >
                        {sceneSetup.dispatchInfo} 🎥
                      </Text>
                    ) : (
                      <Text fontSize="md" color={textColor}>{sceneSetup.dispatchInfo}</Text>
                    )}
                    {(sceneSetup.timeOfDay || sceneSetup.location || sceneSetup.weather) &&
                     (hasValue(sceneSetup.timeOfDay) || hasValue(sceneSetup.location) || hasValue(sceneSetup.weather)) && (
                      <Box mt={1} pt={1} borderTop="1px solid" borderColor={boxBorderColor}>
                        {sceneSetup.timeOfDay && hasValue(sceneSetup.timeOfDay) && (
                          <Text color={textColor} fontSize="sm">{sceneSetup.timeOfDay}</Text>
                        )}
                        {sceneSetup.location && hasValue(sceneSetup.location) && (
                          <Text color={textColor} fontSize="sm">{sceneSetup.location}</Text>
                        )}
                        {sceneSetup.weather && hasValue(sceneSetup.weather) && (
                          <Text color={textColor} fontSize="sm">{sceneSetup.weather}</Text>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
                <Box
                  display={{ base: "none", md: "block" }}
                  flex="1"
                  borderLeft="1px solid"
                  borderRight="1px solid"
                  borderColor={boxBorderColor}
                  borderBottomRadius="md"
                  minH="16px"
                />

              </VStack>

              <VStack align="stretch" spacing={1} h="100%">
                {sceneSetup.flightOperations && hasValue(sceneSetup.flightOperations) && (
                  <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                    <HStack spacing={2} mb={1}>
                      <Box as={FaHelicopter} color="orange.500" />
                      <Text fontWeight="bold" fontSize="sm" color="orange.500">Flight Operations Considerations</Text>
                    </HStack>
                    <Box bg={alertBgWarning} p={2} borderRadius="md" borderLeft="4px solid" borderColor="orange.500">
                      <Text fontSize="sm" color={textColor} whiteSpace="pre-line">{sceneSetup.flightOperations}</Text>
                    </Box>
                  </Box>
                )}

                {(sceneSetup.safetyConsiderations || sceneSetup.safetyIssues) &&
                  hasValue(sceneSetup.safetyConsiderations || sceneSetup.safetyIssues) && (
                  <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                    <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">
                      <Text as="span" color="red.300">⚠</Text> Safety Alert
                    </Text>
                    <Text fontSize="md" color={textColor}>
                      {sceneSetup.safetyConsiderations || sceneSetup.safetyIssues}
                    </Text>
                  </Box>
                )}

                {sceneSetup.bystanders && hasValue(sceneSetup.bystanders) && (
                  <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                    <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">
                      Bystanders/Scene:
                    </Text>
                    <Text color={textColor} fontSize="sm">{sceneSetup.bystanders}</Text>
                  </Box>
                )}
                <Box
                  display={{ base: "none", md: "block" }}
                  flex="1"
                  borderLeft="1px solid"
                  borderRight="1px solid"
                  borderColor={boxBorderColor}
                  borderBottomRadius="md"
                  minH="16px"
                />
              </VStack>
            </SimpleGrid>

            {/* Patient full width at bottom */}
            {(patientInfo.name || patientInfo.age || patientInfo.sex || patientInfo.weight) && 
             (hasValue(patientInfo.name) || hasValue(patientInfo.age) || hasValue(patientInfo.sex) || hasValue(patientInfo.weight)) && (
              <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden" mt={1}>
                <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Patient</Text>
                <Text color={textColor} fontSize="md">
                  {[
                    patientInfo.age && hasValue(patientInfo.age) ? patientInfo.age : null,
                    patientInfo.sex && hasValue(patientInfo.sex) ? patientInfo.sex : null,
                    patientInfo.weight && hasValue(patientInfo.weight) ? patientInfo.weight : null
                  ].filter(Boolean).join(', ')}
                  {patientInfo.name && hasValue(patientInfo.name) && ` (${patientInfo.name})`}
                </Text>
                {(patientInfo.physicalAppearance || patientInfo.moulage || moulage) && (hasValue(patientInfo.physicalAppearance) || hasValue(patientInfo.moulage) || hasValue(moulage)) && (
                  <Text color={textColor} fontSize="sm" mt={1}>
                    <Text as="span" fontWeight="bold">Appearance: </Text>
                    {scenario.patientImageUrl ? (
                      <Text
                        as="span"
                        color="blue.500"
                        textDecoration="underline"
                        cursor="pointer"
                        onClick={() => openMediaModal('image', scenario.patientImageUrl, 'Patient Image')}
                        _hover={{ color: 'blue.600' }}
                      >
                        {patientInfo.moulage || moulage || patientInfo.physicalAppearance} 📷
                      </Text>
                    ) : (
                      patientInfo.moulage || moulage || patientInfo.physicalAppearance
                    )}
                  </Text>
                )}
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Patient History */}
        <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
          <CardHeader bg="purple.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">Patient History</Heading>
          </CardHeader>
          <CardBody p={1}>
            <VStack align="stretch" spacing={1}>
              {/* HPI first */}
              {(history.hpi || scenario.enhancedHpi) && (hasValue(history.hpi) || hasValue(scenario.enhancedHpi)) && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('sample', 'hpi')}
                  onToggle={(checked) => handleCheckboxToggle('sample', 'hpi', checked)}
                  label="History of Present Illness"
                >
                  <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                    <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">History of Present Illness:</Text>
                    <Text fontSize="sm" lineHeight="short" color={textColor}>
                      {scenario.enhancedHpi || history.hpi}
                    </Text>
                    {scenario.enhancedHpi && (
                      <Badge colorScheme="cyan" size="sm" mt={1} display="inline-block">
                        ✨ Enhanced for {scenario.ScenarioMetadata?.TrainingLevel || scenario.trainingLevel} level
                      </Badge>
                    )}
                  </Box>
                </CheckableItem>
              )}

              {/* PTA directly under HPI */}
              {sceneSetup.priorToArrivalCare && hasValue(sceneSetup.priorToArrivalCare) && sceneSetup.priorToArrivalCare !== 'None' && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('sample', 'pta')}
                  onToggle={(checked) => handleCheckboxToggle('sample', 'pta', checked)}
                  label="Prior to Arrival Care"
                >
                  <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                    <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Prior to Arrival (PTA):</Text>
                    <Text fontSize="sm" color={textColor}>{sceneSetup.priorToArrivalCare}</Text>
                  </Box>
                </CheckableItem>
              )}

              {/* Two stacked columns from Chief Complaint through Events */}
              <SimpleGrid columns={2} spacing={1} alignItems="start">
                <VStack align="stretch" spacing={1}>
                  {scenario.chiefComplaint && hasValue(scenario.chiefComplaint) && (
                    <CheckableItem
                      evaluationMode={evaluationMode}
                      isChecked={isChecked('primary', 'chiefComplaint')}
                      onToggle={(checked) => handleCheckboxToggle('primary', 'chiefComplaint', checked)}
                      label="Chief Complaint"
                    >
                      <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                        <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="md">Chief Complaint:</Text>
                        <Text fontSize="md" fontWeight="semibold" color={textColor}>{scenario.chiefComplaint}</Text>
                      </Box>
                    </CheckableItem>
                  )}

                  {history.allergies && hasValue(history.allergies) && (
                    <CheckableItem
                      evaluationMode={evaluationMode}
                      isChecked={isChecked('sample', 'allergies')}
                      onToggle={(checked) => handleCheckboxToggle('sample', 'allergies', checked)}
                      label="Allergies"
                    >
                      <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                        <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Allergies:</Text>
                        <Text color={textColor} fontSize="sm">{history.allergies}</Text>
                      </Box>
                    </CheckableItem>
                  )}

                  {history.medications && hasValue(history.medications) && (
                    <CheckableItem
                      evaluationMode={evaluationMode}
                      isChecked={isChecked('sample', 'medications')}
                      onToggle={(checked) => handleCheckboxToggle('sample', 'medications', checked)}
                      label="Medications"
                    >
                      <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                        <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Medications:</Text>
                        <Text color={textColor} fontSize="sm">{history.medications}</Text>
                      </Box>
                    </CheckableItem>
                  )}
                </VStack>

                <VStack align="stretch" spacing={1}>
                  {history.pmhx && hasValue(history.pmhx) && (
                    <CheckableItem
                      evaluationMode={evaluationMode}
                      isChecked={isChecked('sample', 'pmhx')}
                      onToggle={(checked) => handleCheckboxToggle('sample', 'pmhx', checked)}
                      label="Past Medical History"
                    >
                      <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                        <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Pertinent PMHx:</Text>
                        <Text color={textColor} fontSize="sm">{history.pmhx}</Text>
                      </Box>
                    </CheckableItem>
                  )}

                  {history.lastMeal && hasValue(history.lastMeal) && (
                    <CheckableItem
                      evaluationMode={evaluationMode}
                      isChecked={isChecked('sample', 'lastMeal')}
                      onToggle={(checked) => handleCheckboxToggle('sample', 'lastMeal', checked)}
                      label="Last Oral Intake"
                    >
                      <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                        <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Last Oral Intake:</Text>
                        <Text color={textColor} fontSize="sm">{history.lastMeal}</Text>
                      </Box>
                    </CheckableItem>
                  )}

                  {history.events && hasValue(history.events) && (
                    <CheckableItem
                      evaluationMode={evaluationMode}
                      isChecked={isChecked('sample', 'events')}
                      onToggle={(checked) => handleCheckboxToggle('sample', 'events', checked)}
                      label="Events"
                    >
                      <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={2} borderRadius="md" overflow="hidden">
                        <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Events Leading to Incident:</Text>
                        <Text fontSize="sm" lineHeight="short" color={textColor}>{history.events}</Text>
                      </Box>
                    </CheckableItem>
                  )}
                </VStack>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>
      </Box>

      {isSimplePrint && <Box className="page-break-between-history-assessment" />}

      {/* Row 3: Primary Assessment and Secondary Assessment - 2 Columns */}
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr"
        gap={1}
        mb={1}
        className="second-row-grid page-break-avoid"
      >
        {/* Primary Assessment with Vitals */}
        <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
          <CardHeader bg="green.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">Primary Assessment</Heading>
          </CardHeader>
          <CardBody p={1}>
            <VStack align="stretch" spacing={1}>
              {primaryAssessment.mentalStatus && hasValue(primaryAssessment.mentalStatus) && (
                <Box>
                  <Badge
                    colorScheme={getLOCColor(primaryAssessment.mentalStatus)}
                    fontSize="sm"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    {primaryAssessment.mentalStatus}
                  </Badge>
                </Box>
              )}

              {(primaryAssessment.airway || primaryAssessment.breathing || primaryAssessment.circulation) && (
                <Box>
                  <SimpleGrid columns={3} spacing={1}>
                    {primaryAssessment.airway && hasValue(primaryAssessment.airway) && (
                      <Box
                        bg={isSimplePrint ? "transparent" : "red.50"}
                        border="1px solid"
                        borderColor={isSimplePrint ? "black" : "red.200"}
                        borderLeft="3px solid"
                        borderLeftColor={isSimplePrint ? "black" : "red.600"}
                        p={1}
                        borderRadius="md"
                      >
                        <HStack spacing={0.5} mb={0.5}>
                          <FaUser color={isSimplePrint ? "#000000" : "#C53030"} size={13} />
                          <Text fontWeight="bold" color={isSimplePrint ? "black" : "red.600"} fontSize="sm">AIRWAY</Text>
                        </HStack>
                        <Text fontSize="xs" color={isSimplePrint ? "black" : "gray.900"}>{primaryAssessment.airway}</Text>
                      </Box>
                    )}
                    {primaryAssessment.breathing && hasValue(primaryAssessment.breathing) && (
                      <Box
                        bg={isSimplePrint ? "transparent" : "orange.50"}
                        border="1px solid"
                        borderColor={isSimplePrint ? "black" : "orange.200"}
                        borderLeft="3px solid"
                        borderLeftColor={isSimplePrint ? "black" : "orange.600"}
                        p={1}
                        borderRadius="md"
                      >
                        <HStack spacing={0.5} mb={0.5}>
                          <FaLungs color={isSimplePrint ? "#000000" : "#C05621"} size={13} />
                          <Text fontWeight="bold" color={isSimplePrint ? "black" : "orange.600"} fontSize="sm">BREATHING</Text>
                        </HStack>
                        <Text fontSize="xs" color={isSimplePrint ? "black" : "gray.900"}>{primaryAssessment.breathing}</Text>
                      </Box>
                    )}
                    {primaryAssessment.circulation && hasValue(primaryAssessment.circulation) && (
                      <Box
                        bg={isSimplePrint ? "transparent" : "red.50"}
                        border="1px solid"
                        borderColor={isSimplePrint ? "black" : "red.200"}
                        borderLeft="3px solid"
                        borderLeftColor={isSimplePrint ? "black" : "red.600"}
                        p={1}
                        borderRadius="md"
                      >
                        <HStack spacing={0.5} mb={0.5}>
                          <FaHeartbeat color={isSimplePrint ? "#000000" : "#C53030"} size={13} />
                          <Text fontWeight="bold" color={isSimplePrint ? "black" : "red.600"} fontSize="sm">CIRCULATION</Text>
                        </HStack>
                        <Text fontSize="xs" color={isSimplePrint ? "black" : "gray.900"}>{primaryAssessment.circulation}</Text>
                        {(primaryAssessment.skinColor || primaryAssessment.skinTemp || primaryAssessment.skinMoisture) && (
                          <Text fontSize="xs" color={isSimplePrint ? "black" : "gray.700"} mt={1}>
                            Skin: {[
                              primaryAssessment.skinColor && hasValue(primaryAssessment.skinColor) ? primaryAssessment.skinColor : null,
                              primaryAssessment.skinTemp && hasValue(primaryAssessment.skinTemp) ? primaryAssessment.skinTemp : null,
                              primaryAssessment.skinMoisture && hasValue(primaryAssessment.skinMoisture) ? primaryAssessment.skinMoisture : null
                            ].filter(Boolean).join(', ')}
                          </Text>
                        )}
                      </Box>
                    )}
                    </SimpleGrid>
                  </Box>
              )}

              {/* Initial Vital Signs */}
              {vitals.initial && (
                <Box>
                  <HStack justify="flex-start" align="center" spacing={2} mb={1} mt={1}>
                    <Text fontWeight="bold" color={boldTextColor} fontSize="sm">Initial Vital Signs:</Text>
                    {evaluationMode && initialVitalChecklistKeys.length > 0 && (
                      <HStack spacing={1}>
                        <Text fontSize="xs" color={labelColor}>All</Text>
                        <Box
                          as="input"
                          type="checkbox"
                          checked={areAllInitialVitalsChecked}
                          onChange={(e) => toggleAllInitialVitals(e.target.checked)}
                          cursor="pointer"
                        />
                      </HStack>
                    )}
                  </HStack>
                    <SimpleGrid columns={Math.max(2, initialVitalTileCount)} spacing={0.5}>
                    {vitals.initial.hr && formatVital(vitals.initial.hr) && (
                      <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        cursor={evaluationMode ? "pointer" : "default"}
                        onClick={evaluationMode ? () => handleCheckboxToggle("vitals", "initial_hr", !isChecked("vitals", "initial_hr")) : undefined}
                      >
                                            <Stat size="sm" textAlign="center" sx={{ ".chakra-stat__label": { textAlign: "center" }, ".chakra-stat__number": { textAlign: "center" } }} bg={statsBoxBg} border="1px solid" borderColor="gray.600" borderRadius="md" p={0.5} pr={0.5} pb={evaluationMode ? 5 : 0.5} minH="64px" display="flex" flexDirection="column" justifyContent="center">
                        <StatLabel fontSize="xs" fontWeight="bold" letterSpacing="0.3px" whiteSpace="nowrap" color={monitorVitalColors.hr}>HR</StatLabel>
                        <StatNumber color={monitorVitalColors.hr} fontSize="sm">
                          {formatVital(vitals.initial.hr)}
                        </StatNumber>
                        {evaluationMode && (
                          <Box
                            as="input"
                            type="checkbox"
                            checked={isChecked("vitals", "initial_hr")}
                            onChange={(e) => handleCheckboxToggle("vitals", "initial_hr", e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            mt={0.5}
                            mx="auto"
                            display="block"
                          />
                        )}
                      </Stat>
                      </Box>
                    )}
                    {vitals.initial.bp && formatVital(vitals.initial.bp) && (
                      <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        cursor={evaluationMode ? "pointer" : "default"}
                        onClick={evaluationMode ? () => handleCheckboxToggle("vitals", "initial_bp", !isChecked("vitals", "initial_bp")) : undefined}
                      >
                                            <Stat size="sm" textAlign="center" sx={{ ".chakra-stat__label": { textAlign: "center" }, ".chakra-stat__number": { textAlign: "center" } }} bg={statsBoxBg} border="1px solid" borderColor="gray.600" borderRadius="md" p={0.5} pr={0.5} pb={evaluationMode ? 5 : 0.5} minH="64px" display="flex" flexDirection="column" justifyContent="center">
                        <StatLabel fontSize="xs" fontWeight="bold" letterSpacing="0.3px" whiteSpace="nowrap" color={monitorVitalColors.bp}>BP</StatLabel>
                        <StatNumber color={monitorVitalColors.bp} fontSize="sm">
                          {formatVital(vitals.initial.bp)}
                        </StatNumber>
                        {evaluationMode && (
                          <Box
                            as="input"
                            type="checkbox"
                            checked={isChecked("vitals", "initial_bp")}
                            onChange={(e) => handleCheckboxToggle("vitals", "initial_bp", e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            mt={0.5}
                            mx="auto"
                            display="block"
                          />
                        )}
                      </Stat>
                      </Box>
                    )}
                    {vitals.initial.rr && formatVital(vitals.initial.rr) && (
                      <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        cursor={evaluationMode ? "pointer" : "default"}
                        onClick={evaluationMode ? () => handleCheckboxToggle("vitals", "initial_rr", !isChecked("vitals", "initial_rr")) : undefined}
                      >
                                            <Stat size="sm" textAlign="center" sx={{ ".chakra-stat__label": { textAlign: "center" }, ".chakra-stat__number": { textAlign: "center" } }} bg={statsBoxBg} border="1px solid" borderColor="gray.600" borderRadius="md" p={0.5} pr={0.5} pb={evaluationMode ? 5 : 0.5} minH="64px" display="flex" flexDirection="column" justifyContent="center">
                        <StatLabel fontSize="xs" fontWeight="bold" letterSpacing="0.3px" whiteSpace="nowrap" color={monitorVitalColors.rr}>RR</StatLabel>
                        <StatNumber color={monitorVitalColors.rr} fontSize="sm">
                          {formatVital(vitals.initial.rr)}
                        </StatNumber>
                        {evaluationMode && (
                          <Box
                            as="input"
                            type="checkbox"
                            checked={isChecked("vitals", "initial_rr")}
                            onChange={(e) => handleCheckboxToggle("vitals", "initial_rr", e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            mt={0.5}
                            mx="auto"
                            display="block"
                          />
                        )}
                      </Stat>
                      </Box>
                    )}
                    {vitals.initial.etco2 && formatVital(vitals.initial.etco2) && (
                      <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        cursor={evaluationMode ? "pointer" : "default"}
                        onClick={evaluationMode ? () => handleCheckboxToggle("vitals", "initial_etco2", !isChecked("vitals", "initial_etco2")) : undefined}
                      >
                                            <Stat size="sm" textAlign="center" sx={{ ".chakra-stat__label": { textAlign: "center" }, ".chakra-stat__number": { textAlign: "center" } }} bg={statsBoxBg} border="1px solid" borderColor="gray.600" borderRadius="md" p={0.5} pr={0.5} pb={evaluationMode ? 5 : 0.5} minH="64px" display="flex" flexDirection="column" justifyContent="center">
                        <StatLabel fontSize="xs" fontWeight="bold" letterSpacing="0.3px" whiteSpace="nowrap" color={monitorVitalColors.etco2}>EtCO₂</StatLabel>
                        <StatNumber fontSize="sm" color={monitorVitalColors.etco2}>{formatVital(vitals.initial.etco2)}</StatNumber>
                        {evaluationMode && (
                          <Box
                            as="input"
                            type="checkbox"
                            checked={isChecked("vitals", "initial_etco2")}
                            onChange={(e) => handleCheckboxToggle("vitals", "initial_etco2", e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            mt={0.5}
                            mx="auto"
                            display="block"
                          />
                        )}
                      </Stat>
                      </Box>
                    )}
                    {vitals.initial.spo2 && formatVital(vitals.initial.spo2) && (
                      <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        cursor={evaluationMode ? "pointer" : "default"}
                        onClick={evaluationMode ? () => handleCheckboxToggle("vitals", "initial_spo2", !isChecked("vitals", "initial_spo2")) : undefined}
                      >
                                            <Stat size="sm" textAlign="center" sx={{ ".chakra-stat__label": { textAlign: "center" }, ".chakra-stat__number": { textAlign: "center" } }} bg={statsBoxBg} border="1px solid" borderColor="gray.600" borderRadius="md" p={0.5} pr={0.5} pb={evaluationMode ? 5 : 0.5} minH="64px" display="flex" flexDirection="column" justifyContent="center">
                        <StatLabel fontSize="xs" fontWeight="bold" letterSpacing="0.3px" whiteSpace="nowrap" color={monitorVitalColors.spo2}>SpO₂</StatLabel>
                        <StatNumber color={monitorVitalColors.spo2} fontSize="sm">
                          {formatVital(vitals.initial.spo2)}
                        </StatNumber>
                        {evaluationMode && (
                          <Box
                            as="input"
                            type="checkbox"
                            checked={isChecked("vitals", "initial_spo2")}
                            onChange={(e) => handleCheckboxToggle("vitals", "initial_spo2", e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            mt={0.5}
                            mx="auto"
                            display="block"
                          />
                        )}
                      </Stat>
                      </Box>
                    )}
                    {vitals.initial.temp && formatVital(vitals.initial.temp) && (
                      <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        cursor={evaluationMode ? "pointer" : "default"}
                        onClick={evaluationMode ? () => handleCheckboxToggle("vitals", "initial_temp", !isChecked("vitals", "initial_temp")) : undefined}
                      >
                                            <Stat size="sm" textAlign="center" sx={{ ".chakra-stat__label": { textAlign: "center" }, ".chakra-stat__number": { textAlign: "center" } }} bg={statsBoxBg} border="1px solid" borderColor="gray.600" borderRadius="md" p={0.5} pr={0.5} pb={evaluationMode ? 5 : 0.5} minH="64px" display="flex" flexDirection="column" justifyContent="center">
                        <StatLabel fontSize="xs" fontWeight="bold" letterSpacing="0.3px" whiteSpace="nowrap" color={monitorVitalColors.temp}>Temp</StatLabel>
                        <StatNumber color={monitorVitalColors.temp} fontSize="sm">
                          {formatVital(vitals.initial.temp)}
                        </StatNumber>
                        {evaluationMode && (
                          <Box
                            as="input"
                            type="checkbox"
                            checked={isChecked("vitals", "initial_temp")}
                            onChange={(e) => handleCheckboxToggle("vitals", "initial_temp", e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            mt={0.5}
                            mx="auto"
                            display="block"
                          />
                        )}
                      </Stat>
                      </Box>
                    )}
                    {vitals.initial.bloodGlucose && formatVital(vitals.initial.bloodGlucose) && (
                      <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        cursor={evaluationMode ? "pointer" : "default"}
                        onClick={evaluationMode ? () => handleCheckboxToggle("vitals", "initial_bgl", !isChecked("vitals", "initial_bgl")) : undefined}
                      >
                                            <Stat size="sm" textAlign="center" sx={{ ".chakra-stat__label": { textAlign: "center" }, ".chakra-stat__number": { textAlign: "center" } }} bg={statsBoxBg} border="1px solid" borderColor="gray.600" borderRadius="md" p={0.5} pr={0.5} pb={evaluationMode ? 5 : 0.5} minH="64px" display="flex" flexDirection="column" justifyContent="center">
                        <StatLabel fontSize="xs" fontWeight="bold" letterSpacing="0.3px" whiteSpace="nowrap" color={vitalLabelColor}>BGL</StatLabel>
                        <StatNumber color={initialVitalTextColor(getVitalStatus('bloodGlucose', vitals.initial.bloodGlucose))} fontSize="sm">
                          {formatVital(vitals.initial.bloodGlucose)}
                        </StatNumber>
                        {evaluationMode && (
                          <Box
                            as="input"
                            type="checkbox"
                            checked={isChecked("vitals", "initial_bgl")}
                            onChange={(e) => handleCheckboxToggle("vitals", "initial_bgl", e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            mt={0.5}
                            mx="auto"
                            display="block"
                          />
                        )}
                      </Stat>
                      </Box>
                    )}
                    {vitals.initial.rhythm && formatVital(vitals.initial.rhythm) && (
                      <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        cursor={evaluationMode ? "pointer" : "default"}
                        onClick={evaluationMode ? () => handleCheckboxToggle("vitals", "initial_rhythm", !isChecked("vitals", "initial_rhythm")) : undefined}
                      >
                                            <Stat size="sm" textAlign="center" sx={{ ".chakra-stat__label": { textAlign: "center" }, ".chakra-stat__number": { textAlign: "center" } }} bg={statsBoxBg} border="1px solid" borderColor="gray.600" borderRadius="md" p={0.5} pr={0.5} pb={evaluationMode ? 5 : 0.5} minH="64px" display="flex" flexDirection="column" justifyContent="center">
                        <StatLabel fontSize="xs" fontWeight="bold" letterSpacing="0.3px" whiteSpace="nowrap" color={vitalLabelColor}>Rhythm</StatLabel>
                        <StatNumber fontSize="sm" color={vitalNeutralColor}>{formatVital(vitals.initial.rhythm)}</StatNumber>
                        {evaluationMode && (
                          <Box
                            as="input"
                            type="checkbox"
                            checked={isChecked("vitals", "initial_rhythm")}
                            onChange={(e) => handleCheckboxToggle("vitals", "initial_rhythm", e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            mt={0.5}
                            mx="auto"
                            display="block"
                          />
                        )}
                      </Stat>
                      </Box>
                    )}
                    {vitals.initial.pupils && hasValue(vitals.initial.pupils) && (
                      <Box
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        cursor={evaluationMode ? "pointer" : "default"}
                        onClick={evaluationMode ? () => handleCheckboxToggle("vitals", "initial_pupils", !isChecked("vitals", "initial_pupils")) : undefined}
                      >
                                            <Stat size="sm" textAlign="center" sx={{ ".chakra-stat__label": { textAlign: "center" }, ".chakra-stat__number": { textAlign: "center" } }} bg={statsBoxBg} border="1px solid" borderColor="gray.600" borderRadius="md" p={0.5} pr={0.5} pb={evaluationMode ? 5 : 0.5} minH="64px" display="flex" flexDirection="column" justifyContent="center">
                        <StatLabel fontSize="xs" fontWeight="bold" letterSpacing="0.3px" whiteSpace="nowrap" color={vitalLabelColor}>Pupils</StatLabel>
                        <StatNumber fontSize="sm" color={vitalNeutralColor}>{vitals.initial.pupils}</StatNumber>
                        {evaluationMode && (
                          <Box
                            as="input"
                            type="checkbox"
                            checked={isChecked("vitals", "initial_pupils")}
                            onChange={(e) => handleCheckboxToggle("vitals", "initial_pupils", e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            mt={0.5}
                            mx="auto"
                            display="block"
                          />
                        )}
                      </Stat>
                      </Box>
                    )}
                  </SimpleGrid>
                </Box>
              )}

              {/* 12-Lead ECG Interpretation - Only if included */}
              {vitals?.initial?.['12Lead'] && formatVital(vitals.initial['12Lead']) && (
                <Box mt={2}>
                  <HStack spacing={1} align="center" mb={1}>
                    <Text fontWeight="bold" color={boldTextColor} fontSize="sm">12-Lead ECG:</Text>
                    {evaluationMode && (
                      <Box
                        as="input"
                        type="checkbox"
                        checked={isChecked("vitals", "initial_12lead")}
                        onChange={(e) => handleCheckboxToggle("vitals", "initial_12lead", e.target.checked)}
                        cursor="pointer"
                      />
                    )}
                  </HStack>
                  <Box
                    bg={isSimplePrint ? "transparent" : "yellow.50"}
                    p={2}
                    borderRadius="md"
                    borderLeft="4px solid"
                    borderColor={isSimplePrint ? "black" : "yellow.600"}
                  >
                    <Text fontSize="xs" color={isSimplePrint ? "black" : "gray.800"} whiteSpace="pre-wrap">{formatVital(vitals.initial['12Lead'])}</Text>
                  </Box>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Secondary Assessment / Physical Exam */}
        {secondaryAssessment && Object.keys(secondaryAssessment).some(key => hasValue(secondaryAssessment[key])) && (
          <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
            <CardHeader bg="cyan.600" color="white" borderTopRadius="md" py={1}>
              <Heading size="sm" color="white">Secondary Assessment</Heading>
            </CardHeader>
            <CardBody p={0.5}>
              <Box sx={{ columnCount: { base: 1, md: 2 }, columnGap: "8px" }}>
                {secondaryAssessment.heent && hasValue(secondaryAssessment.heent) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'heent')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'heent', checked)}
                    label="HEENT Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">HEENT</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.heent}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.neck && hasValue(secondaryAssessment.neck) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'neck')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'neck', checked)}
                    label="Neck Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Neck</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.neck}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.chest && hasValue(secondaryAssessment.chest) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'chest')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'chest', checked)}
                    label="Chest Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Chest</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.chest}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.respiratory && hasValue(secondaryAssessment.respiratory) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'respiratory')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'respiratory', checked)}
                    label="Respiratory Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Respiratory</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.respiratory}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.cardiovascular && hasValue(secondaryAssessment.cardiovascular) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'cardiovascular')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'cardiovascular', checked)}
                    label="Cardiovascular Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Cardiovascular</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.cardiovascular}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.abdomen && hasValue(secondaryAssessment.abdomen) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'abdomen')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'abdomen', checked)}
                    label="Abdomen Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Abdomen</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.abdomen}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.pelvis && hasValue(secondaryAssessment.pelvis) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'pelvis')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'pelvis', checked)}
                    label="Pelvis Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Pelvis</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.pelvis}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.extremities && hasValue(secondaryAssessment.extremities) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'extremities')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'extremities', checked)}
                    label="Extremities Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Extremities</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.extremities}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.back && hasValue(secondaryAssessment.back) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'back')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'back', checked)}
                    label="Back Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Back</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.back}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.neuro && hasValue(secondaryAssessment.neuro) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'neuro')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'neuro', checked)}
                    label="Neurological Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Neurological</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.neuro}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.skin && hasValue(secondaryAssessment.skin) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'skin')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'skin', checked)}
                    label="Skin Exam"
                  >
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={1} borderRadius="md" overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontWeight="bold" color={elementHeaderColor} fontSize="sm">Skin</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.skin}</Text>
                    </Box>
                  </CheckableItem>
                )}
              </Box>
            </CardBody>
          </Card>
        )}
      </Box>

      {isSimplePrint && dedicatedPrintMode && <Box className="page-break-after-assessment-row" />}

      {/* Row 4: Progression Vitals - 2 Columns */}
      {(scenario.vitalSigns?.progressionWithoutTreatment || scenario.vitalSigns?.progressionWithProperTreatment) && (
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr"
          gap={1}
          mb={1}
          className="trending-row-grid"
        >
          {/* Progression WITH Proper Treatment */}
          {scenario.vitalSigns?.progressionWithProperTreatment && (
            <Card bg={trendingCardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="green.600" color="white" borderTopRadius="md" py={1}>
                <Heading size="sm" color="white">
                  <HStack><FaHeartbeat /><Text>Progression WITH Proper Treatment</Text></HStack>
                </Heading>
              </CardHeader>
              <CardBody p={1}>
                <VStack align="stretch" spacing={0}>
                  {scenario.vitalSigns.progressionWithProperTreatment.description && (
                    <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={1}>
                      {scenario.vitalSigns.progressionWithProperTreatment.description}
                    </Text>
                  )}
                  {scenario.vitalSigns.progressionWithProperTreatment.timeframe && (
                    scenario.improvementVideoUrl ? (
                      <Badge 
                        colorScheme="green" 
                        mb={1} 
                        fontSize="xs" 
                        maxW="100%" 
                        wordBreak="break-word"
                        cursor="pointer"
                        onClick={() => openMediaModal('video', scenario.improvementVideoUrl, 'Patient Improvement Video')}
                        _hover={{ opacity: 0.8 }}
                      >
                        {scenario.vitalSigns.progressionWithProperTreatment.timeframe} 🎥
                      </Badge>
                    ) : (
                      <Badge colorScheme="green" mb={1} fontSize="xs" maxW="100%" wordBreak="break-word">
                        {scenario.vitalSigns.progressionWithProperTreatment.timeframe}
                      </Badge>
                    )
                  )}
                  {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals && (
                    <Text fontSize="xs" color={textColor} lineHeight="tall">
                      {[
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.hr ? <><Text as="span" fontWeight="bold">HR</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.hr}</> : null,
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.bp ? <><Text as="span" fontWeight="bold">BP</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.bp}</> : null,
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.rr ? <><Text as="span" fontWeight="bold">RR</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.rr}</> : null,
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.spo2 ? <><Text as="span" fontWeight="bold">SpO₂</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.spo2}</> : null,
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.gcs ? <><Text as="span" fontWeight="bold">GCS</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.gcs}</> : null
                      ].filter(Boolean).map((part, i) => (
                        <Text as="span" key={i}>
                          {i > 0 && ' • '}
                          {part}
                        </Text>
                      ))}
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Deterioration Without Treatment/With Improper Treatment */}
          {scenario.vitalSigns?.progressionWithoutTreatment && (
            <Card bg={trendingCardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="red.600" color="white" borderTopRadius="md" py={1}>
                <Heading size="sm" color="white">
                  <HStack><FaExclamationTriangle /><Text>Deterioration Without Treatment/With Improper Treatment</Text></HStack>
                </Heading>
              </CardHeader>
              <CardBody p={1} overflow="hidden">
                <VStack align="stretch" spacing={0}>
                  {scenario.vitalSigns.progressionWithoutTreatment.description && (
                    <Box overflow="hidden" p={1}>
                      <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={1} whiteSpace="pre-wrap" wordBreak="break-word" overflowWrap="break-word">
                        {scenario.vitalSigns.progressionWithoutTreatment.description}
                      </Text>
                    </Box>
                  )}
                  {scenario.vitalSigns.progressionWithoutTreatment.timeframe && (
                    scenario.deteriorationVideoUrl ? (
                      <Badge 
                        colorScheme="red" 
                        mb={1} 
                        fontSize="xs" 
                        maxW="100%" 
                        wordBreak="break-word"
                        cursor="pointer"
                        onClick={() => openMediaModal('video', scenario.deteriorationVideoUrl, 'Patient Deterioration Video')}
                        _hover={{ opacity: 0.8 }}
                      >
                        {scenario.vitalSigns.progressionWithoutTreatment.timeframe} 🎥
                      </Badge>
                    ) : (
                      <Badge colorScheme="red" mb={1} fontSize="xs" maxW="100%" wordBreak="break-word">
                        {scenario.vitalSigns.progressionWithoutTreatment.timeframe}
                      </Badge>
                    )
                  )}
                  {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals && (
                    <Text fontSize="xs" color={textColor} lineHeight="tall" whiteSpace="pre-wrap" wordBreak="break-word">
                      {[
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.hr ? <><Text as="span" fontWeight="bold">HR</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.hr}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.bp ? <><Text as="span" fontWeight="bold">BP</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.bp}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.rr ? <><Text as="span" fontWeight="bold">RR</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.rr}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.spo2 ? <><Text as="span" fontWeight="bold">SpO₂</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.spo2}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.etco2 ? <><Text as="span" fontWeight="bold">EtCO₂</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.etco2}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.gcs ? <><Text as="span" fontWeight="bold">GCS</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.gcs}</> : null
                      ].filter(Boolean).map((part, i) => (
                        <Text as="span" key={i}>
                          {i > 0 && ' • '}
                          {part}
                        </Text>
                      ))}
                    </Text>
                  )}
                  
                  {/* Cardiac Arrest Sequence - Compact subsection */}
                  {hasCardiacArrest && (
                    <Box className="cardiac-arrest-sequence" mt={2} pt={2} borderTop="1px solid" borderColor="red.300">
                      <Text fontSize="2xs" fontWeight="bold" color="red.700" mb={0.5}>If Cardiac Arrest Occurs:</Text>
                      <Table size="sm" variant="simple">
                        <Tbody>
                          {cardiacArrest.initialRhythm && hasValue(cardiacArrest.initialRhythm) && (
                            <Tr>
                              <Td fontSize="2xs" p={0.5} fontWeight="semibold">Initial:</Td>
                              <Td fontSize="2xs" p={0.5}>{cardiacArrest.initialRhythm}</Td>
                            </Tr>
                          )}
                          {cardiacArrest.sequence && cardiacArrest.sequence.length > 0 && cardiacArrest.sequence.map((step, index) => (
                            <Tr key={index}>
                              <Td fontSize="2xs" p={0.5} fontWeight="semibold">{index + 1}.</Td>
                              <Td fontSize="2xs" p={0.5}>
                                {typeof step === 'string' ? step : (
                                  `${step.rhythm || 'N/A'} - ${step.intervention || 'N/A'}${step.expectedResponse ? ` (${step.expectedResponse})` : ''}`
                                )}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                      {cardiacArrest.targetOutcome && hasValue(cardiacArrest.targetOutcome) && (
                        <Text fontSize="2xs" color={textColor} mt={0.5} fontStyle="italic">→ {cardiacArrest.targetOutcome}</Text>
                      )}
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>
          )}
        </Box>
      )}

      {/* Interventions - Categorized - 2 Column Layout */}
      {interventions && (interventions.LifeSaving?.length > 0 || interventions.Secondary?.length > 0 || interventions.Optional?.length > 0) && (
        <Card mb={1.5} bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid interventions-card">
          <CardHeader bg="orange.600" color="white" borderTopRadius="md" py={1.5}>
            <Heading size="sm" color="white">Interventions</Heading>
          </CardHeader>
          <CardBody p={1.5}>
            <SimpleGrid columns={2} spacing={1.5}>
              {/* Life-Saving Interventions */}
              {interventions.LifeSaving && interventions.LifeSaving.length > 0 && (
                <Box>
                  <Text fontWeight="bold" color="red.700" mb={1} fontSize="sm">Critical / Life-Saving</Text>
                  <VStack align="stretch" spacing={1} className="interventions-grid">
                    {interventions.LifeSaving.map((intervention, index) => (
                      <CheckableItem
                        key={makeChecklistKey("interventions_lifesaving", intervention, index)}
                        evaluationMode={evaluationMode}
                        isChecked={isChecked("interventions", makeChecklistKey("lifesaving", intervention, index))}
                        onToggle={(checked) => handleCheckboxToggle("interventions", makeChecklistKey("lifesaving", intervention, index), checked)}
                        label={intervention}
                      >
                        <Card bg="red.50" borderLeft="4px solid" borderColor="red.600">
                          <CardBody py={1} px={2}>
                            <HStack spacing={1}>
                              <FaExclamationTriangle color="#C53030" size={12} />
                              <Text fontWeight="semibold" color="red.900" fontSize="xs">{intervention}</Text>
                            </HStack>
                          </CardBody>
                        </Card>
                      </CheckableItem>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* Secondary + Optional Interventions Combined */}
              <VStack align="stretch" spacing={1.5}>
                {/* Secondary Interventions */}
                {interventions.Secondary && interventions.Secondary.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Secondary</Text>
                    <VStack align="stretch" spacing={1} className="interventions-grid">
                      {interventions.Secondary.map((intervention, index) => (
                        <CheckableItem
                          key={makeChecklistKey("interventions_secondary", intervention, index)}
                          evaluationMode={evaluationMode}
                          isChecked={isChecked("interventions", makeChecklistKey("secondary", intervention, index))}
                          onToggle={(checked) => handleCheckboxToggle("interventions", makeChecklistKey("secondary", intervention, index), checked)}
                          label={intervention}
                        >
                          <Card bg="blue.50" borderLeft="4px solid" borderColor="blue.500">
                            <CardBody py={1} px={2}>
                              <HStack spacing={1}>
                                <FaCheckCircle color="#3182CE" size={12} />
                                <Text color="blue.900" fontSize="xs">{intervention}</Text>
                              </HStack>
                            </CardBody>
                          </Card>
                        </CheckableItem>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Optional Interventions */}
                {interventions.Optional && interventions.Optional.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Optional/Conditional</Text>
                    <VStack align="stretch" spacing={1} className="interventions-grid">
                      {interventions.Optional.map((intervention, index) => (
                        <CheckableItem
                          key={makeChecklistKey("interventions_optional", intervention, index)}
                          evaluationMode={evaluationMode}
                          isChecked={isChecked("interventions", makeChecklistKey("optional", intervention, index))}
                          onToggle={(checked) => handleCheckboxToggle("interventions", makeChecklistKey("optional", intervention, index), checked)}
                          label={intervention}
                        >
                          <Card bg="gray.50" borderLeft="4px solid" borderColor="gray.400">
                            <CardBody py={1} px={2}>
                              <Text color="gray.700" fontSize="xs">{intervention}</Text>
                            </CardBody>
                          </Card>
                        </CheckableItem>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Trending Vital Signs */}
      {vitals.trending && vitals.trending.length > 0 && (
        <Card bg={cardBg} shadow="md" color={cardTextColor} mb={4}>
          <CardHeader bg="pink.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">Trending Vitals</Heading>
          </CardHeader>
          <CardBody p={2}>
            <VStack align="stretch" spacing={0.5} fontSize="xs">
              {vitals.trending.map((vitalSet, index) => {
                const vitalsParts = [];
                if (vitalSet.hr && formatVital(vitalSet.hr)) vitalsParts.push(<><Text as="span" fontWeight="bold">HR</Text> {formatVital(vitalSet.hr)}</>);
                if (vitalSet.bp && formatVital(vitalSet.bp)) vitalsParts.push(<><Text as="span" fontWeight="bold">BP</Text> {formatVital(vitalSet.bp)}</>);
                if (vitalSet.rr && formatVital(vitalSet.rr)) vitalsParts.push(<><Text as="span" fontWeight="bold">RR</Text> {formatVital(vitalSet.rr)}</>);
                if (vitalSet.spo2 && formatVital(vitalSet.spo2)) vitalsParts.push(<><Text as="span" fontWeight="bold">SpO₂</Text> {formatVital(vitalSet.spo2)}</>);
                if (vitalSet.temp && formatVital(vitalSet.temp)) vitalsParts.push(<><Text as="span" fontWeight="bold">T</Text> {formatVital(vitalSet.temp)}</>);
                if (vitalSet.bloodGlucose && formatVital(vitalSet.bloodGlucose)) vitalsParts.push(<><Text as="span" fontWeight="bold">BGL</Text> {formatVital(vitalSet.bloodGlucose)}</>);
                if (vitalSet.etco2 && formatVital(vitalSet.etco2)) vitalsParts.push(<><Text as="span" fontWeight="bold">EtCO₂</Text> {formatVital(vitalSet.etco2)}</>);
                
                return (
                  <Box key={index} borderBottom="1px solid" borderColor="gray.200" py={1}>
                    <Text color={textColor}>
                      <Text as="span" fontWeight="bold" color="pink.700">
                        • {vitalSet.time && hasValue(vitalSet.time) ? vitalSet.time : `Time ${index + 1}`}:
                      </Text>{' '}
                      {vitalsParts.map((part, i) => (
                        <Text as="span" key={i}>
                          {i > 0 && ' • '}
                          {part}
                        </Text>
                      ))}
                    </Text>
                    {vitalSet.notes && hasValue(vitalSet.notes) && (
                      <Text fontSize="2xs" color={labelColor} fontStyle="italic" mt={0.5}>
                        {vitalSet.notes}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Expected Outcome */}
      {(outcome.finalCondition || outcome.disposition || outcome.continuingCareNeeds) && (
        <Card bg={cardBg} shadow="md" color={cardTextColor} mb={4}>
          <CardHeader bg="blue.600" color="white" borderTopRadius="md">
            <Heading size="md" color="white">Expected Outcome</Heading>
          </CardHeader>
          <CardBody>
            {outcome.finalCondition && hasValue(outcome.finalCondition) && (
              <Text mb={2}><Text as="span" fontWeight="bold">Final Condition:</Text> {outcome.finalCondition}</Text>
            )}
            {outcome.disposition && hasValue(outcome.disposition) && (
              <Text mb={2}><Text as="span" fontWeight="bold">Disposition:</Text> {outcome.disposition}</Text>
            )}
            {outcome.continuingCareNeeds && hasValue(outcome.continuingCareNeeds) && (
              <Text><Text as="span" fontWeight="bold">Continuing Care Needs:</Text> {outcome.continuingCareNeeds}</Text>
            )}
          </CardBody>
        </Card>
      )}

      {/* Critical Actions and Common Pitfalls - Side by Side */}
      {printMode === "simple-print" && <Box className="page-break-before-critical-actions" />}
      {((learning.criticalActions && learning.criticalActions.length > 0) || (learning.commonPitfalls && learning.commonPitfalls.length > 0)) && (
        <SimpleGrid columns={isSimplePrint ? 2 : { base: 1, md: 2 }} spacing={2} mb={3} className="page-break-avoid">
          {/* Critical Actions */}
          {learning.criticalActions && learning.criticalActions.length > 0 && (
            <Card bg={cardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="red.600" color="white" borderTopRadius="md" py={1}>
                <HStack>
                  <FaExclamationTriangle />
                  <Heading size="sm" color="white">Critical Actions</Heading>
                </HStack>
              </CardHeader>
              <CardBody p={2}>
                <List spacing={0.5} className="critical-actions-list">
                  {learning.criticalActions.map((action, index) => (
                    <CheckableItem
                      key={makeChecklistKey("critical_actions", action, index)}
                      evaluationMode={evaluationMode}
                      isChecked={isChecked("criticalActions", makeChecklistKey("action", action, index))}
                      onToggle={(checked) => handleCheckboxToggle("criticalActions", makeChecklistKey("action", action, index), checked)}
                      label={action}
                    >
                      <ListItem>
                        <HStack align="center">
                          <ListIcon as={FaExclamationTriangle} color="red.500" />
                          <Text color={textColor} fontWeight="medium" fontSize="sm">{action}</Text>
                        </HStack>
                      </ListItem>
                    </CheckableItem>
                  ))}
                </List>
              </CardBody>
            </Card>
          )}

          {/* Common Pitfalls */}
          {learning.commonPitfalls && learning.commonPitfalls.length > 0 && (
            <Card bg={cardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="orange.600" color="white" borderTopRadius="md" py={1}>
                <HStack>
                  <FaExclamationTriangle />
                  <Heading size="sm" color="white">Common Pitfalls</Heading>
                </HStack>
              </CardHeader>
              <CardBody p={2}>
                <List spacing={0.5} className="common-pitfalls-list">
                  {learning.commonPitfalls.map((pitfall, index) => (
                    <CheckableItem
                      key={makeChecklistKey("pitfalls", pitfall, index)}
                      evaluationMode={evaluationMode}
                      isChecked={isChecked("pitfalls", makeChecklistKey("pitfall", pitfall, index))}
                      onToggle={(checked) => handleCheckboxToggle("pitfalls", makeChecklistKey("pitfall", pitfall, index), checked)}
                      label={pitfall}
                    >
                      <ListItem>
                        <HStack align="center">
                          <ListIcon as={FaExclamationTriangle} color="orange.500" />
                          <Text color={textColor} fontSize="sm">{pitfall}</Text>
                        </HStack>
                      </ListItem>
                    </CheckableItem>
                  ))}
                </List>
              </CardBody>
            </Card>
          )}
        </SimpleGrid>
      )}

      {/* Hard page-break marker is disabled in simple print */}
      {printMode !== "simple-print" && (
        <Box className={`page-break-before-differentials page-break-after ${shouldForcePageBreak ? 'force-page-break' : ''}`} />
      )}

      {/* Differential Diagnosis and Definitive Care - Side by Side */}
      {(learning.differentialDiagnosis && learning.differentialDiagnosis.length > 0) || hasDefinitiveCare ? (
        <SimpleGrid columns={isSimplePrint ? 2 : { base: 1, md: 2 }} spacing={4} mb={4} className="page-break-avoid">
          {/* Differential Diagnosis - Left Half */}
          {learning.differentialDiagnosis && learning.differentialDiagnosis.length > 0 && (
            <Card bg={cardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="cyan.600" color="white" borderTopRadius="md" py={1}>
                <HStack>
                  <FaBrain />
                  <Heading size="sm" color="white">Differential Diagnosis</Heading>
                </HStack>
              </CardHeader>
              <CardBody p={2}>
                <VStack align="stretch" spacing={3}>
                  {learning.differentialDiagnosis.map((diagnosis, index) => {
                    // Handle both string format (legacy) and object format (new)
                    if (typeof diagnosis === 'string') {
                      return (
                        <HStack key={index} align="start">
                          <Icon as={FaCheckCircle} color="cyan.500" mt={1} />
                          <Text color={textColor}>{diagnosis}</Text>
                        </HStack>
                      );
                    } else {
                      return (
                        <Box key={index} bg={boxBg} border="1px solid" borderColor={boxBorderColor} p={3} borderRadius="md" borderLeft="4px solid" borderLeftColor="cyan.500" overflow="hidden">
                          <Box bg={elementHeaderBg} px={2} py={0.5} mx={-3} mt={-3} mb={1}>
                            <HStack align="flex-start" justify="space-between" spacing={2}>
                              <Text fontWeight="bold" color={elementHeaderColor} fontSize="sm" flex="1" minW={0}>
                                {diagnosis.condition || diagnosis.Condition}
                              </Text>
                              {(diagnosis.likelihood || diagnosis.Likelihood) && (
                                <Badge colorScheme="cyan" fontSize="xs" flexShrink={0}>
                                  {diagnosis.likelihood || diagnosis.Likelihood}
                                </Badge>
                              )}
                            </HStack>
                          </Box>
                          {(diagnosis.supportingFindings || diagnosis.SupportingFindings) && (
                            <Text color="green.600" fontSize="sm" mt={1}>
                              ✓ Supporting: {diagnosis.supportingFindings || diagnosis.SupportingFindings}
                            </Text>
                          )}
                          {(diagnosis.contradictingFindings || diagnosis.ContradictingFindings) && (
                            <Text color="red.600" fontSize="sm" mt={1}>
                              ✗ Against: {diagnosis.contradictingFindings || diagnosis.ContradictingFindings}
                            </Text>
                          )}
                        </Box>
                      );
                    }
                  })}
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Definitive Care - Enhanced Display */}
          {hasDefinitiveCare && (
            <Card bg={cardBg} shadow="md" color={cardTextColor} className="avoid-break">
              <CardHeader bg="teal.600" color="white" borderTopRadius="md" py={1}>
                <HStack>
                  <FaHospital />
                  <Heading size="sm" color="white">Hospital Definitive Care</Heading>
                </HStack>
              </CardHeader>
              <CardBody p={2}>
                <VStack spacing={2} align="stretch">
                  {/* Lab Tests with Values */}
                  {definitiveCare.labTests && definitiveCare.labTests.length > 0 && (
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} borderRadius="md" p={2} overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontSize="sm" fontWeight="bold" color={elementHeaderColor}>Laboratory Results</Text>
                      <List spacing={0.5}>
                        {definitiveCare.labTests.map((test, index) => (
                          <ListItem key={index} fontSize="sm" color={textColor}>• {test}</ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Imaging with Detailed Findings */}
                  {definitiveCare.imagingStudies && definitiveCare.imagingStudies.length > 0 && (
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} borderRadius="md" p={2} overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontSize="sm" fontWeight="bold" color={elementHeaderColor}>Imaging Studies</Text>
                      <List spacing={0.5}>
                        {definitiveCare.imagingStudies.map((study, index) => (
                          <ListItem key={index} fontSize="sm" color={textColor}>• {study}</ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Diagnostic Procedures */}
                  {definitiveCare.diagnosticProcedures && definitiveCare.diagnosticProcedures.length > 0 && (
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} borderRadius="md" p={2} overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontSize="sm" fontWeight="bold" color={elementHeaderColor}>Diagnostic Procedures</Text>
                      <List spacing={0.5}>
                        {definitiveCare.diagnosticProcedures.map((procedure, index) => (
                          <ListItem key={index} fontSize="sm" color={textColor}>• {procedure}</ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Definitive Interventions - What Actually Fixes the Problem */}
                  {definitiveCare.definitiveInterventions && definitiveCare.definitiveInterventions.length > 0 && (
                    <Box bg={boxBg} border="1px solid" borderColor={boxBorderColor} borderRadius="md" p={2} overflow="hidden">
                      <Text display="block" bg={elementHeaderBg} px={2} py={1} mx={-2} mt={-2} mb={2} borderTopRadius="md" fontSize="sm" fontWeight="bold" color={elementHeaderColor}>Definitive Treatment (What Fixes the Problem)</Text>
                      <List spacing={0.5}>
                        {definitiveCare.definitiveInterventions.map((intervention, index) => (
                          <ListItem key={index} fontSize="sm" color={textColor} fontWeight="medium">• {intervention}</ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>
          )}
        </SimpleGrid>
      ) : null}

      {/* Evaluation Form - Page 2 for print/tablet modes */}
      {evaluationForm && printMode !== "simple-print" && (
        <>
          {/* Page break before evaluation */}
          <Box className="page-break-before" />
          
          <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
            <CardHeader bg="teal.600" color="white" borderTopRadius="md">
              <Heading size="md" color="white">Evaluation Form</Heading>
            </CardHeader>
            <CardBody>
              {evaluationForm}
            </CardBody>
          </Card>
        </>
      )}

      <Box
        className="inline-watermark-border-bottom"
        border="1px solid"
        borderColor={boxBorderColor}
        bg={boxBg}
        borderRadius="sm"
        px={2}
        py={1}
        mt={2}
      >
        <Text
          fontSize="8px"
          color={mutedTextColor}
          lineHeight="1.2"
          noOfLines={1}
        >
          {PRINT_COPYRIGHT_NOTICE}
        </Text>
      </Box>

      </Box>
        </>
      )}



      {/* Watermarks - always visible to prevent screenshots */}
      {watermarkConfig && scenario && (
        <>
          {/* Overt watermark */}
          <Box
            className="scenario-watermark"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            pointerEvents="none"
            userSelect="none"
          >
            {renderOvertWatermark()}
          </Box>
          
          {/* Subtle watermark */}
          {renderSubtleWatermark()}
        </>
      )}

      {/* Media Viewing Modal */}
      <Modal isOpen={mediaModalOpen} onClose={() => setMediaModalOpen(false)} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{currentMedia?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* AI Generated Disclaimer */}
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box flex="1">
                  <AlertTitle fontSize="sm">AI-Generated Content</AlertTitle>
                  <AlertDescription fontSize="xs">
                    This media was generated by artificial intelligence and may not be medically accurate. 
                    It is for educational purposes only. Any concerns should be reported to{' '}
                    <Text as="a" href="mailto:support@elevationems.com" color="blue.500" textDecoration="underline">
                      support@elevationems.com
                    </Text>
                  </AlertDescription>
                </Box>
              </Alert>
              
              {/* Media Display */}
              {currentMedia?.type === 'image' && (
                <AspectRatio ratio={16/9}>
                  <Image 
                    src={currentMedia.url} 
                    alt={currentMedia.title}
                    borderRadius="md"
                    objectFit="contain"
                  />
                </AspectRatio>
              )}
              
              {currentMedia?.type === 'video' && (
                <AspectRatio ratio={16/9}>
                  <video 
                    controls 
                    autoPlay
                    style={{ borderRadius: '8px' }}
                    src={currentMedia.url}
                  >
                    Your browser does not support the video tag.
                  </video>
                </AspectRatio>
              )}
              
              <HStack justify="flex-end">
                <Button
                  as="a"
                  href={currentMedia?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  leftIcon={<ExternalLinkIcon />}
                  colorScheme="blue"
                  size="sm"
                >
                  Open in New Tab
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setMediaModalOpen(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ScenarioDisplay;
